import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { useOrg } from '../contexts/OrgContext';
import { PageLayout } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Plus, Search, Filter, Download, Upload, MoreHorizontal, Pencil, Trash2,
  Building2, User, BarChart3, Calculator, Layers, FileText, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  AlertTriangle, Wallet, PiggyBank, ArrowRightLeft, Loader2, Calendar,
  Target, Zap, Activity, CreditCard, Receipt, LineChart as LineChartIcon, Database, PieChart,
  Users, ArrowLeft, Clock, RefreshCw, CircleDollarSign, CheckSquare, UserPlus, Link2,
  Square, ArrowRight, X, ChevronDown, Settings, ArrowUpDown, History, Check, Save, Info,
  Archive, RotateCcw, HelpCircle, Tag, Landmark, Truck, FolderKanban, Network,
  Scale, Printer,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import Papa from 'papaparse';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, Legend
} from 'recharts';
import PLForecastTab from './finance/PLForecastTab';
import CapTableTab from './finance/CapTableTab';
import { ExpenseModalContent, IncomeModalContent } from './finance/FinanceModals';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const API = '/api';

// ==================== CONSTANTS ====================
const EXPENSE_TYPES = [
  'Payroll & Benefits', 'SaaS & Tools', 'Professional Services', 'Marketing & Advertising',
  'Office & Facilities', 'Travel & Entertainment', 'Hosting & Infrastructure',
  'Insurance & Compliance', 'R&D', 'Bank Fees', 'Depreciation', 'Other',
];

const RECEIPT_TYPES = ['Invoice', 'Receipt', 'Contract', 'Bank Statement', 'Email Confirmation', 'Screenshot', 'Other', 'None'];

const ORG_FUNCTIONS = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales', 'Operations',
  'Finance', 'Legal', 'HR', 'Customer Support', 'Executive', 'Other',
];

// Legacy color map for dashboard breakdown (backward compat with stored category codes)
const CATEGORY_COLORS = {
  A: { name: 'Payroll & Benefits', color: 'bg-blue-500' },
  B: { name: 'SaaS & Tools', color: 'bg-violet-500' },
  C: { name: 'Professional Services', color: 'bg-amber-500' },
  D: { name: 'Marketing', color: 'bg-pink-500' },
  E: { name: 'Office & Facilities', color: 'bg-emerald-500' },
  F: { name: 'Travel', color: 'bg-cyan-500' },
  G: { name: 'Hosting & Infra', color: 'bg-indigo-500' },
  H: { name: 'Insurance & Compliance', color: 'bg-orange-500' },
  I: { name: 'R&D', color: 'bg-teal-500' },
  J: { name: 'Other', color: 'bg-slate-500' },
};

const INCOME_TYPES = {
  '1': 'Product Revenue', '2': 'Service Revenue', '3': 'Subscription',
  '4': 'Consulting', '5': 'Investment Income', '6': 'Grant/Subsidy',
  '7': 'Equity Investment', '8': 'Debt/Loan', '9': 'Refund',
  '10': 'Inter-company', '11': 'Royalty/License', '12': 'Other',
};

const INCOME_CLASSIFICATIONS = ['Revenue', 'Deferred Revenue', 'Loan', 'Investment', 'Grant', 'Intercompany', 'Refund', 'Other'];
const INVOICE_STATUSES = ['Pending', 'Invoiced', 'Paid', 'Refunded'];
const PAYMENT_CHANNELS = ['Bank Transfer', 'Credit Card', 'PayPal', 'Stripe', 'Wire Transfer', 'Cash', 'Crypto', 'Check', 'Other'];
const INCOME_SOURCE_TYPES = ['Client', 'Investment', 'Grant', 'Refund', 'Interest', 'Royalties', 'Other'];
const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'INR', 'SGD', 'AED', 'CHF', 'JPY'];

const formatMoney = (val, currency = 'USD') => {
  const n = Number(val) || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign}${currency === 'USD' ? '$' : ''}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${currency === 'USD' ? '$' : ''}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${currency === 'USD' ? '$' : ''}${abs.toFixed(2)}`;
};

// ==================== MAIN COMPONENT ====================
const FinancePage = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { orgId, orgLoading, handle409 } = useOrg();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const fileInputRef = useRef(null);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [staffing, setStaffing] = useState(null);
  const [activeForecast, setActiveForecast] = useState(null);

  // Modals
  const [expenseModal, setExpenseModal] = useState(false);
  const [incomeModal, setIncomeModal] = useState(false);
  const [forecastModal, setForecastModal] = useState(false);
  const [scenarioModal, setScenarioModal] = useState(false);
  const [wsModal, setWsModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [importType, setImportType] = useState(null); // 'expenses' or 'income'

  // Selection & bulk
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const [selectedIncome, setSelectedIncome] = useState(new Set());
  const [bulkEditModal, setBulkEditModal] = useState(false);
  const [bulkEditType, setBulkEditType] = useState(null);
  const [bulkEditForm, setBulkEditForm] = useState({});

  // Import mapping dialog
  const [importDialog, setImportDialog] = useState(false);
  const [importDialogType, setImportDialogType] = useState(null);
  const [fullChangeLog, setFullChangeLog] = useState({ open: false, logs: [], loading: false });
  const [importRawData, setImportRawData] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importStep, setImportStep] = useState(1); // 1=map, 2=preview, 3=done
  const [importing, setImporting] = useState(false);

  // Custom fields
  const [customFields, setCustomFields] = useState({ expense_fields: [], income_fields: [] });
  const [orgMembers, setOrgMembers] = useState([]);
  const [staffingContractors, setStaffingContractors] = useState([]);
  const [offboardedStaff, setOffboardedStaff] = useState([]);

  // Reference data (expense tabs)
  const [refAccounts, setRefAccounts] = useState([]);
  const [refCategories, setRefCategories] = useState([]);
  const [refBusinessUnits, setRefBusinessUnits] = useState([]);
  const [refProjects, setRefProjects] = useState([]);
  const [refVendors, setRefVendors] = useState([]);
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [expenseSubTab, setExpenseSubTab] = useState('ledger');
  const [incomeSubTab, setIncomeSubTab] = useState('ledger');
  const [refForm, setRefForm] = useState({});
  const [refEditing, setRefEditing] = useState(null);
  const [showRefForm, setShowRefForm] = useState(false);
  const [vendorForm, setVendorForm] = useState({});
  const [vendorEditing, setVendorEditing] = useState(null);
  const [showVendorForm, setShowVendorForm] = useState(false);
  // Income reference data
  const [refSourceOfFunds, setRefSourceOfFunds] = useState([]);
  const [refIncomeCategories, setRefIncomeCategories] = useState([]);
  const [incRefForm, setIncRefForm] = useState({});
  const [incRefEditing, setIncRefEditing] = useState(null);
  const [showIncRefForm, setShowIncRefForm] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Forms
  const emptyExpense = {
    date: new Date().toISOString().slice(0, 10), account_paid_from: '', business_unit: '',
    staff_contractor_name: '', expense_role: '', expense_item: '', description: '', vendor: '', amount_usd: '', original_amount: '',
    original_currency: 'USD', org_function: '', expense_type: '', expense_sub_type: '',
    receipt_type: '', receipt_link: '', bank_statement_entry: '', bank_statement_date: '', comments: '',
    project_attribution: '',
  };
  const emptyIncome = {
    date_received: new Date().toISOString().slice(0, 10), due_date: '', invoice_issue_date: '',
    days_late: '', invoice_number: '', invoice_status: '', income_item_summary: '',
    source_of_funds: '', amount_usd: '', is_loan: 'N', original_amount: '', original_currency: 'USD',
    business_unit: '', income_source_type: '', sales_person: '', income_paid_to: '',
    payment_channel: '', income_classification: 'Revenue', market: '', income_note: '', receipt_issued: 'N', receipt_link: '',
  };
  const [expenseForm, setExpenseForm] = useState(emptyExpense);
  const [incomeForm, setIncomeForm] = useState(emptyIncome);
  const [forecastForm, setForecastForm] = useState({ name: '', start_month: '', end_month: '' });
  const [scenarioForm, setScenarioForm] = useState({ name: '', scenario_type: 'base', description: '' });
  const [wsForm, setWsForm] = useState({ name: '', workspace_type: 'business', currency: 'USD' });
  const [myFinanceProfile, setMyFinanceProfile] = useState(null);
  const [wsRenameModal, setWsRenameModal] = useState(false);
  const [wsRenameValue, setWsRenameValue] = useState('');
  const [wsDeleteConfirm, setWsDeleteConfirm] = useState(false);

  // ==================== FETCHERS ====================
  const fetchMyProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/org/access/my-profile`, { credentials: 'include' });
      if (res.ok) setMyFinanceProfile(await res.json());
    } catch {}
  }, []);

  const handleRenameWorkspace = async () => {
    if (!activeWorkspace || !wsRenameValue.trim()) return;
    try {
      const r = await fetch(`${API}/finance/workspaces/${activeWorkspace.workspace_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: wsRenameValue.trim() }),
      });
      if (r.ok) {
        toast.success('Workspace renamed');
        setWsRenameModal(false);
        setActiveWorkspace(prev => ({ ...prev, name: wsRenameValue.trim() }));
        fetchWorkspaces();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Rename failed');
      }
    } catch { toast.error('Network error'); }
  };

  const handleDeleteWorkspace = async () => {
    if (!activeWorkspace) return;
    const deletedId = activeWorkspace.workspace_id;
    try {
      const r = await fetch(`${API}/finance/workspaces/${deletedId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) {
        toast.success('Workspace deleted');
        setWsDeleteConfirm(false);
        // Atomically fetch remaining workspaces and pick a safe fallback
        try {
          const res = await fetch(`${API}/finance/workspaces${orgId ? `?org_id=${orgId}` : ''}`, { credentials: 'include' });
          if (res.status === 409) { handle409(); return; }
          if (res.ok) {
            const remaining = await res.json();
            setWorkspaces(remaining);
            if (remaining.length) {
              const personal = remaining.find(w => w.workspace_type === 'personal');
              const business = remaining.find(w => w.workspace_type === 'business');
              setActiveWorkspace(personal || business || remaining[0]);
            } else {
              setActiveWorkspace(null);
            }
          } else {
            setWorkspaces([]);
            setActiveWorkspace(null);
          }
        } catch {
          setWorkspaces([]);
          setActiveWorkspace(null);
        }
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Delete failed');
      }
    } catch { toast.error('Network error'); }
  };

  const fetchWorkspaces = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API}/finance/workspaces?org_id=${orgId}`, { credentials: 'include' });
      if (res.status === 409) { handle409(); return; }
      if (res.ok) {
        const data = await res.json();
        setWorkspaces(data);
        if (data.length && !activeWorkspace) {
          // Default to business/organisation workspace if user has access
          const bizWs = data.find(w => w.workspace_type === 'business');
          setActiveWorkspace(bizWs || data[0]);
        } else if (data.length && activeWorkspace) {
          // Verify current workspace is still in the list
          const stillExists = data.find(w => w.workspace_id === activeWorkspace.workspace_id);
          if (!stillExists) setActiveWorkspace(data[0]);
        } else if (!data.length) {
          setActiveWorkspace(null);
        }
      }
    } catch (e) {
      console.error('Finance workspaces fetch error:', e);
      // Graceful fallback if user lost org access
      setWorkspaces([]);
      setActiveWorkspace(null);
    }
    finally { setLoading(false); }
  }, [activeWorkspace, orgId, handle409]);

  const fetchDashboard = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/dashboard-enhanced/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchExpenses = useCallback(async () => {
    if (!activeWorkspace) return;
    const params = new URLSearchParams();
    if (categoryFilter !== 'all') params.append('category', categoryFilter);
    try {
      const res = await fetch(`${API}/finance/expenses/${activeWorkspace.workspace_id}?${params}`, { credentials: 'include' });
      if (res.ok) setExpenses(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace, categoryFilter]);

  const fetchIncome = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/income/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setIncome(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchForecasts = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/forecasts/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setForecasts(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchScenarios = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/scenarios/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setScenarios(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchStaffing = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setStaffing(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchCustomFields = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/custom-fields/${activeWorkspace.workspace_id}`, { credentials: 'include' });
      if (res.ok) setCustomFields(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  const fetchForecastDetail = useCallback(async (forecastId) => {
    if (!activeWorkspace) return;
    try {
      const res = await fetch(`${API}/finance/forecasts/${activeWorkspace.workspace_id}/${forecastId}`, { credentials: 'include' });
      if (res.ok) setActiveForecast(await res.json());
    } catch (e) { console.error(e); }
  }, [activeWorkspace]);

  useEffect(() => { fetchWorkspaces(); fetchMyProfile(); }, [fetchWorkspaces, fetchMyProfile]);
  useEffect(() => {
    // Clear stale data when switching workspaces
    setExpenses([]); setIncome([]); setForecasts([]); setScenarios([]);
    setStaffing(null); setDashboard(null); setActiveForecast(null);
    setStaffingContractors([]); setOffboardedStaff([]);
    setOrgMembers([]); // Clear org members — only reload for org workspaces
  }, [activeWorkspace?.workspace_id]);
  useEffect(() => {
    // Only fetch org members for organization workspaces, not personal
    if (!activeWorkspace || activeWorkspace.workspace_type === 'personal') {
      setOrgMembers([]);
      return;
    }
    (async () => {
      try { const r = await fetch(`${API}/org/members`, { credentials: 'include' }); if (r.ok) setOrgMembers(await r.json()); } catch {}
    })();
  }, [API, activeWorkspace?.workspace_id, activeWorkspace?.workspace_type]);

  const fetchStaffingForDropdown = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}/entries?status=active`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        const contractors = (d.contractors || []).map(c => ({
          name: c.name, email: c.email || '', user_id: c.entry_id, role: 'Contractor', source: 'staffing',
        }));
        setStaffingContractors(contractors);
      }
      // Also fetch offboarded/archived
      const ra = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}/entries?status=archived`, { credentials: 'include' });
      if (ra.ok) {
        const da = await ra.json();
        const archived = [...(da.org_team || []), ...(da.contractors || [])].map(p => ({
          name: p.name, email: p.email || '', user_id: p.entry_id || p.user_id, role: p.job_title || 'Offboarded', source: 'archived',
        }));
        setOffboardedStaff(archived);
      }
    } catch {}
  }, [activeWorkspace]);

  useEffect(() => { fetchStaffingForDropdown(); }, [fetchStaffingForDropdown]);

  // Combined list: org members + staffing contractors (deduped by name)
  const allStaffAndContractors = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const m of orgMembers) {
      const key = (m.name || m.email || '').toLowerCase();
      if (key && !seen.has(key)) { seen.add(key); combined.push({ ...m, _active: true }); }
    }
    for (const c of staffingContractors) {
      const key = (c.name || c.email || '').toLowerCase();
      if (key && !seen.has(key)) { seen.add(key); combined.push({ ...c, _active: true }); }
    }
    for (const o of offboardedStaff) {
      const key = (o.name || o.email || '').toLowerCase();
      if (key && !seen.has(key)) { seen.add(key); combined.push({ ...o, _active: false }); }
    }
    return combined;
  }, [orgMembers, staffingContractors, offboardedStaff]);

  const fetchFullChangeLog = async (entityType) => {
    if (!activeWorkspace) return;
    setFullChangeLog({ open: true, logs: [], loading: true });
    try {
      const res = await fetch(`${API}/finance/audit-log/${activeWorkspace.workspace_id}?entity_type=${entityType}&limit=200`, { credentials: 'include' });
      if (res.ok) {
        const logs = await res.json();
        setFullChangeLog({ open: true, logs, loading: false });
      } else { setFullChangeLog(p => ({ ...p, loading: false })); }
    } catch { setFullChangeLog(p => ({ ...p, loading: false })); }
  };

  const fetchRefData = useCallback(async () => {
    if (!activeWorkspace) return;
    const wid = activeWorkspace.workspace_id;
    const fj = async (path) => { try { const r = await fetch(`${API}/finance/ref/${wid}/${path}`, { credentials: 'include' }); return r.ok ? await r.json() : []; } catch { return []; } };
    const [accts, cats, bus, projs, vnds, sofs, icats] = await Promise.all([
      fj('expense-accounts'), fj('expense-categories'), fj('business-units'), fj('projects'), fj('vendors'),
      fj('source-of-funds'), fj('income-categories'),
    ]);
    setRefAccounts(accts); setRefCategories(cats); setRefBusinessUnits(bus); setRefProjects(projs); setRefVendors(vnds);
    setRefSourceOfFunds(sofs); setRefIncomeCategories(icats);
    try { const r = await fetch(`${API}/org/departments`, { credentials: 'include' }); if (r.ok) setOrgDepartments(await r.json()); } catch {}
  }, [API, activeWorkspace]);

  useEffect(() => {
    if (!activeWorkspace) return;
    fetchDashboard();
    fetchExpenses();
    fetchIncome();
    fetchForecasts();
    fetchScenarios();
    fetchStaffing();
    fetchCustomFields();
    fetchRefData();
  }, [activeWorkspace, fetchDashboard, fetchExpenses, fetchIncome, fetchForecasts, fetchScenarios, fetchStaffing, fetchCustomFields, fetchRefData]);

  // ==================== REFERENCE DATA CRUD ====================
  const refCrud = async (type, method, id, data) => {
    if (!activeWorkspace) return null;
    const wid = activeWorkspace.workspace_id;
    const url = id ? `${API}/finance/ref/${wid}/${type}/${id}` : `${API}/finance/ref/${wid}/${type}`;
    const r = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: data ? JSON.stringify(data) : undefined });
    if (r.ok) { fetchRefData(); return await r.json(); }
    return null;
  };

  const saveRefItem = async (type, form, editId) => {
    let r;
    if (type === '_org_departments') {
      // Use org departments API for bidirectional sync
      const url = editId ? `${API}/org/departments/${editId}` : `${API}/org/departments`;
      const res = await fetch(url, { method: editId ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { r = await res.json(); fetchRefData(); }
    } else {
      r = await refCrud(type, editId ? 'PUT' : 'POST', editId, form);
    }
    if (r) { toast.success(editId ? 'Updated' : 'Created'); setShowRefForm(false); setRefEditing(null); setRefForm({}); }
  };

  const deleteRefItem = async (type, id) => {
    if (!confirm('Delete this item?')) return;
    if (type === '_org_departments') {
      await fetch(`${API}/org/departments/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchRefData();
    } else {
      await refCrud(type, 'DELETE', id);
    }
    toast.success('Deleted');
  };

  const saveVendor = async () => {
    const r = await refCrud('vendors', vendorEditing ? 'PUT' : 'POST', vendorEditing?.vendor_id, vendorForm);
    if (r) { toast.success(vendorEditing ? 'Updated' : 'Created'); setShowVendorForm(false); setVendorEditing(null); setVendorForm({}); }
  };

  const deleteVendor = async (id) => {
    if (!confirm('Delete this vendor?')) return;
    await refCrud('vendors', 'DELETE', id);
    toast.success('Deleted');
  };

  // ==================== CRUD HANDLERS ====================
  const saveExpense = async () => {
    if (!expenseForm.amount_usd) { toast.error('Amount (USD) is required'); return; }
    const url = editItem
      ? `${API}/finance/expenses/${activeWorkspace.workspace_id}/${editItem.transaction_id}`
      : `${API}/finance/expenses/${activeWorkspace.workspace_id}`;
    try {
      const res = await fetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(expenseForm),
      });
      if (res.ok) {
        toast.success(editItem ? 'Expense updated' : 'Expense added');
        setExpenseModal(false); setEditItem(null); setExpenseForm(emptyExpense);
        fetchExpenses(); fetchDashboard();
      }
    } catch { toast.error('Failed to save expense'); }
  };

  const saveIncome = async () => {
    if (!incomeForm.amount_usd) { toast.error('Amount (USD) is required'); return; }
    const url = editItem
      ? `${API}/finance/income/${activeWorkspace.workspace_id}/${editItem.income_id}`
      : `${API}/finance/income/${activeWorkspace.workspace_id}`;
    try {
      const res = await fetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(incomeForm),
      });
      if (res.ok) {
        toast.success(editItem ? 'Income updated' : 'Income added');
        setIncomeModal(false); setEditItem(null); setIncomeForm(emptyIncome);
        fetchIncome(); fetchDashboard();
      }
    } catch { toast.error('Failed to save income'); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await fetch(`${API}/finance/expenses/${activeWorkspace.workspace_id}/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchExpenses(); fetchDashboard();
  };

  const deleteIncome = async (id) => {
    if (!window.confirm('Delete this income record?')) return;
    await fetch(`${API}/finance/income/${activeWorkspace.workspace_id}/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchIncome(); fetchDashboard();
  };


  const inlineUpdateExpense = async (transactionId, changes) => {
    try {
      const res = await fetch(`${API}/finance/expenses/${activeWorkspace.workspace_id}/${transactionId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(changes),
      });
      if (res.ok) { toast.success('Updated'); fetchExpenses(); }
      else toast.error('Update failed');
    } catch { toast.error('Update failed'); }
  };

  const inlineUpdateIncome = async (incomeId, changes) => {
    try {
      const res = await fetch(`${API}/finance/income/${activeWorkspace.workspace_id}/${incomeId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(changes),
      });
      if (res.ok) { toast.success('Updated'); fetchIncome(); }
      else toast.error('Update failed');
    } catch { toast.error('Update failed'); }
  };

  const saveForecast = async () => {
    if (!forecastForm.name) { toast.error('Name required'); return; }
    try {
      const res = await fetch(`${API}/finance/forecasts/${activeWorkspace.workspace_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(forecastForm),
      });
      if (res.ok) { toast.success('Forecast created'); setForecastModal(false); setForecastForm({ name: '', start_month: '', end_month: '' }); fetchForecasts(); }
    } catch { toast.error('Failed'); }
  };

  const updateForecast = async (forecastId, data) => {
    try {
      const res = await fetch(`${API}/finance/forecasts/${activeWorkspace.workspace_id}/${forecastId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(data),
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveForecast(updated);
        fetchForecasts();
        toast.success('Forecast updated');
      }
    } catch { toast.error('Failed to update forecast'); }
  };

  const deleteForecast = async (forecastId) => {
    if (!window.confirm('Delete this forecast?')) return;
    await fetch(`${API}/finance/forecasts/${activeWorkspace.workspace_id}/${forecastId}`, { method: 'DELETE', credentials: 'include' });
    setActiveForecast(null);
    fetchForecasts();
    toast.success('Forecast deleted');
  };

  const syncStaffing = async () => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const res = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}/sync`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ month }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced ${data.synced} payroll entries for ${month}`);
        fetchExpenses(); fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Sync failed');
      }
    } catch { toast.error('Staffing sync failed'); }
  };

  const saveScenario = async () => {
    if (!scenarioForm.name) { toast.error('Name required'); return; }
    try {
      const res = await fetch(`${API}/finance/scenarios/${activeWorkspace.workspace_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(scenarioForm),
      });
      if (res.ok) { toast.success('Scenario created'); setScenarioModal(false); setScenarioForm({ name: '', scenario_type: 'base', description: '' }); fetchScenarios(); }
    } catch { toast.error('Failed'); }
  };

  const createWorkspace = async () => {
    try {
      const res = await fetch(`${API}/finance/workspaces${orgId ? `?org_id=${orgId}` : ''}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(wsForm),
      });
      if (res.status === 409) { handle409(); return; }
      if (res.ok) { toast.success('Workspace created'); setWsModal(false); fetchWorkspaces(); }
    } catch { toast.error('Failed'); }
  };

  // ==================== IMPORT / EXPORT ====================
  const exportFinanceData = (type, fmt = 'csv') => {
    const data = type === 'expenses' ? expenses : income;
    if (!data.length) { toast.error(`No ${type} to export`); return; }

    const fields = type === 'expenses' ? EXPENSE_FIELDS : INCOME_FIELDS;
    const coreFields = fields.filter(f => !f.key.startsWith('custom_'));

    if (fmt === 'json') {
      const exportData = data.map(d => {
        const row = {};
        coreFields.forEach(f => { row[f.label] = d[f.key] ?? ''; });
        return row;
      });
      const blob = new Blob([JSON.stringify({ type, data: exportData, count: exportData.length, exported_at: new Date().toISOString() }, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `orka_${type}_${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} ${type}`);
      return;
    }

    // CSV — use human-readable labels as headers (matches template exactly)
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = data.map(d => coreFields.map(f => esc(d[f.key])).join(','));
    const csv = [coreFields.map(f => esc(f.label)).join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `orka_${type}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} ${type}`);
  };

  // ==================== IMPORT WITH COLUMN MAPPING ====================
  const EXPENSE_FIELDS = [
    { key: 'date', label: 'Date of Expense', required: true },
    { key: 'account_paid_from', label: 'Account Paid From' },
    { key: 'business_unit', label: 'Business Unit' },
    { key: 'expense_item', label: 'Expense Item' },
    { key: 'description', label: 'Expense Description' },
    { key: 'vendor', label: 'Vendor/Supplier' },
    { key: 'amount_usd', label: 'Amount (USD)', required: true },
    { key: 'original_amount', label: 'Original Currency Amount' },
    { key: 'original_currency', label: 'Original Currency Type' },
    { key: 'org_function', label: 'Expense Organisation Function' },
    { key: 'expense_type', label: 'Expense Type' },
    { key: 'expense_sub_type', label: 'Expense Sub Type' },
    { key: 'staff_contractor_name', label: 'Staff/Contractor Name' },
    { key: 'expense_role', label: 'Role' },
    { key: 'project_attribution', label: 'Project Attribution' },
    { key: 'receipt_type', label: 'Receipt/Evidence Type' },
    { key: 'receipt_link', label: 'Receipt/Evidence Type Link' },
    { key: 'bank_statement_entry', label: 'Bank Statement Entry' },
    { key: 'bank_statement_date', label: 'Bank Statement Date' },
    { key: 'comments', label: 'Comments' },
    ...customFields.expense_fields.map(f => ({ key: `custom_${f.key}`, label: f.label })),
  ];

  const INCOME_FIELDS = [
    { key: 'date_received', label: 'Date Income Received', required: true },
    { key: 'due_date', label: 'Due Date of Payment' },
    { key: 'invoice_issue_date', label: 'Date Invoice Issued' },
    { key: 'days_late', label: 'Days Late' },
    { key: 'invoice_number', label: 'Invoice Number' },
    { key: 'invoice_status', label: 'Invoice Status' },
    { key: 'income_item_summary', label: 'Income Item Summary' },
    { key: 'source_of_funds', label: 'Source of Funds' },
    { key: 'amount_usd', label: 'Amount (USD)', required: true },
    { key: 'is_loan', label: 'Is Loan Y/N?' },
    { key: 'original_amount', label: 'Original Currency Amount' },
    { key: 'original_currency', label: 'Original Currency Type' },
    { key: 'business_unit', label: 'Income Business Unit' },
    { key: 'income_source_type', label: 'Income Source Type' },
    { key: 'sales_person', label: 'Sales Person Associated' },
    { key: 'income_paid_to', label: 'Income Paid To' },
    { key: 'payment_channel', label: 'Income Payment Channel' },
    { key: 'income_classification', label: 'Income Classification' },
    { key: 'market', label: 'Market' },
    { key: 'income_note', label: 'Income Note' },
    { key: 'receipt_issued', label: 'Receipt Issued Y/N' },
    { key: 'receipt_link', label: 'Receipt Link' },
    ...customFields.income_fields.map(f => ({ key: `custom_${f.key}`, label: f.label })),
  ];

  const openImportDialog = (type) => {
    setImportDialogType(type);
    setImportDialog(true);
    setImportStep(1);
    setImportRawData([]);
    setImportHeaders([]);
    setImportMapping({});
  };

  const downloadTemplate = () => {
    const fields = importDialogType === 'expenses' ? EXPENSE_FIELDS : INCOME_FIELDS;
    const labels = fields.filter(f => !f.key.startsWith('custom_')).map(f => f.label);
    const csv = labels.map(h => `"${h}"`).join(',') + '\r\n';
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orka_${importDialogType}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      let records = [];
      let headers = [];

      if (file.name.endsWith('.json')) {
        const parsed = JSON.parse(text);
        records = Array.isArray(parsed.data) ? parsed.data : Array.isArray(parsed) ? parsed : [];
        if (records.length) headers = Object.keys(records[0]);
      } else {
        // Use Papa Parse for proper RFC 4180 CSV parsing — handles quoted commas, newlines, etc.
        const result = Papa.parse(text, { header: true, skipEmptyLines: true, transformHeader: (h) => h.trim() });
        headers = result.meta.fields || [];
        records = result.data;
      }

      if (!records.length) { toast.error('No records found'); return; }

      setImportRawData(records);
      setImportHeaders(headers);

      // Auto-map: deterministic matching of CSV headers → field keys
      const targetFields = importDialogType === 'expenses' ? EXPENSE_FIELDS : INCOME_FIELDS;
      const autoMap = {};
      const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
      const labelToKey = {};
      const keyToKey = {};
      targetFields.forEach(f => {
        labelToKey[normalize(f.label)] = f.key;
        keyToKey[normalize(f.key)] = f.key;
      });

      headers.forEach(h => {
        const hn = normalize(h);
        // Priority 1: exact normalized label match
        if (labelToKey[hn]) { autoMap[h] = labelToKey[hn]; return; }
        // Priority 2: exact normalized key match
        if (keyToKey[hn]) { autoMap[h] = keyToKey[hn]; return; }
        // Priority 3: longest substring match
        let bestMatch = null;
        let bestLen = 0;
        targetFields.forEach(f => {
          const ln = normalize(f.label);
          const kn = normalize(f.key);
          if ((ln.includes(hn) || hn.includes(ln)) && ln.length > bestLen) { bestMatch = f.key; bestLen = ln.length; }
          if ((kn.includes(hn) || hn.includes(kn)) && kn.length > bestLen) { bestMatch = f.key; bestLen = kn.length; }
        });
        if (bestMatch) autoMap[h] = bestMatch;
      });

      // Deduplicate: one field key per CSV column
      const seenKeys = {};
      Object.entries(autoMap).forEach(([csvCol, fieldKey]) => {
        if (seenKeys[fieldKey]) {
          const prevH = normalize(seenKeys[fieldKey]);
          const currH = normalize(csvCol);
          const fl = normalize(targetFields.find(f => f.key === fieldKey)?.label || '');
          if (Math.abs(currH.length - fl.length) < Math.abs(prevH.length - fl.length)) {
            delete autoMap[seenKeys[fieldKey]];
            seenKeys[fieldKey] = csvCol;
          } else {
            delete autoMap[csvCol];
          }
        } else {
          seenKeys[fieldKey] = csvCol;
        }
      });

      setImportMapping(autoMap);
    } catch (err) {
      toast.error(`Failed to parse file: ${err.message}`);
    }
  };

  const executeImport = async () => {
    if (!activeWorkspace || !importRawData.length) return;
    setImporting(true);

    // Transform records using mapping
    const mapped = importRawData.map(row => {
      const out = {};
      Object.entries(importMapping).forEach(([csvCol, fieldKey]) => {
        if (fieldKey && row[csvCol] !== undefined) out[fieldKey] = row[csvCol];
      });
      return out;
    });

    try {
      const endpoint = importDialogType === 'expenses' ? 'expenses' : 'income';
      const CHUNK_SIZE = 200;
      let totalImported = 0;
      let allErrors = [];

      for (let i = 0; i < mapped.length; i += CHUNK_SIZE) {
        const chunk = mapped.slice(i, i + CHUNK_SIZE);
        const res = await fetch(`${API}/finance/${endpoint}/${activeWorkspace.workspace_id}/bulk-import`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ records: chunk }),
        });
        if (res.ok) {
          const data = await res.json();
          totalImported += (data.imported || 0);
          if (data.errors?.length) allErrors.push(...data.errors);
        } else {
          const err = await res.json().catch(() => ({}));
          allErrors.push(`Chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${err.detail || res.statusText}`);
        }
      }

      toast.success(`Imported ${totalImported} ${importDialogType}`);
      if (allErrors.length) toast.warning(`${allErrors.length} errors during import`);
      setImportStep(3);
      if (importDialogType === 'expenses') fetchExpenses();
      else fetchIncome();
      fetchDashboard();
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // ==================== BULK OPERATIONS ====================
  const bulkDelete = async (type) => {
    const ids = type === 'expenses' ? [...selectedExpenses] : [...selectedIncome];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} ${type}?`)) return;

    const endpoint = type === 'expenses' ? 'expenses' : 'income';
    const res = await fetch(`${API}/finance/${endpoint}/${activeWorkspace.workspace_id}/bulk-delete`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ ids }),
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Deleted ${data.deleted} ${type}`);
      if (type === 'expenses') { fetchExpenses(); setSelectedExpenses(new Set()); }
      else { fetchIncome(); setSelectedIncome(new Set()); }
      fetchDashboard();
    }
  };

  const bulkUpdate = async () => {
    const type = bulkEditType;
    const ids = type === 'expenses' ? [...selectedExpenses] : [...selectedIncome];
    if (!ids.length) return;

    const endpoint = type === 'expenses' ? 'expenses' : 'income';
    const res = await fetch(`${API}/finance/${endpoint}/${activeWorkspace.workspace_id}/bulk-update`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ ids, updates: bulkEditForm }),
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Updated ${data.updated} ${type}`);
      setBulkEditModal(false);
      setBulkEditForm({});
      if (type === 'expenses') { fetchExpenses(); setSelectedExpenses(new Set()); }
      else { fetchIncome(); setSelectedIncome(new Set()); }
      fetchDashboard();
    }
  };

  // ==================== FILTER HELPERS ====================
  // Pass all data — filtering/sorting is handled inside FinanceLedgerTable
  const filteredExpenses = expenses;
  const filteredIncome = income;

  if (loading) return (
    <PageLayout title="Finance" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    </PageLayout>
  );

  // Derived: can this user manage workspaces?
  const isFinanceAdmin = myFinanceProfile?.permissions?.includes('finance.workspaces.manage') || false;
  const canCreateWorkspace = myFinanceProfile?.permissions?.includes('finance.workspaces.create') || false;

  // Header actions for PageLayout
  const headerActions = (
    <div className="flex items-center gap-2">
      <Select value={activeWorkspace?.workspace_id || ''} onValueChange={(v) => setActiveWorkspace(workspaces.find(w => w.workspace_id === v))}>
        <SelectTrigger className="w-48 h-9" data-testid="workspace-selector">
          <SelectValue placeholder="Select workspace" />
        </SelectTrigger>
        <SelectContent>
          {workspaces.map(ws => (
            <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
              <div className="flex items-center gap-2">
                {ws.workspace_type === 'personal' ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                {ws.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {canCreateWorkspace && <Button variant="outline" size="sm" onClick={() => setWsModal(true)} data-testid="new-workspace-btn">
        <Plus className="w-4 h-4" />
      </Button>}
      {isFinanceAdmin && activeWorkspace && activeWorkspace.workspace_type !== 'personal' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="workspace-manage-btn">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => { setWsRenameValue(activeWorkspace.name); setWsRenameModal(true); }} data-testid="rename-workspace-btn">
              <Pencil className="w-3.5 h-3.5 mr-2" />Rename Workspace
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={() => setWsDeleteConfirm(true)} data-testid="delete-workspace-btn">
              <Trash2 className="w-3.5 h-3.5 mr-2" />Delete Workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <div className="w-px h-6 bg-border" />
      <Button variant="outline" size="sm" onClick={() => navigate('/expense-capture')} data-testid="launch-expense-capture-btn">
        <Receipt className="w-3.5 h-3.5 mr-1.5" />Expense Capture
      </Button>
    </div>
  );

  // ==================== RENDER ====================
  // No workspace fallback — covers both empty list AND transient null-activeWorkspace
  if (!loading && !activeWorkspace) {
    return (
      <PageLayout title="Orka Finance" user={user} onLogout={logout} theme={theme} setTheme={setTheme}>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="no-workspace-fallback">
          <Wallet className="w-12 h-12 text-muted-foreground/30 mb-4" />
          <h2 className="text-lg font-medium mb-2">No Finance Workspaces Available</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {myFinanceProfile?.profile_key === 'user'
              ? "Your current access profile only allows Personal Finance. Ask an admin to create a workspace for you."
              : "No workspaces found. Create one to get started."}
          </p>
          {canCreateWorkspace && (
            <Button className="mt-4" onClick={() => setWsModal(true)} data-testid="create-first-workspace-btn">
              <Plus className="w-4 h-4 mr-2" />Create Workspace
            </Button>
          )}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Orka Finance" user={user} onLogout={logout} theme={theme} setTheme={setTheme} actions={headerActions}>
      <input ref={fileInputRef} type="file" accept=".csv,.json" className="hidden" onChange={handleFileSelect} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedExpenses(new Set()); setSelectedIncome(new Set()); }} className="space-y-4" data-testid="finance-page">
        <div className="space-y-1">
          {/* Primary tabs */}
          <TabsList className="bg-card border shadow-sm">
            {[
              { value: 'dashboard', label: 'Dashboard', icon: Activity },
              { value: 'pl-forecast', label: 'P/L & Forecast', icon: LineChartIcon },
              { value: 'expenses', label: 'Expenses', icon: CreditCard },
              { value: 'income', label: 'Income', icon: Wallet },
              { value: 'cap-table', label: 'Fundraising', icon: PieChart },
              { value: 'reports', label: 'Reports', icon: FileText },
              { value: 'staffing', label: 'Staffing', icon: Users },
              { value: 'scenarios', label: 'Scenarios', icon: Layers },
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value} className="text-xs gap-1.5" data-testid={`tab-${tab.value}`}>
                <tab.icon className="w-3.5 h-3.5" />{tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {/* Reference Data tabs */}
          <TabsList className="bg-card/60 border shadow-sm h-8">
            <span className="text-[10px] text-muted-foreground font-medium px-2 select-none">Master Data</span>
            {[
              { value: 'ref-accounts', label: 'Accounts', icon: Building2 },
              { value: 'ref-vendors', label: 'Vendors', icon: Truck },
              { value: 'ref-source-of-funds', label: 'Source of Funds', icon: Landmark },
              { value: 'ref-expense-categories', label: 'Expense Categories', icon: Tag },
              { value: 'ref-income-types', label: 'Income Types', icon: Database },
              { value: 'ref-business-units', label: 'Business Units', icon: Building2 },
              { value: 'ref-departments', label: 'Departments', icon: Network },
              { value: 'ref-projects', label: 'Projects', icon: FolderKanban },
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value} className="text-[11px] gap-1 h-7 px-2" data-testid={`tab-${tab.value}`}>
                <tab.icon className="w-3 h-3" />{tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* ==================== DASHBOARD TAB ==================== */}
        <TabsContent value="dashboard">
          {dashboard ? <DashboardView data={dashboard} workspaceId={activeWorkspace?.workspace_id} /> : (
            <div className="text-center py-16 text-muted-foreground">
              <Activity className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>No data yet. Add expenses or income to see your dashboard.</p>
            </div>
          )}
        </TabsContent>

        {/* ==================== EXPENSES TAB ==================== */}
        <TabsContent value="expenses">
          {/* Expense Sub-Tabs */}
          <div className="flex items-center gap-1 mb-3 border-b pb-2 overflow-x-auto">
            {[
              { id: 'ledger', label: 'Ledger' },
              { id: 'vendors', label: 'Vendors/Suppliers' },
              { id: 'categories', label: 'Expense Categories' },
              { id: 'projects', label: 'Projects' },
            ].map(st => (
              <button key={st.id} className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap",
                expenseSubTab === st.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/50")}
                onClick={() => setExpenseSubTab(st.id)} data-testid={`expense-subtab-${st.id}`}>{st.label}</button>
            ))}
          </div>

          {expenseSubTab === 'ledger' ? (<>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
            </div>
            <div className="flex items-center gap-2">
              {selectedExpenses.size > 0 && (
                <>
                  <span className="text-xs text-muted-foreground font-medium">{selectedExpenses.size} selected</span>
                  <Button variant="outline" size="sm" onClick={() => { setBulkEditType('expenses'); setBulkEditForm({}); setBulkEditModal(true); }} data-testid="bulk-edit-expenses-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" />Bulk Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => bulkDelete('expenses')} data-testid="bulk-delete-expenses-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete Selected
                  </Button>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-expenses-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => exportFinanceData('expenses', 'csv')}>Export CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportFinanceData('expenses', 'json')}>Export JSON</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={() => openImportDialog('expenses')} data-testid="import-expenses-btn">
                <Upload className="w-4 h-4 mr-1" />Import
              </Button>
              <Button variant="outline" size="sm" onClick={() => fetchFullChangeLog('expense')} data-testid="full-changelog-expenses-btn">
                <History className="w-4 h-4 mr-1" />Change Log
              </Button>
              <Button onClick={() => { setEditItem(null); setExpenseForm(emptyExpense); setExpenseModal(true); }} data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-1" />Add Expense
              </Button>
            </div>
          </div>
          <FinanceLedgerTable columns={EXPENSE_COLUMNS} data={filteredExpenses} idField="transaction_id"
            selected={selectedExpenses} onSelectionChange={setSelectedExpenses}
            emptyIcon={Receipt} emptyLabel="No expenses recorded"
            workspaceId={activeWorkspace?.workspace_id}
            orgMembers={allStaffAndContractors}
            refData={{ accounts: refAccounts, categories: refCategories, businessUnits: refBusinessUnits, projects: refProjects, vendors: refVendors, departments: orgDepartments }}
            onCreateStaff={async (name) => {
              try {
                const r = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}/entries`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name, employment_type: 'contractor', pay_type: 'salary', annual_salary: '', currency: 'USD' }),
                });
                if (r.ok) { toast.success(`Added "${name}" to staffing list`); fetchStaffingForDropdown(); }
                else { toast.error('Failed to create contractor'); }
              } catch { toast.error('Failed to create contractor'); }
            }}
            onCreateRef={async (refKey, nameOrObj, rowContext) => {
              // If a full object is passed (already created), just refresh data
              const name = typeof nameOrObj === 'object' ? nameOrObj.name : nameOrObj;
              const alreadyCreated = typeof nameOrObj === 'object' && nameOrObj.project_id;
              const typeMap = { accounts: 'expense-accounts', businessUnits: 'business-units', projects: 'projects', vendors: 'vendors' };
              if (typeMap[refKey]) {
                if (!alreadyCreated) await refCrud(typeMap[refKey], 'POST', null, { name });
                else await fetchRefData();  // Just refresh if already created
              } else if (refKey === '_expense_types') {
                // Create as a top-level expense category (Type)
                await refCrud('expense-categories', 'POST', null, { name, code: '', parent_code: '' });
              } else if (refKey === 'categories') {
                // Create as a sub-type under the current row's expense_type
                const parentType = rowContext?.expense_type || '';
                await refCrud('expense-categories', 'POST', null, { name, code: '', parent_code: parentType });
              }
            }}
            onEdit={(item) => { setEditItem(item); setExpenseForm({...emptyExpense, ...item}); setExpenseModal(true); }}
            onDelete={(id) => deleteExpense(id)}
            onInlineUpdate={(id, changes) => inlineUpdateExpense(id, changes)} />
          </>) : expenseSubTab === 'vendors' ? (
            /* ========== VENDORS/SUPPLIERS SUB-TAB ========== */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Vendors & Suppliers</h3>
                  <p className="text-[10px] text-muted-foreground">Manage your vendor/supplier list. Bidirectionally synced with forecast driver items.</p>
                </div>
                <Button size="sm" onClick={() => { setVendorEditing(null); setVendorForm({ name: '', contact_name: '', email: '', phone: '', address: '', website: '', category: '', payment_terms: '', notes: '' }); setShowVendorForm(true); }} data-testid="add-vendor-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Vendor
                </Button>
              </div>
              {showVendorForm && (
                <div className="bg-card border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                      <Input value={vendorForm.name || ''} onChange={e => setVendorForm(p => ({ ...p, name: e.target.value }))} data-testid="vendor-name-input" /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Contact Name</label>
                      <Input value={vendorForm.contact_name || ''} onChange={e => setVendorForm(p => ({ ...p, contact_name: e.target.value }))} /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Email</label>
                      <Input value={vendorForm.email || ''} onChange={e => setVendorForm(p => ({ ...p, email: e.target.value }))} /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                      <Input value={vendorForm.phone || ''} onChange={e => setVendorForm(p => ({ ...p, phone: e.target.value }))} /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Website</label>
                      <Input value={vendorForm.website || ''} onChange={e => setVendorForm(p => ({ ...p, website: e.target.value }))} /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Category</label>
                      <Input value={vendorForm.category || ''} onChange={e => setVendorForm(p => ({ ...p, category: e.target.value }))} /></div>
                    <div><label className="text-xs text-muted-foreground mb-1 block">Payment Terms</label>
                      <Input value={vendorForm.payment_terms || ''} onChange={e => setVendorForm(p => ({ ...p, payment_terms: e.target.value }))} /></div>
                    <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1 block">Address</label>
                      <Input value={vendorForm.address || ''} onChange={e => setVendorForm(p => ({ ...p, address: e.target.value }))} /></div>
                    <div className="col-span-3"><label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                      <Input value={vendorForm.notes || ''} onChange={e => setVendorForm(p => ({ ...p, notes: e.target.value }))} /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveVendor} data-testid="save-vendor-btn">{vendorEditing ? 'Update' : 'Create'}</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowVendorForm(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              <div className="bg-card rounded-lg border overflow-hidden">
                <table className="w-full text-sm" data-testid="vendors-table">
                  <thead className="bg-card border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Name</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Contact</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Email</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Category</th>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Payment Terms</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {refVendors.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-xs">No vendors yet. Add your first vendor above.</td></tr>
                    ) : refVendors.map(v => (
                      <tr key={v.vendor_id} className="hover:bg-accent/50 group" data-testid={`vendor-row-${v.vendor_id}`}>
                        <td className="px-3 py-2.5 font-medium">{v.name}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{v.contact_name || '-'}</td>
                        <td className="px-3 py-2.5 text-xs">{v.email || '-'}</td>
                        <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{v.category || '-'}</Badge></td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{v.payment_terms || '-'}</td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setVendorEditing(v); setVendorForm(v); setShowVendorForm(true); }}><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteVendor(v.vendor_id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* ========== REFERENCE DATA SUB-TABS ========== */
            <RefDataTab type={expenseSubTab} activeWorkspace={activeWorkspace} API={API}
              refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
              refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
              onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
              form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
              showForm={showRefForm} setShowForm={setShowRefForm} />
          )}
        </TabsContent>

        {/* ==================== INCOME TAB ==================== */}
        <TabsContent value="income">
          {/* Income Sub-Tabs */}
          <div className="flex items-center gap-1 mb-3 border-b pb-2 overflow-x-auto">
            {[
              { id: 'ledger', label: 'Ledger' },
              { id: 'invoices', label: 'Invoices' },
              { id: 'sourceOfFunds', label: 'Source of Funds' },
              { id: 'incomeCategories', label: 'Income Types & Classifications' },
              { id: 'projects', label: 'Projects' },
            ].map(st => (
              <button key={st.id} className={cn("px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap",
                incomeSubTab === st.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/50")}
                onClick={() => { setIncomeSubTab(st.id); setIncRefForm({}); setIncRefEditing(null); setShowIncRefForm(false); }} data-testid={`income-subtab-${st.id}`}>{st.label}</button>
            ))}
          </div>

          {incomeSubTab === 'ledger' ? (<>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
            </div>
            <div className="flex items-center gap-2">
              {selectedIncome.size > 0 && (
                <>
                  <span className="text-xs text-muted-foreground font-medium">{selectedIncome.size} selected</span>
                  <Button variant="outline" size="sm" onClick={() => { setBulkEditType('income'); setBulkEditForm({}); setBulkEditModal(true); }} data-testid="bulk-edit-income-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" />Bulk Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => bulkDelete('income')} data-testid="bulk-delete-income-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Delete Selected
                  </Button>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-income-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => exportFinanceData('income', 'csv')}>Export CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportFinanceData('income', 'json')}>Export JSON</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={() => openImportDialog('income')} data-testid="import-income-btn">
                <Upload className="w-4 h-4 mr-1" />Import
              </Button>
              <Button variant="outline" size="sm" onClick={() => fetchFullChangeLog('income')} data-testid="full-changelog-income-btn">
                <History className="w-4 h-4 mr-1" />Change Log
              </Button>
              <Button onClick={() => { setEditItem(null); setIncomeForm(emptyIncome); setIncomeModal(true); }} data-testid="add-income-btn">
                <Plus className="w-4 h-4 mr-1" />Add Income
              </Button>
            </div>
          </div>
          <FinanceLedgerTable columns={INCOME_COLUMNS} data={filteredIncome} idField="income_id"
            selected={selectedIncome} onSelectionChange={setSelectedIncome}
            emptyIcon={DollarSign} emptyLabel="No income recorded"
            workspaceId={activeWorkspace?.workspace_id}
            orgMembers={allStaffAndContractors}
            refData={{ accounts: refAccounts, categories: refCategories, businessUnits: refBusinessUnits, projects: refProjects, vendors: refVendors, departments: orgDepartments, sourceOfFunds: refSourceOfFunds, incomeCategories: refIncomeCategories }}
            onCreateRef={async (refKey, name, rowContext) => {
              if (refKey === 'sourceOfFunds') {
                await refCrud('source-of-funds', 'POST', null, { name, type: 'Company' });
              } else if (refKey === '_income_types') {
                await refCrud('income-categories', 'POST', null, { name, code: '', parent_code: '' });
              } else if (refKey === '_income_classifications') {
                await refCrud('income-categories', 'POST', null, { name, code: '', parent_code: rowContext?.income_source_type || '' });
              } else if (refKey === '_income_markets') {
                await refCrud('income-categories', 'POST', null, { name, code: '', parent_code: rowContext?.income_classification || '' });
              } else if (refKey === 'accounts') {
                await refCrud('expense-accounts', 'POST', null, { name });
              } else if (refKey === 'businessUnits') {
                await refCrud('business-units', 'POST', null, { name });
              }
            }}
            onCreateStaff={async (name) => {
              try {
                const r = await fetch(`${API}/finance/staffing/${activeWorkspace.workspace_id}/entries`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name, employment_type: 'contractor', pay_type: 'salary', annual_salary: '', currency: 'USD' }),
                });
                if (r.ok) { toast.success(`Added "${name}" to staffing list`); fetchStaffingForDropdown(); }
                else { toast.error('Failed to create contractor'); }
              } catch { toast.error('Failed to create contractor'); }
            }}
            onClassificationChange={async (row, newClassification) => {
              if (!row || !activeWorkspace) return;
              const sourceName = row.source_of_funds || '';
              if (!sourceName) return;
              const isInvestment = (newClassification || '').toLowerCase().includes('investment');
              try {
                await fetch(`${API}/finance/ref/${activeWorkspace.workspace_id}/source-of-funds/mark-investor`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ source_name: sourceName, is_investor: isInvestment }),
                });
                if (isInvestment) toast.info(`"${sourceName}" marked as Investor`);
                fetchRefData();
              } catch {}
            }}
            onEdit={(item) => { setEditItem(item); setIncomeForm({...emptyIncome, ...item}); setIncomeModal(true); }}
            onDelete={(id) => deleteIncome(id)}
            onInlineUpdate={(id, changes) => inlineUpdateIncome(id, changes)} />
          </>) : incomeSubTab === 'invoices' ? (
            <InvoicesTab income={income} workspaceId={activeWorkspace?.workspace_id} API={API} fetchIncome={fetchIncome} />
          ) : incomeSubTab === 'sourceOfFunds' ? (
            <SourceOfFundsTab
              items={refSourceOfFunds} activeWorkspace={activeWorkspace} API={API}
              onSave={async (data, editId) => { await refCrud('source-of-funds', editId ? 'PUT' : 'POST', editId, data); setIncRefForm({}); setIncRefEditing(null); setShowIncRefForm(false); }}
              onDelete={async (id) => { await refCrud('source-of-funds', 'DELETE', id); }}
              form={incRefForm} setForm={setIncRefForm} editing={incRefEditing} setEditing={setIncRefEditing}
              showForm={showIncRefForm} setShowForm={setShowIncRefForm} />
          ) : incomeSubTab === 'incomeCategories' ? (
            <IncomeCategoriesTab refIncomeCategories={refIncomeCategories}
              onSave={async (data, editId) => { await refCrud('income-categories', editId ? 'PUT' : 'POST', editId, data); setIncRefForm({}); setIncRefEditing(null); setShowIncRefForm(false); }}
              onDelete={async (id) => { await refCrud('income-categories', 'DELETE', id); }}
              activeWorkspace={activeWorkspace} fetchRefData={fetchRefData}
              form={incRefForm} setForm={setIncRefForm} editing={incRefEditing} setEditing={setIncRefEditing}
              showForm={showIncRefForm} setShowForm={setShowIncRefForm} />
          ) : incomeSubTab === 'projects' ? (
            <RefDataTab type="projects" activeWorkspace={activeWorkspace} API={API}
              refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
              refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
              onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
              form={incRefForm} setForm={setIncRefForm} editing={incRefEditing} setEditing={setIncRefEditing}
              showForm={showIncRefForm} setShowForm={setShowIncRefForm} />
          ) : null}
        </TabsContent>

        {/* ==================== P/L & FORECAST TAB ==================== */}
        <TabsContent value="pl-forecast">
          {activeWorkspace && <PLForecastTab workspaceId={activeWorkspace.workspace_id} />}
        </TabsContent>

        {/* ==================== CAP TABLE TAB ==================== */}
        <TabsContent value="cap-table">
          {activeWorkspace && <CapTableTab workspaceId={activeWorkspace.workspace_id} />}
        </TabsContent>

        {/* ==================== SCENARIOS TAB ==================== */}
        <TabsContent value="scenarios">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Scenario Comparison</h2>
            <Button onClick={() => setScenarioModal(true)} data-testid="add-scenario-btn">
              <Plus className="w-4 h-4 mr-1" />New Scenario
            </Button>
          </div>
          <ScenarioView scenarios={scenarios} />
        </TabsContent>

        {/* ==================== REPORTS TAB ==================== */}
        <TabsContent value="reports">
          <ReportsView expenses={expenses} income={income} dashboard={dashboard} />
        </TabsContent>

        {/* ==================== STAFFING TAB ==================== */}
        <TabsContent value="staffing">
          <StaffingView workspaceId={activeWorkspace.workspace_id} workspaceType={activeWorkspace.workspace_type} />
        </TabsContent>

        {/* ==================== REFERENCE DATA TABS ==================== */}
        <TabsContent value="ref-accounts">
          <RefDataTab type="accounts" activeWorkspace={activeWorkspace} API={API}
            refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
            refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
            onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>
        <TabsContent value="ref-vendors">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Vendors & Suppliers</h3>
                <p className="text-[10px] text-muted-foreground">Shared master data. Used across Expenses, P/L Forecast, and Reconciliation.</p>
              </div>
              <Button size="sm" onClick={() => { setVendorEditing(null); setVendorForm({ name: '', contact_name: '', email: '', phone: '', address: '', website: '', category: '', payment_terms: '', notes: '' }); setShowVendorForm(true); }} data-testid="add-vendor-master-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Add Vendor
              </Button>
            </div>
            {showVendorForm && (
              <div className="bg-card border rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  {[{k:'name',l:'Name *'},{k:'contact_name',l:'Contact Name'},{k:'email',l:'Email'},{k:'phone',l:'Phone'},{k:'website',l:'Website'},{k:'category',l:'Category'},{k:'payment_terms',l:'Payment Terms'}].map(f=>(
                    <div key={f.k}><label className="text-xs text-muted-foreground mb-1 block">{f.l}</label>
                      <Input value={vendorForm[f.k]||''} onChange={e=>setVendorForm(p=>({...p,[f.k]:e.target.value}))} /></div>
                  ))}
                  <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1 block">Address</label>
                    <Input value={vendorForm.address||''} onChange={e=>setVendorForm(p=>({...p,address:e.target.value}))} /></div>
                  <div className="col-span-3"><label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                    <Input value={vendorForm.notes||''} onChange={e=>setVendorForm(p=>({...p,notes:e.target.value}))} /></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveVendor}>{vendorEditing ? 'Update' : 'Create'}</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowVendorForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            <div className="bg-card rounded-lg border overflow-hidden">
              <table className="w-full text-sm" data-testid="vendors-master-table">
                <thead className="bg-card border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Contact</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Category</th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Payment Terms</th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {refVendors.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-xs">No vendors yet.</td></tr>
                  ) : refVendors.map(v => (
                    <tr key={v.vendor_id} className="hover:bg-accent/50 group">
                      <td className="px-3 py-2.5 font-medium">{v.name}</td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">{v.contact_name || '-'}</td>
                      <td className="px-3 py-2.5 text-xs">{v.email || '-'}</td>
                      <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{v.category || '-'}</Badge></td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">{v.payment_terms || '-'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setVendorEditing(v); setVendorForm(v); setShowVendorForm(true); }}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteVendor(v.vendor_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="ref-source-of-funds">
          <SourceOfFundsTab
            items={refSourceOfFunds} activeWorkspace={activeWorkspace} API={API}
            onSave={async (data, editId) => { await refCrud('source-of-funds', editId ? 'PUT' : 'POST', editId, data); setRefForm({}); setRefEditing(null); setShowRefForm(false); }}
            onDelete={async (id) => { await refCrud('source-of-funds', 'DELETE', id); }}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>
        <TabsContent value="ref-expense-categories">
          <ExpenseCategoriesTab refCategories={refCategories} onSave={saveRefItem} onDelete={deleteRefItem}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} activeWorkspace={activeWorkspace} fetchRefData={fetchRefData} />
        </TabsContent>
        <TabsContent value="ref-income-types">
          <IncomeCategoriesTab refIncomeCategories={refIncomeCategories}
            onSave={async (data, editId) => { await refCrud('income-categories', editId ? 'PUT' : 'POST', editId, data); setRefForm({}); setRefEditing(null); setShowRefForm(false); }}
            onDelete={async (id) => { await refCrud('income-categories', 'DELETE', id); }}
            activeWorkspace={activeWorkspace} fetchRefData={fetchRefData}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>
        <TabsContent value="ref-business-units">
          <RefDataTab type="businessUnits" activeWorkspace={activeWorkspace} API={API}
            refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
            refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
            onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>
        <TabsContent value="ref-departments">
          <RefDataTab type="departments" activeWorkspace={activeWorkspace} API={API}
            refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
            refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
            onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>
        <TabsContent value="ref-projects">
          <RefDataTab type="projects" activeWorkspace={activeWorkspace} API={API}
            refAccounts={refAccounts} refCategories={refCategories} refBusinessUnits={refBusinessUnits}
            refProjects={refProjects} orgDepartments={orgDepartments} allUsers={orgMembers}
            onSave={saveRefItem} onDelete={deleteRefItem} fetchRefData={fetchRefData}
            form={refForm} setForm={setRefForm} editing={refEditing} setEditing={setRefEditing}
            showForm={showRefForm} setShowForm={setShowRefForm} />
        </TabsContent>

        {/* ==================== VENDORS/SUPPLIERS TAB ==================== */}
      </Tabs>

      {/* ==================== MODALS ==================== */}
      {/* ==================== IMPORT MAPPING DIALOG ==================== */}
      <Dialog open={importDialog} onOpenChange={(open) => { if (!open) { setImportDialog(false); setImportStep(1); setImportRawData([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import {importDialogType === 'expenses' ? 'Expenses' : 'Income'}</DialogTitle>
          </DialogHeader>

          {importStep === 1 && !importRawData.length && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-muted rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Drop or click to upload CSV/JSON</p>
                <p className="text-xs text-muted-foreground mt-1">Supports .csv and .json files</p>
              </div>
              <Button variant="outline" className="w-full" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />Download CSV Template
              </Button>
            </div>
          )}

          {importStep === 1 && importRawData.length > 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{importRawData.length} records found. Map your CSV columns to fields:</p>
              <div className="space-y-1.5 max-h-[45vh] overflow-y-auto">
                {importHeaders.map(h => {
                  const fields = importDialogType === 'expenses' ? EXPENSE_FIELDS : INCOME_FIELDS;
                  const sampleValues = importRawData.slice(0, 3).map(r => r[h]).filter(v => v !== undefined && v !== '');
                  const mappedField = fields.find(f => f.key === importMapping[h]);
                  return (
                    <div key={h} className={cn("flex items-center gap-3 p-2.5 rounded border", mappedField ? "bg-primary/5 border-primary/20" : "bg-muted/20 border-transparent")} data-testid={`import-map-${h}`}>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium block truncate">{h}</span>
                        {sampleValues.length > 0 && (
                          <span className="text-[10px] text-muted-foreground block truncate">e.g. {sampleValues.slice(0, 2).join(' | ')}</span>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      <Select value={importMapping[h] || '_skip'} onValueChange={v => setImportMapping(prev => ({ ...prev, [h]: v === '_skip' ? undefined : v }))}>
                        <SelectTrigger className={cn("w-56 h-8 text-xs", mappedField ? "border-primary/40" : "")}><SelectValue placeholder="Skip this column" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_skip">-- Skip this column --</SelectItem>
                          {fields.map(f => {
                            const alreadyMapped = Object.values(importMapping).includes(f.key) && importMapping[h] !== f.key;
                            return (
                              <SelectItem key={f.key} value={f.key} disabled={alreadyMapped}>
                                {f.label}{f.required ? ' *' : ''}{alreadyMapped ? ' (mapped)' : ''}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-xs text-muted-foreground">{Object.values(importMapping).filter(Boolean).length} of {importHeaders.length} columns mapped</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setImportStep(2)} data-testid="import-preview-btn">Preview</Button>
                  <Button onClick={executeImport} disabled={importing} data-testid="import-execute-btn">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                    Import {importRawData.length} Records
                  </Button>
                </div>
              </div>
            </div>
          )}

          {importStep === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setImportStep(1)}><ArrowLeft className="w-4 h-4 mr-1" />Back to Mapping</Button>
                <span className="text-xs text-muted-foreground">Preview (first 5 records)</span>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                {(() => {
                  const fields = importDialogType === 'expenses' ? EXPENSE_FIELDS : INCOME_FIELDS;
                  const mappedEntries = Object.entries(importMapping).filter(([, v]) => v);
                  return (
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          {mappedEntries.map(([, fieldKey]) => {
                            const fieldDef = fields.find(f => f.key === fieldKey);
                            return <th key={fieldKey} className="p-2 text-left font-medium whitespace-nowrap">{fieldDef?.label || fieldKey}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {importRawData.slice(0, 5).map((row, i) => (
                          <tr key={i} className="border-t border-border">
                            {mappedEntries.map(([csvCol, fieldKey]) => (
                              <td key={fieldKey} className="p-2 truncate max-w-[180px]">{row[csvCol] || ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              <Button onClick={executeImport} disabled={importing} className="w-full" data-testid="import-confirm-btn">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Importing...</> : `Import ${importRawData.length} Records`}
              </Button>
            </div>
          )}

          {importStep === 3 && (
            <div className="text-center py-8">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">Import Complete</p>
              <Button className="mt-4" onClick={() => { setImportDialog(false); setImportStep(1); setImportRawData([]); }}>Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== BULK EDIT MODAL ==================== */}
      <Dialog open={bulkEditModal} onOpenChange={setBulkEditModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Edit {bulkEditType === 'expenses' ? selectedExpenses.size : selectedIncome.size} {bulkEditType}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Only non-empty fields will be applied to all selected items.</p>
          <div className="grid grid-cols-2 gap-3 mt-2 max-h-[55vh] overflow-y-auto pr-1">
            {(bulkEditType === 'expenses' ? EXPENSE_COLUMNS : INCOME_COLUMNS).filter(c => c.type !== 'link').map(col => {
              const bulkRefData = { accounts: refAccounts, categories: refCategories, businessUnits: refBusinessUnits, projects: refProjects, vendors: refVendors, departments: orgDepartments, sourceOfFunds: refSourceOfFunds };
              // Build income category lists for bulk edit
              const incTopLevel = (refIncomeCategories || []).filter(c => !c.parent_code);
              const incTopNames = new Set(incTopLevel.map(c => c.name.toLowerCase()));
              const incClassifications = (refIncomeCategories || []).filter(c => c.parent_code && incTopNames.has(c.parent_code.toLowerCase()));
              const incClassNames = new Set(incClassifications.map(c => c.name.toLowerCase()));
              const incMarkets = (refIncomeCategories || []).filter(c => c.parent_code && incClassNames.has(c.parent_code.toLowerCase()));
              return (
              <div key={col.key}>
                <label className="text-xs text-muted-foreground block mb-1">{col.label}</label>
                {col.type === 'ref_select' && col.refKey === '_expense_types' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {[...new Set([...EXPENSE_TYPES, ...(refCategories || []).filter(c => !c.parent_code).map(c => c.name)])].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'ref_select' && col.refKey === '_income_types' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {incTopLevel.map(c => <SelectItem key={c.category_id || c.name} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'ref_select' && col.refKey === '_income_classifications' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {(bulkEditForm.income_source_type
                        ? incClassifications.filter(c => c.parent_code === bulkEditForm.income_source_type)
                        : incClassifications
                      ).map(c => <SelectItem key={c.category_id || c.name} value={c.name}>{c.code ? `${c.code} — ` : ''}{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'ref_select' && col.refKey === '_income_markets' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {(bulkEditForm.income_classification
                        ? incMarkets.filter(c => c.parent_code === bulkEditForm.income_classification)
                        : incMarkets
                      ).map(c => <SelectItem key={c.category_id || c.name} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'ref_select' && col.refKey ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {(bulkRefData[col.refKey] || []).map(r => <SelectItem key={r.name || r.id} value={r.name}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'person_combo' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {allStaffAndContractors.filter(m => m._active !== false).map(m => <SelectItem key={m.user_id || m.email} value={m.name || m.email}>{m.name || m.email}</SelectItem>)}
                      {allStaffAndContractors.some(m => m._active === false) && (
                        <div className="px-2 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Offboarded</div>
                      )}
                      {allStaffAndContractors.filter(m => m._active === false).map(m => <SelectItem key={m.user_id || m.email} value={m.name || m.email}><span className="opacity-60">{m.name || m.email}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'date' ? (
                  <Input type="date" value={bulkEditForm[col.key] || ''} onChange={e => setBulkEditForm(p => ({...p, [col.key]: e.target.value}))} className="h-8 text-sm" />
                ) : col.type === 'money' ? (
                  <Input type="number" step="0.01" value={bulkEditForm[col.key] || ''} onChange={e => setBulkEditForm(p => ({...p, [col.key]: e.target.value}))} className="h-8 text-sm" placeholder="No change" />
                ) : col.type === 'status_select' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent>
                      {INVOICE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : col.type === 'checkbox' ? (
                  <Select value={bulkEditForm[col.key] === true ? 'yes' : bulkEditForm[col.key] === false ? 'no' : ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v === 'yes'}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                  </Select>
                ) : col.type === 'yesno' ? (
                  <Select value={bulkEditForm[col.key] || ''} onValueChange={v => setBulkEditForm(p => ({...p, [col.key]: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                    <SelectContent><SelectItem value="Y">Y</SelectItem><SelectItem value="N">N</SelectItem></SelectContent>
                  </Select>
                ) : (
                  <Input value={bulkEditForm[col.key] || ''} onChange={e => setBulkEditForm(p => ({...p, [col.key]: e.target.value}))} className="h-8 text-sm" placeholder="No change" />
                )}
              </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditModal(false)}>Cancel</Button>
            <Button onClick={bulkUpdate} data-testid="bulk-edit-apply-btn">Apply to Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing modals below */}
      {/* Expense Modal — Rebuilt with searchable combobox, file upload, organized sections */}
      <Dialog open={expenseModal} onOpenChange={setExpenseModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>Record a business expense transaction</DialogDescription>
          </DialogHeader>
          <ExpenseModalContent
            expenseForm={expenseForm}
            setExpenseForm={setExpenseForm}
            editItem={editItem}
            refAccounts={refAccounts}
            refBusinessUnits={refBusinessUnits}
            refVendors={refVendors}
            refProjects={refProjects}
            refCategories={refCategories}
            orgDepartments={orgDepartments}
            allStaffAndContractors={allStaffAndContractors}
            activeWorkspace={activeWorkspace}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseModal(false)}>Cancel</Button>
            <Button onClick={saveExpense} data-testid="save-expense-btn">{editItem ? 'Update' : 'Add'} Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Income Modal — Rebuilt with searchable combobox, file upload, organized sections */}
      <Dialog open={incomeModal} onOpenChange={setIncomeModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Income' : 'Add Income'}</DialogTitle>
            <DialogDescription>Record an income or revenue transaction</DialogDescription>
          </DialogHeader>
          <IncomeModalContent
            incomeForm={incomeForm}
            setIncomeForm={setIncomeForm}
            editItem={editItem}
            refAccounts={refAccounts}
            refBusinessUnits={refBusinessUnits}
            refSourceOfFunds={refSourceOfFunds}
            refIncomeCategories={refIncomeCategories}
            allStaffAndContractors={allStaffAndContractors}
            activeWorkspace={activeWorkspace}
            fetchRefData={fetchRefData}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomeModal(false)}>Cancel</Button>
            <Button onClick={saveIncome} data-testid="save-income-btn">{editItem ? 'Update' : 'Add'} Income</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forecast Modal */}
      <Dialog open={forecastModal} onOpenChange={setForecastModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Forecast Model</DialogTitle><DialogDescription>Create a forecast projection</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Name</label><Input value={forecastForm.name} onChange={e => setForecastForm(p => ({...p, name: e.target.value}))} placeholder="e.g. 2026 Annual Forecast" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground mb-1 block">Start Month</label><Input type="month" value={forecastForm.start_month} onChange={e => setForecastForm(p => ({...p, start_month: e.target.value}))} /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">End Month</label><Input type="month" value={forecastForm.end_month} onChange={e => setForecastForm(p => ({...p, end_month: e.target.value}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForecastModal(false)}>Cancel</Button>
            <Button onClick={saveForecast}>Create Forecast</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scenario Modal */}
      <Dialog open={scenarioModal} onOpenChange={setScenarioModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Scenario</DialogTitle><DialogDescription>Model a financial scenario</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Name</label><Input value={scenarioForm.name} onChange={e => setScenarioForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Conservative Case" /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <Select value={scenarioForm.scenario_type} onValueChange={v => setScenarioForm(p => ({...p, scenario_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base Case</SelectItem>
                  <SelectItem value="conservative">Conservative</SelectItem>
                  <SelectItem value="aggressive">Aggressive Growth</SelectItem>
                  <SelectItem value="raise_now">Raise Now</SelectItem>
                  <SelectItem value="raise_later">Raise Later</SelectItem>
                  <SelectItem value="no_raise">No Raise</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Description</label><Textarea value={scenarioForm.description} onChange={e => setScenarioForm(p => ({...p, description: e.target.value}))} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScenarioModal(false)}>Cancel</Button>
            <Button onClick={saveScenario}>Create Scenario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace Modal */}
      <Dialog open={wsModal} onOpenChange={setWsModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Finance Workspace</DialogTitle><DialogDescription>Create a separate ledger for a business entity or personal finance</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Name</label><Input value={wsForm.name} onChange={e => setWsForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Entity A" /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <Select value={wsForm.workspace_type} onValueChange={v => setWsForm(p => ({...p, workspace_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business Entity</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="consolidated">Consolidated View</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Default Currency</label>
              <Select value={wsForm.currency} onValueChange={v => setWsForm(p => ({...p, currency: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsModal(false)}>Cancel</Button>
            <Button onClick={createWorkspace}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Workspace Dialog */}
      <Dialog open={wsRenameModal} onOpenChange={setWsRenameModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rename Workspace</DialogTitle><DialogDescription>Change the name of "{activeWorkspace?.name}"</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={wsRenameValue} onChange={e => setWsRenameValue(e.target.value)} placeholder="Workspace name" data-testid="rename-workspace-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsRenameModal(false)}>Cancel</Button>
            <Button onClick={handleRenameWorkspace} disabled={!wsRenameValue.trim()} data-testid="confirm-rename-btn">Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Confirmation */}
      <Dialog open={wsDeleteConfirm} onOpenChange={setWsDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Workspace</DialogTitle><DialogDescription>Are you sure you want to delete "{activeWorkspace?.name}"? This action cannot be undone. All data in this workspace will be permanently removed.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteWorkspace} data-testid="confirm-delete-workspace-btn">Delete Workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Change Log Dialog */}
      <Dialog open={fullChangeLog.open} onOpenChange={open => { if (!open) setFullChangeLog({ open: false, logs: [], loading: false }); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4" /> Full Change Log</DialogTitle>
            <DialogDescription>All recorded changes across this ledger</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {fullChangeLog.loading ? (
              <p className="text-sm text-muted-foreground text-center py-8">Loading change log...</p>
            ) : fullChangeLog.logs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No changes recorded yet.</p>
            ) : fullChangeLog.logs.map((log, i) => (
              <div key={i} className="text-xs border rounded-md p-3 bg-muted/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-semibold text-foreground">{log.user_name || log.user_id || 'System'}</span>
                  <Badge variant="outline" className="text-[9px] px-1.5">{log.action}</Badge>
                  <span className="text-muted-foreground ml-auto text-[10px]">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
                {log.entity_id && <p className="text-[10px] text-muted-foreground mb-1">Record: {log.entity_id}</p>}
                {Object.entries(log.changes || {}).map(([field, vals]) => {
                  const label = FIELD_LABEL_MAP[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                  <div key={field} className="ml-2 flex items-center gap-1.5 text-[11px] py-0.5">
                    <span className="text-muted-foreground font-medium min-w-[100px]">{label}</span>
                    <span className="px-1 py-0.5 rounded bg-red-50 dark:bg-red-950/30 text-red-500 line-through max-w-[200px] truncate">{String(vals.old ?? '—')}</span>
                    <ArrowRight className="w-2.5 h-2.5 text-muted-foreground shrink-0" />
                    <span className="px-1 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 font-medium max-w-[200px] truncate">{String(vals.new ?? '—')}</span>
                  </div>
                  );
                })}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </PageLayout>
  );
};

// ==================== SUB-COMPONENTS ====================

const TileTooltip = ({ text, children }) => (
  <div className="relative group inline-flex items-center">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-[11px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-52 text-center leading-tight shadow-lg">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
    </div>
  </div>
);

const DATE_RANGES = [
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
  { key: 'all', label: 'All' },
];

const getDateRange = (key) => {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (key === 'all') return { from: null, to: null };
  const months = key === '1m' ? 1 : key === '3m' ? 3 : key === '6m' ? 6 : 12;
  const from = new Date(now.getFullYear(), now.getMonth() - months, 1);
  return { from: from.toISOString().slice(0, 10), to };
};

const DashboardView = ({ data, workspaceId }) => {
  const br = data.burn_runway || {};
  const [dateRange, setDateRange] = useState('all');
  const [filteredData, setFilteredData] = useState(data);
  const [txLimit, setTxLimit] = useState(8);

  const API = '/api';

  // Fetch filtered data when date range changes
  useEffect(() => {
    if (dateRange === 'all' || !workspaceId) {
      setFilteredData(data);
      return;
    }
    const { from, to } = getDateRange(dateRange);
    const fetchFiltered = async () => {
      try {
        const params = new URLSearchParams();
        if (from) params.append('date_from', from);
        if (to) params.append('date_to', to);
        const res = await fetch(`${API}/finance/dashboard-enhanced/${workspaceId}?${params}`, { credentials: 'include' });
        if (res.ok) {
          const newData = await res.json();
          setFilteredData(newData);
        }
      } catch (e) { console.error(e); }
    };
    fetchFiltered();
  }, [dateRange, workspaceId, data, API]);

  // Chart data from trend
  const trend = data.cash_on_hand_trend || data.net_position_trend || {};
  const trendMonths = Object.keys(trend).sort();
  
  const { from: rangeFrom } = getDateRange(dateRange);
  const filteredMonths = rangeFrom 
    ? trendMonths.filter(m => m >= rangeFrom.slice(0, 7))
    : trendMonths;

  const burnChartData = filteredMonths.map(m => ({
    month: m.slice(2),
    grossBurn: Math.round(trend[m]?.gross_burn || br.monthly_expenses?.[m] || 0),
    netBurn: Math.round(trend[m]?.net_burn || (br.monthly_expenses?.[m] || 0) - (br.monthly_income?.[m] || 0)),
  }));

  const cashChartData = filteredMonths.map(m => ({
    month: m.slice(2),
    cashOnHand: Math.round(trend[m]?.cumulative || 0),
  }));

  // Merge recent transactions with item names
  const allTx = [
    ...(data.recent_expenses || []).map(e => ({
      _type: 'expense', _date: e.date, _amount: -(e.amount_usd || 0),
      _label: e.expense_item || e.description || 'Expense',
      _sublabel: e.description && e.expense_item ? e.description : '',
    })),
    ...(data.recent_income || []).map(i => ({
      _type: 'income', _date: i.date_received, _amount: i.amount_usd || 0,
      _label: i.income_name || i.description || 'Income',
      _sublabel: i.description && i.income_name ? i.description : '',
    }))
  ].sort((a, b) => (b._date || '').localeCompare(a._date || ''));

  // Use filtered data for breakdowns
  const fd = filteredData;

  return (
    <div className="space-y-5">
      {/* Top Row: Total Income, Total Expenses, Cash On Hand, Pending Income */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Total Income</span>
              <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center"><TrendingUp className="w-3.5 h-3.5 text-emerald-500" /></div>
            </div>
            <p className="text-xl font-bold text-emerald-500" data-testid="total-income">{formatMoney(data.total_income)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Total Expenses</span>
              <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center"><TrendingDown className="w-3.5 h-3.5 text-red-500" /></div>
            </div>
            <p className="text-xl font-bold text-red-500" data-testid="total-expenses">{formatMoney(data.total_expenses)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TileTooltip text="All income minus all expenses over all time">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">Cash On Hand <Info className="w-3 h-3" /></span>
              </TileTooltip>
              <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center"><Wallet className="w-3.5 h-3.5 text-blue-500" /></div>
            </div>
            <p className={cn("text-xl font-bold", (br.cash_on_hand || 0) >= 0 ? "text-blue-500" : "text-red-500")} data-testid="cash-on-hand">{formatMoney(br.cash_on_hand || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Pending Income</span>
              <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center"><Clock className="w-3.5 h-3.5 text-amber-500" /></div>
            </div>
            <p className="text-xl font-bold text-amber-500" data-testid="pending-income">{formatMoney(data.pending_income || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bottom KPI Row: Gross Burn, Net Burn, Runway */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TileTooltip text="Total expenses over the last 3 months divided by 3. Shows your average monthly spending rate.">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">Gross Burn Avg (3mo) <Info className="w-3 h-3" /></span>
              </TileTooltip>
              <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center"><Activity className="w-3.5 h-3.5 text-red-500" /></div>
            </div>
            <p className="text-xl font-bold text-red-400" data-testid="gross-burn">{formatMoney(br.gross_burn_3m || br.avg_burn_3m || 0)}/mo</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TileTooltip text="Cash flow change: (last 3 months expenses minus last 3 months income) divided by 3. Negative = you're spending more than earning.">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">Net Burn Avg (3mo) <Info className="w-3 h-3" /></span>
              </TileTooltip>
              <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center"><ArrowRightLeft className="w-3.5 h-3.5 text-amber-500" /></div>
            </div>
            <p className={cn("text-xl font-bold", (br.net_burn_3m || 0) > 0 ? "text-red-400" : "text-emerald-500")} data-testid="net-burn">{formatMoney(br.net_burn_3m || 0)}/mo</p>
          </CardContent>
        </Card>
        <Card className={cn("border-0 shadow-sm bg-card", br.warning && "ring-2 ring-red-400/50")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TileTooltip text="Cash on hand divided by gross burn rate. How many months of runway you have at current spending.">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">Runway <Info className="w-3 h-3" /></span>
              </TileTooltip>
              <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", br.warning ? "bg-red-500/15" : "bg-emerald-500/15")}>
                {br.warning ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> : <Target className="w-3.5 h-3.5 text-emerald-500" />}
              </div>
            </div>
            <p className={cn("text-xl font-bold", br.warning ? "text-red-500" : "text-emerald-500")} data-testid="runway-months">
              {br.runway_months != null ? `${br.runway_months} mo` : (br.gross_burn_3m > 0 ? 'N/A' : 'Cash generative')}
            </p>
            {br.warning && <p className="text-[10px] text-red-400 mt-1">Below 6 month threshold</p>}
            {br.runway_months == null && !br.warning && (br.gross_burn_3m || 0) <= 0 && <p className="text-[10px] text-emerald-500 mt-1">Not burning cash</p>}
          </CardContent>
        </Card>
      </div>

      {/* Date Range Filter */}
      <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 w-fit" data-testid="date-range-filter">
        {DATE_RANGES.map(r => (
          <button
            key={r.key}
            onClick={() => setDateRange(r.key)}
            className={cn(
              "px-3 py-1 rounded-md text-xs font-medium transition-colors",
              dateRange === r.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
            )}
            data-testid={`date-range-${r.key}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Burn Trend - Gross on left axis, Net on right axis */}
        <Card className="border-0 shadow-sm bg-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Burn Trend</CardTitle></CardHeader>
          <CardContent>
            {burnChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={burnChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <RechartTooltip
                    formatter={(value, name) => [formatMoney(value), name === 'grossBurn' ? 'Gross Burn' : 'Net Burn']}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend formatter={(value) => value === 'grossBurn' ? 'Gross Burn' : 'Net Burn'} wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="grossBurn" fill="#f87171" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="right" dataKey="netBurn" fill="#fb923c" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-muted-foreground text-center py-8">No burn data yet</p>}
          </CardContent>
        </Card>

        {/* Cash On Hand Trend */}
        <Card className="border-0 shadow-sm bg-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Cash On Hand Trend</CardTitle></CardHeader>
          <CardContent>
            {cashChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={cashChartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <RechartTooltip
                    formatter={(value) => [formatMoney(value), 'Cash On Hand']}
                    labelFormatter={(label) => `Month: ${label}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Line type="monotone" dataKey="cashOnHand" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-muted-foreground text-center py-8">No data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: Expense Breakdown, Income by Type, Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Expense Breakdown (date-range responsive) */}
        <Card className="border-0 shadow-sm bg-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Expense Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(fd.expense_by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                const config = CATEGORY_COLORS[cat];
                const total = Object.values(fd.expense_by_category || {}).reduce((s, v) => s + v, 0) || 1;
                const pct = (amt / total * 100);
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <div className={cn("w-2 h-2 rounded-full", config?.color || 'bg-slate-400')} />
                    <span className="text-xs flex-1 truncate">{config?.name || cat}</span>
                    <span className="text-xs font-mono font-medium">{formatMoney(amt)}</span>
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={cn("h-full rounded-full", config?.color || 'bg-slate-400')} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {!Object.keys(fd.expense_by_category || {}).length && <p className="text-xs text-muted-foreground text-center py-4">No expenses yet</p>}
            </div>
          </CardContent>
        </Card>

        {/* Income by Type (date-range responsive) */}
        <Card className="border-0 shadow-sm bg-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Income by Type</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(fd.income_by_type || {}).sort((a, b) => b[1] - a[1]).map(([type, amt]) => {
                const totalInc = Object.values(fd.income_by_type || {}).reduce((s, v) => s + v, 0) || 1;
                const pct = (amt / totalInc * 100);
                return (
                  <div key={type} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-xs flex-1 truncate">{INCOME_TYPES[type] || type}</span>
                    <span className="text-xs font-mono font-medium">{formatMoney(amt)}</span>
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {!Object.keys(fd.income_by_type || {}).length && <p className="text-xs text-muted-foreground text-center py-4">No income yet</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions - scrollable with load more */}
        <Card className="border-0 shadow-sm bg-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Recent Transactions</CardTitle>
              {allTx.length > 0 && <span className="text-[10px] text-muted-foreground">{allTx.length} total</span>}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[320px] overflow-y-auto px-4 scroll-smooth">
              <div className="space-y-0.5">
                {allTx.slice(0, txLimit).map((t, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 border-b border-border last:border-0 transition-colors hover:bg-muted/30" data-testid={`transaction-${i}`}>
                    <div className={cn("w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0", t._type === 'expense' ? "bg-red-500/15" : "bg-emerald-500/15")}>
                      {t._type === 'expense' ? <ArrowDownRight className="w-3 h-3 text-red-500" /> : <ArrowUpRight className="w-3 h-3 text-emerald-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{t._label}</p>
                      {t._sublabel && <p className="text-[10px] text-muted-foreground truncate">{t._sublabel}</p>}
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{t._date}</span>
                    <span className={cn("text-xs font-mono font-medium whitespace-nowrap", t._amount < 0 ? "text-red-500" : "text-emerald-500")}>
                      {formatMoney(t._amount)}
                    </span>
                  </div>
                ))}
                {!allTx.length && <p className="text-xs text-muted-foreground text-center py-4">No transactions yet</p>}
              </div>
            </div>
            {allTx.length > txLimit && (
              <div className="px-4 py-2 border-t flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setTxLimit(l => l + 10)}
                  data-testid="load-more-transactions"
                >
                  Load More ({allTx.length - txLimit} remaining)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => setTxLimit(allTx.length)}
                  data-testid="show-all-transactions"
                >
                  Show All
                </Button>
              </div>
            )}
            {txLimit > 8 && allTx.length > 8 && txLimit >= allTx.length && (
              <div className="px-4 py-1.5 border-t">
                <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground" onClick={() => setTxLimit(8)} data-testid="collapse-transactions">
                  Collapse
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== SHARED TABLE HELPERS ====================
// ==================== Reference Data Sub-Tab Component ====================

function ExpenseCategoriesTab({ refCategories, onSave, onDelete, form, setForm, editing, setEditing, showForm, setShowForm, activeWorkspace, fetchRefData }) {
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [formMode, setFormMode] = useState('type'); // 'type' or 'sub'
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineForm, setInlineForm] = useState({});

  // Build hierarchy: parent types (no parent_code) and their children, sorted by code
  const types = useMemo(() => {
    const parents = (refCategories || []).filter(c => !c.parent_code)
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' }));
    const childMap = {};
    for (const c of (refCategories || [])) {
      if (c.parent_code) {
        if (!childMap[c.parent_code]) childMap[c.parent_code] = [];
        childMap[c.parent_code].push(c);
      }
    }
    for (const key in childMap) {
      childMap[key].sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' }));
    }
    return parents.map(p => ({ ...p, children: childMap[p.name] || [] }));
  }, [refCategories]);

  const allTypeNames = useMemo(() => new Set((refCategories || []).filter(c => !c.parent_code).map(c => c.name)), [refCategories]);
  const missingDefaults = EXPENSE_TYPES.filter(t => !allTypeNames.has(t));

  const toggleExpand = (name) => {
    setExpandedTypes(prev => { const next = new Set(prev); if (next.has(name)) next.delete(name); else next.add(name); return next; });
  };

  const startInlineEdit = (item) => {
    setInlineEditId(item.category_id);
    setInlineForm({ name: item.name || '', code: item.code || '', description: item.description || '', parent_code: item.parent_code || '' });
  };
  const cancelInlineEdit = () => { setInlineEditId(null); setInlineForm({}); };
  const saveInlineEdit = (categoryId) => {
    onSave('expense-categories', { ...inlineForm }, categoryId);
    setInlineEditId(null); setInlineForm({});
  };

  const openTypeForm = () => {
    setEditing(null); setForm({ name: '', code: '', parent_code: '', description: '' });
    setFormMode('type'); setShowForm(true);
  };

  const openSubForm = (parentName) => {
    setEditing(null); setForm({ name: '', code: '', parent_code: parentName, description: '' });
    setFormMode('sub'); setShowForm(true);
    setExpandedTypes(prev => new Set([...prev, parentName]));
  };

  // Template download
  const downloadTemplate = () => {
    const header = 'Type,Type Code,Sub Type,Sub Type Code';
    const exampleRows = [
      'Payroll & Benefits,A,CEO Salary,A.01',
      'Payroll & Benefits,A,Engineering Salaries,A.02',
      'SaaS & Tools,B,CRM,B.01',
      'SaaS & Tools,B,Analytics,B.02',
      'Professional Services,C,Legal,C.01',
    ];
    const csv = [header, ...exampleRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'expense_categories_template.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Import CSV
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      const dataLines = lines.slice(1);
      const rows = dataLines.map(line => {
        const parts = line.split(',').map(s => s.trim());
        return { type: parts[0] || '', type_code: parts[1] || '', sub_type: parts[2] || '', sub_type_code: parts[3] || '' };
      }).filter(r => r.type);
      if (rows.length === 0) { toast.error('No valid rows found'); setImporting(false); return; }
      const wsId = activeWorkspace?.workspace_id || '';
      const r = await fetch(`/api/finance/ref/${wsId}/expense-categories/bulk-import`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      if (r.ok) { const d = await r.json(); toast.success(d.message); if (fetchRefData) fetchRefData(); }
      else toast.error('Import failed');
    } catch { toast.error('Error reading file'); }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const InlineCell = ({ value, field, editId, catId }) => {
    if (editId === catId) {
      return <Input value={inlineForm[field] || ''} onChange={e => setInlineForm(p => ({...p, [field]: e.target.value}))} className="h-7 text-xs" autoFocus={field === 'name'} data-testid={`inline-edit-${field}`} />;
    }
    return <span>{value || '-'}</span>;
  };

  return (
    <div className="space-y-4" data-testid="ref-tab-categories">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Expense Categories</h3>
          <p className="text-[10px] text-muted-foreground">Click on any row to edit inline. Ordered by code.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" />Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-categories-btn">
            <Upload className="w-3.5 h-3.5 mr-1" />{importing ? 'Importing...' : 'Import CSV'}
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
          <Button size="sm" onClick={openTypeForm} data-testid="add-categories-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Type
          </Button>
        </div>
      </div>

      {/* Add Form — only for creating new items */}
      {showForm && (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground">Create {formMode === 'type' ? 'Type' : 'Sub-Type'}</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
              <Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="ref-field-name" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Code</label>
              <Input value={form.code || ''} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. A.01" data-testid="ref-field-code" />
            </div>
            {formMode === 'sub' && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Parent Type</label>
                <Input value={form.parent_code || ''} readOnly className="bg-muted" data-testid="ref-field-parent_code" />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Input value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="ref-field-description" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave('expense-categories', { ...form, parent_code: formMode === 'type' ? '' : (form.parent_code || '') })} data-testid="save-categories-btn">Create</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Hierarchical list with inline editing */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="categories-table">
          <thead className="bg-card border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs w-8"></th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Name</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs w-24">Code</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Description</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs w-20">Sub-Types</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-28">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {types.length === 0 && missingDefaults.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground text-xs">No categories yet. Click &quot;Add Type&quot; or &quot;Import CSV&quot; to get started.</td></tr>
            )}
            {types.map(t => {
              const isExpanded = expandedTypes.has(t.name);
              const isEditing = inlineEditId === t.category_id;
              return (
                <React.Fragment key={t.category_id || t.name}>
                  <tr className={cn("hover:bg-accent/50 group", isEditing && "bg-blue-50/50 dark:bg-blue-950/20")} data-testid={`cat-type-${t.name}`}>
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleExpand(t.name)} className="text-muted-foreground hover:text-foreground" data-testid={`expand-${t.name}`}>
                        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !isExpanded && "-rotate-90")} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-xs font-medium"><InlineCell value={t.name} field="name" editId={inlineEditId} catId={t.category_id} /></td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono"><InlineCell value={t.code} field="code" editId={inlineEditId} catId={t.category_id} /></td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground"><InlineCell value={t.description} field="description" editId={inlineEditId} catId={t.category_id} /></td>
                    <td className="px-3 py-2.5 text-xs"><Badge variant="outline" className="text-[10px]">{t.children.length}</Badge></td>
                    <td className="px-3 py-2.5 text-right">
                      {isEditing ? (
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => saveInlineEdit(t.category_id)} data-testid={`save-inline-${t.category_id}`}><Check className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancelInlineEdit} data-testid={`cancel-inline-${t.category_id}`}><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Add Sub-Type" onClick={() => openSubForm(t.name)} data-testid={`add-sub-${t.name}`}><Plus className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit Inline" onClick={() => startInlineEdit(t)} data-testid={`edit-cat-${t.name}`}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Delete" onClick={() => onDelete('expense-categories', t.category_id)} data-testid={`delete-cat-${t.name}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && t.children.map(sub => {
                    const isSubEditing = inlineEditId === sub.category_id;
                    return (
                      <tr key={sub.category_id || sub.name} className={cn("bg-muted/20 hover:bg-muted/40 group", isSubEditing && "bg-blue-50/50 dark:bg-blue-950/20")} data-testid={`cat-sub-${sub.name}`}>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-xs pl-8">
                          {isSubEditing ? <Input value={inlineForm.name || ''} onChange={e => setInlineForm(p => ({...p, name: e.target.value}))} className="h-7 text-xs" autoFocus data-testid="inline-edit-name" />
                          : <><span className="text-muted-foreground mr-1.5">&#x2514;</span>{sub.name}</>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground font-mono"><InlineCell value={sub.code} field="code" editId={inlineEditId} catId={sub.category_id} /></td>
                        <td className="px-3 py-2 text-xs text-muted-foreground"><InlineCell value={sub.description} field="description" editId={inlineEditId} catId={sub.category_id} /></td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-right">
                          {isSubEditing ? (
                            <div className="flex gap-0.5 justify-end">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => saveInlineEdit(sub.category_id)} data-testid={`save-inline-${sub.category_id}`}><Check className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancelInlineEdit}><X className="w-3.5 h-3.5" /></Button>
                            </div>
                          ) : (
                            <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => startInlineEdit(sub)}><Pencil className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Delete" onClick={() => onDelete('expense-categories', sub.category_id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {isExpanded && t.children.length === 0 && (
                    <tr className="bg-muted/20">
                      <td colSpan={6} className="px-3 py-2 text-center">
                        <button className="text-xs text-blue-600 hover:underline" onClick={() => openSubForm(t.name)}>+ Add a sub-type</button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {missingDefaults.length > 0 && (
              <>
                <tr className="border-t-2">
                  <td colSpan={6} className="px-3 py-2 text-[10px] text-muted-foreground italic bg-muted/30">
                    Default types (not yet saved as categories — add sub-types to auto-create)
                  </td>
                </tr>
                {missingDefaults.map(name => (
                  <tr key={name} className="hover:bg-accent/50 group opacity-60" data-testid={`cat-default-${name}`}>
                    <td className="px-3 py-2.5"><ChevronDown className="w-3.5 h-3.5 text-muted-foreground/30 -rotate-90" /></td>
                    <td className="px-3 py-2.5 text-xs font-medium">{name}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">-</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground italic">Built-in default</td>
                    <td className="px-3 py-2.5 text-xs"><Badge variant="outline" className="text-[10px]">0</Badge></td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Add Sub-Type" onClick={() => openSubForm(name)} data-testid={`add-sub-default-${name}`}><Plus className="w-3 h-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function RefDataTab({ type, activeWorkspace, API, refAccounts, refCategories, refBusinessUnits, refProjects, orgDepartments, allUsers, onSave, onDelete, fetchRefData, form, setForm, editing, setEditing, showForm, setShowForm }) {
  // Special handling for categories — hierarchical view
  if (type === 'categories') {
    return <ExpenseCategoriesTab refCategories={refCategories} onSave={onSave} onDelete={onDelete} form={form} setForm={setForm} editing={editing} setEditing={setEditing} showForm={showForm} setShowForm={setShowForm} activeWorkspace={activeWorkspace} fetchRefData={fetchRefData} />;
  }
  const getConfig = () => {
    switch (type) {
      case 'accounts': return { title: 'Expense / Income Accounts', desc: 'Manage accounts for money in/out. Used in both expense "Account Paid From" and income "Paid To" columns.', apiType: 'expense-accounts',
        items: refAccounts, idKey: 'account_id', fields: [{ key: 'name', label: 'Account Name', required: true }, { key: 'account_number', label: 'Account Number' }, { key: 'bank_name', label: 'Bank Name' }, { key: 'description', label: 'Description' }] };
      case 'departments': return { title: 'Org Departments', desc: 'Synced with Organisation module. Changes here update Organisation departments.', apiType: '_org_departments',
        items: orgDepartments, idKey: 'department_id', fields: [{ key: 'name', label: 'Name', required: true }, { key: 'description', label: 'Description' }] };
      case 'businessUnits': return { title: 'Business Units', desc: 'Define business units for expense attribution.', apiType: 'business-units',
        items: refBusinessUnits, idKey: 'unit_id', fields: [{ key: 'name', label: 'Name', required: true }, { key: 'description', label: 'Description' }] };
      case 'projects': return { title: 'Projects', desc: 'Projects for expense attribution. Feeds the "Project Attribution" column.', apiType: 'projects',
        items: refProjects, idKey: 'project_id', fields: [{ key: 'name', label: 'Name', required: true }, { key: 'project_code', label: 'Code' }, { key: 'description', label: 'Description' }, { key: 'status', label: 'Status' }, { key: 'start_date', label: 'Start Date', type: 'date' }, { key: 'end_date', label: 'End Date', type: 'date' }, { key: 'budget', label: 'Budget', type: 'number' }] };
      default: return null;
    }
  };
  const cfg = getConfig();
  if (!cfg) return null;

  return (
    <div className="space-y-4" data-testid={`ref-tab-${type}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{cfg.title}</h3>
          <p className="text-[10px] text-muted-foreground">{cfg.desc}</p>
        </div>
        {!cfg.readOnly && (
          <Button size="sm" onClick={() => { setEditing(null); setForm({}); setShowForm(true); }} data-testid={`add-${type}-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add
          </Button>
        )}
      </div>

      {showForm && !cfg.readOnly && (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {cfg.fields.map(f => (
              <div key={f.key}>
                <label className="text-xs text-muted-foreground mb-1 block">{f.label}{f.required ? ' *' : ''}</label>
                <Input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  data-testid={`ref-field-${f.key}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave(cfg.apiType, form, editing?.[cfg.idKey])} data-testid={`save-${type}-btn`}>{editing ? 'Update' : 'Create'}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid={`${type}-table`}>
          <thead className="bg-card border-b">
            <tr>
              {cfg.fields.map(f => <th key={f.key} className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">{f.label}</th>)}
              {!cfg.readOnly && <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-24">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {cfg.items.length === 0 ? (
              <tr><td colSpan={cfg.fields.length + (cfg.readOnly ? 0 : 1)} className="px-3 py-8 text-center text-muted-foreground text-xs">No items yet.</td></tr>
            ) : cfg.items.map(item => (
              <tr key={item[cfg.idKey]} className="hover:bg-accent/50 group">
                {cfg.fields.map(f => <td key={f.key} className="px-3 py-2.5 text-xs">{item[f.key] || '-'}</td>)}
                {!cfg.readOnly && (
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(item); setForm(item); setShowForm(true); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(cfg.apiType, item[cfg.idKey])}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EXPENSE_COLUMNS = [
  { key: 'date', label: 'Date', type: 'date', w: '100px' },
  { key: 'account_paid_from', label: 'Account Paid From', fallback: 'paid_from_account', type: 'ref_select', refKey: 'accounts', w: '140px' },
  { key: 'business_unit', label: 'Business Unit', type: 'ref_select', refKey: 'businessUnits', w: '120px' },
  { key: 'expense_item', label: 'Expense Item', type: 'text', w: '140px' },
  { key: 'description', label: 'Description', type: 'text', w: '180px' },
  { key: 'vendor', label: 'Vendor/Supplier', type: 'ref_select', refKey: 'vendors', w: '130px' },
  { key: 'amount_usd', label: 'Amount (USD)', type: 'money', align: 'right', w: '110px' },
  { key: 'original_amount', label: 'Orig Amount', type: 'money', align: 'right', w: '100px' },
  { key: 'original_currency', label: 'Orig Currency', fallback: 'currency', type: 'text', w: '90px' },
  { key: 'org_function', label: 'Org Department', type: 'ref_select', refKey: 'departments', w: '120px' },
  { key: 'expense_type', label: 'Type', type: 'ref_select', refKey: '_expense_types', w: '130px' },
  { key: 'expense_sub_type', label: 'Sub Type', type: 'ref_select', refKey: 'categories', w: '100px' },
  { key: 'staff_contractor_name', label: 'Staff / Contractor', type: 'person_combo', w: '160px' },
  { key: 'expense_role', label: 'Role', type: 'text', w: '130px' },
  { key: 'project_attribution', label: 'Project Attribution', type: 'project_attribution', w: '200px' },
  { key: 'receipt_type', label: 'Receipt Type', type: 'text', w: '100px' },
  { key: 'receipt_link', label: 'Receipt Link', type: 'link', w: '90px' },
  { key: 'bank_statement_entry', label: 'Bank Entry', type: 'text', w: '100px' },
  { key: 'bank_statement_date', label: 'Bank Date', type: 'date', w: '100px' },
  { key: 'comments', label: 'Comments', fallback: 'notes', type: 'text', w: '150px' },
];

const INCOME_COLUMNS = [
  { key: 'date_received', label: 'Date Received', type: 'date', w: '110px' },
  { key: 'due_date', label: 'Due Date', fallback: 'invoice_due_date', type: 'date', w: '100px' },
  { key: 'invoice_issue_date', label: 'Invoice Issued', type: 'date', w: '110px' },
  { key: 'days_late', label: 'Days Late', type: 'daysLate', align: 'right', w: '80px' },
  { key: 'invoice_number', label: 'Invoice #', type: 'text', w: '100px' },
  { key: 'invoice_status', label: 'Status', fallback: 'payment_status', type: 'status_select', w: '100px' },
  { key: 'income_item_summary', label: 'Income Item', fallback: 'income_name', type: 'text', w: '180px' },
  { key: 'source_of_funds', label: 'Source of Funds', fallback: 'source_entity', type: 'ref_select', refKey: 'sourceOfFunds', w: '140px' },
  { key: 'amount_usd', label: 'Amount (USD)', type: 'money', align: 'right', w: '110px' },
  { key: 'is_loan', label: 'Loan?', type: 'checkbox', align: 'center', w: '60px' },
  { key: 'original_amount', label: 'Orig Amount', type: 'money', align: 'right', w: '100px' },
  { key: 'original_currency', label: 'Orig Currency', fallback: 'currency', type: 'text', w: '90px' },
  { key: 'business_unit', label: 'Business Unit', type: 'ref_select', refKey: 'businessUnits', w: '120px' },
  { key: 'income_source_type', label: 'Income Source Type', fallback: 'source_type', type: 'ref_select', refKey: '_income_types', w: '130px' },
  { key: 'sales_person', label: 'Sales Person', fallback: 'attributed_salesperson', type: 'person_combo', w: '130px' },
  { key: 'income_paid_to', label: 'Paid To', fallback: 'received_to_account', type: 'ref_select', refKey: 'accounts', w: '130px' },
  { key: 'payment_channel', label: 'Payment Channel', fallback: 'received_via', type: 'text', w: '110px' },
  { key: 'income_classification', label: 'Income Classification', type: 'ref_select', refKey: '_income_classifications', w: '140px' },
  { key: 'market', label: 'Market', type: 'ref_select', refKey: '_income_markets', w: '120px' },
  { key: 'income_note', label: 'Note', fallback: 'notes', type: 'text', w: '150px' },
  { key: 'receipt_issued', label: 'Receipt?', type: 'yesno', align: 'center', w: '70px' },
  { key: 'receipt_link', label: 'Receipt Link', fallback: 'evidence_link', type: 'link', w: '90px' },
];

// Tag color map for reference columns (grouped as user requested)
const TAG_COLORS = {
  account_paid_from: { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  business_unit:     { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  vendor:            { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  org_function:      { bg: 'bg-violet-50 dark:bg-violet-950/40', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-200 dark:border-violet-800' },
  expense_type:      { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' },
  expense_sub_type:  { bg: 'bg-orange-50 dark:bg-orange-950/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' },
  staff_contractor_name: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800' },
  project_attribution: { bg: 'bg-indigo-50 dark:bg-indigo-950/40', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-200 dark:border-indigo-800' },
  source_of_funds:   { bg: 'bg-teal-50 dark:bg-teal-950/40', text: 'text-teal-700 dark:text-teal-300', border: 'border-teal-200 dark:border-teal-800' },
  income_source_type: { bg: 'bg-amber-50 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' },
  income_classification: { bg: 'bg-purple-50 dark:bg-purple-950/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' },
  income_paid_to:    { bg: 'bg-sky-50 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300', border: 'border-sky-200 dark:border-sky-800' },
  sales_person:      { bg: 'bg-emerald-50 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800' },
  market:            { bg: 'bg-rose-50 dark:bg-rose-950/40', text: 'text-rose-700 dark:text-rose-300', border: 'border-rose-200 dark:border-rose-800' },
};

// Field key → human-readable label for audit log display
const FIELD_LABEL_MAP = {
  date: 'Date', amount_usd: 'Amount (USD)', original_amount: 'Orig Amount', original_currency: 'Currency',
  account_paid_from: 'Account Paid From', business_unit: 'Business Unit', expense_item: 'Expense Item',
  description: 'Description', vendor: 'Vendor/Supplier', org_function: 'Org Department',
  expense_type: 'Expense Type', expense_sub_type: 'Sub Type', staff_contractor_name: 'Staff/Contractor',
  expense_role: 'Role', project_attribution: 'Project', receipt_type: 'Receipt Type',
  receipt_link: 'Receipt Link', bank_statement_entry: 'Bank Entry', bank_statement_date: 'Bank Date',
  comments: 'Comments', date_received: 'Date Received', due_date: 'Due Date',
  invoice_issue_date: 'Invoice Issue Date', days_late: 'Days Late', invoice_number: 'Invoice #',
  invoice_status: 'Invoice Status', income_item_summary: 'Income Item', source_of_funds: 'Source of Funds',
  is_loan: 'Is Loan', income_source_type: 'Income Type', income_classification: 'Classification',
  market: 'Market', sales_person: 'Sales Person', income_paid_to: 'Paid To',
  payment_channel: 'Payment Channel', receipt_issued: 'Receipt Issued', income_note: 'Note',
  receipt_link: 'Receipt Link',
};

// Columns that should have tagged display
const TAGGED_COLUMNS = new Set([
  'account_paid_from', 'business_unit', 'vendor', 'org_function',
  'expense_type', 'expense_sub_type', 'staff_contractor_name', 'project_attribution',
  'source_of_funds', 'income_source_type', 'income_classification', 'income_paid_to', 'sales_person', 'market',
]);

const getCellValue = (item, col) => item[col.key] || (col.fallback ? item[col.fallback] : '') || '';

const renderCell = (item, col) => {
  const val = getCellValue(item, col);
  if (!val && val !== 0) return '';
  switch (col.type) {
    case 'money': return formatMoney(val);
    case 'link': return <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Link</a>;
    case 'yesno': return val === 'Y' ? <Badge variant="destructive" className="text-[10px]">Y</Badge> : '';
    case 'checkbox': return val === 'Y' ? <CheckSquare className="w-3.5 h-3.5 text-green-600 mx-auto" /> : <Square className="w-3.5 h-3.5 text-muted-foreground/30 mx-auto" />;
    case 'status_select': {
      const colors = { Pending: 'bg-yellow-100 text-yellow-800 border-yellow-300', Invoiced: 'bg-blue-100 text-blue-800 border-blue-300', Refunded: 'bg-red-100 text-red-800 border-red-300', Paid: 'bg-green-100 text-green-800 border-green-300' };
      return <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border", colors[val] || 'bg-muted text-muted-foreground')}>{String(val)}</span>;
    }
    case 'daysLate': {
      const n = Number(val);
      return <span className={n > 0 ? "text-red-500 font-medium" : ""}>{isNaN(n) ? val : n}</span>;
    }
    default: return String(val);
  }
};

const FinanceLedgerTable = ({ columns, data, idField, onEdit, onDelete, onInlineUpdate, selected = new Set(), onSelectionChange, emptyIcon: EmptyIcon, emptyLabel, workspaceId, orgMembers = [], onViewFullLog, refData = {}, onCreateRef, onCreateStaff, onClassificationChange }) => {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [colFilter, setColFilter] = useState('');
  const [filterCol, setFilterCol] = useState('_all');
  const [filterOp, setFilterOp] = useState('contains');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [personDropOpen, setPersonDropOpen] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [projAttrModal, setProjAttrModal] = useState(null); // { expenseId, expenseName, current: [{project_id, project_code, project_name, percentage}] }
  const [projAttrForm, setProjAttrForm] = useState([]); // [{project_id, project_code, project_name, percentage}]
  const [projNewName, setProjNewName] = useState('');
  const [projSaving, setProjSaving] = useState(false);
  const [colWidths, setColWidths] = useState(() => {
    const w = {}; columns.forEach(c => { w[c.key] = parseInt(c.w); }); return w;
  });
  const resizingRef = useRef(null);
  const tableRef = useRef(null);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);

  const API = '/api';
  const dateKey = columns[0]?.key;

  // Enhanced filtering
  const filtered = data.filter(item => {
    if (dateFrom || dateTo) {
      const d = item[dateKey] || '';
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
    }
    const isEmptyOp = filterOp === 'empty' || filterOp === 'notEmpty';
    if (colFilter || isEmptyOp) {
      const q = (colFilter || '').toLowerCase();
      const matchCol = (col) => {
        const v = String(getCellValue(item, col)).toLowerCase();
        switch (filterOp) {
          case 'exact': return v === q;
          case 'starts': return v.startsWith(q);
          case 'ends': return v.endsWith(q);
          case 'gt': return (Number(v) || 0) > (Number(q) || 0);
          case 'lt': return (Number(v) || 0) < (Number(q) || 0);
          case 'empty': return !v || v === '0';
          case 'notEmpty': return v != null && v !== '' && String(v).trim() !== '';
          default: return v.includes(q);
        }
      };
      if (filterCol === '_all') {
        if (isEmptyOp) return columns.some(matchCol);
        return columns.some(matchCol);
      }
      const col = columns.find(c => c.key === filterCol);
      return col ? matchCol(col) : true;
    }
    return true;
  });

  // Sort
  const sorted = [...filtered];
  if (sortCol) {
    sorted.sort((a, b) => {
      const col = columns.find(c => c.key === sortCol);
      let va = getCellValue(a, col || { key: sortCol });
      let vb = getCellValue(b, col || { key: sortCol });
      if (col?.type === 'money' || col?.type === 'daysLate') { va = Number(va) || 0; vb = Number(vb) || 0; }
      else if (col?.type === 'date') {
        const parseDate = (v) => {
          if (!v) return 0;
          const s = String(v).trim();
          // DD/MM/YYYY or DD-MM-YYYY
          const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
          if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]).getTime();
          // YYYY-MM-DD (ISO)
          return new Date(s).getTime() || 0;
        };
        va = parseDate(va); vb = parseDate(vb);
      }
      else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Pagination
  const totalFiltered = sorted.length;
  const totalPages = Math.ceil(totalFiltered / pageSize);
  const paginatedStart = (currentPage - 1) * pageSize;
  const paginatedData = sorted.slice(paginatedStart, paginatedStart + pageSize);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); setSelectAllFiltered(false); }, [colFilter, filterCol, filterOp, dateFrom, dateTo, sortCol, sortDir, data.length]);

  // Selection: page-level or all-filtered
  const pageIds = paginatedData.map(item => item[idField]);
  const allFilteredIds = sorted.map(item => item[idField]);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  const togglePageAll = () => {
    setSelectAllFiltered(false);
    if (allPageSelected) {
      const n = new Set(selected);
      pageIds.forEach(id => n.delete(id));
      onSelectionChange(n);
    } else {
      const n = new Set(selected);
      pageIds.forEach(id => n.add(id));
      onSelectionChange(n);
    }
  };

  const selectAllFilteredRecords = () => {
    setSelectAllFiltered(true);
    onSelectionChange(new Set(allFilteredIds));
  };

  const clearSelection = () => {
    setSelectAllFiltered(false);
    onSelectionChange(new Set());
  };

  const toggleOne = (id) => { setSelectAllFiltered(false); const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); onSelectionChange(n); };
  const handleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
  };

  // Build a lookup of all known reference values for tagged column display
  const refLookup = useMemo(() => {
    const lookup = {};
    // ref_select columns use refData
    const refMap = { accounts: 'name', businessUnits: 'name', vendors: 'name', departments: 'name', projects: 'name', categories: 'name', sourceOfFunds: 'name', incomeCategories: 'name' };
    for (const [rk, nameField] of Object.entries(refMap)) {
      const items = refData[rk] || [];
      lookup[rk] = new Set(items.map(i => {
        const val = i[nameField];
        return (typeof val === 'string' ? val : String(val || '')).toLowerCase();
      }));
    }
    // EXPENSE_TYPES constant + custom types from categories (top-level)
    const customTypes = (refData.categories || []).filter(c => !c.parent_code).map(c => (c.name || '').toLowerCase());
    lookup._expense_types = new Set([...EXPENSE_TYPES.map(t => t.toLowerCase()), ...customTypes]);
    // Income types from income categories (top-level)
    const incTypes = (refData.incomeCategories || []).filter(c => !c.parent_code).map(c => (c.name || '').toLowerCase());
    lookup._income_types = new Set(incTypes);
    // Income classifications from income categories (Level 2 only — parent is a top-level type)
    const topLevelTypeNames = new Set(incTypes);
    const incClass = (refData.incomeCategories || []).filter(c => c.parent_code && topLevelTypeNames.has(c.parent_code.toLowerCase())).map(c => (c.name || '').toLowerCase());
    lookup._income_classifications = new Set([...INCOME_CLASSIFICATIONS.map(c => c.toLowerCase()), ...incClass]);
    // Income markets from income categories (sub-sub: items whose parent_code is itself a sub-type)
    const incMarkets = (refData.incomeCategories || []).filter(c => {
      if (!c.parent_code) return false;
      return (refData.incomeCategories || []).some(p => p.name === c.parent_code && p.parent_code);
    }).map(c => (c.name || '').toLowerCase());
    lookup._income_markets = new Set(incMarkets);
    // org members for person_combo
    lookup._members = new Set(orgMembers.map(m => (m.name || m.email || m.full_name || '').toLowerCase()));
    return lookup;
  }, [refData, orgMembers]);

  // Check if a value is from a known reference list
  const isRefValue = (colKey, val) => {
    if (!val) return false;
    const v = String(val).toLowerCase();
    switch (colKey) {
      case 'account_paid_from': return refLookup.accounts?.has(v);
      case 'business_unit': return refLookup.businessUnits?.has(v);
      case 'vendor': return refLookup.vendors?.has(v);
      case 'org_function': return refLookup.departments?.has(v);
      case 'expense_type': return refLookup._expense_types?.has(v);
      case 'expense_sub_type': return refLookup.categories?.has(v);
      case 'staff_contractor_name': return refLookup._members?.has(v);
      case 'project_attribution': return refLookup.projects?.has(v);
      case 'source_of_funds': return refLookup.sourceOfFunds?.has(v);
      case 'income_source_type': return refLookup._income_types?.has(v);
      case 'income_classification': return refLookup._income_classifications?.has(v);
      case 'income_paid_to': return refLookup.accounts?.has(v);
      case 'sales_person': return refLookup._members?.has(v);
      case 'market': return refLookup._income_markets?.has(v);
      default: return false;
    }
  };

  // Render a tagged cell value
  const renderTaggedValue = (item, col) => {
    // Special rendering for project_attribution — show colored tags with project codes
    if (col.type === 'project_attribution') {
      const attributions = item.project_attributions || [];
      if (attributions.length === 0) {
        const fallbackVal = getCellValue(item, col);
        if (fallbackVal) return <span className="text-muted-foreground text-[10px]">{String(fallbackVal)}</span>;
        return <span className="text-muted-foreground/30">—</span>;
      }
      const projColors = ['bg-indigo-100 text-indigo-700 border-indigo-300', 'bg-emerald-100 text-emerald-700 border-emerald-300', 'bg-amber-100 text-amber-700 border-amber-300', 'bg-rose-100 text-rose-700 border-rose-300', 'bg-cyan-100 text-cyan-700 border-cyan-300'];
      return (
        <div className="flex flex-wrap gap-0.5">
          {attributions.map((a, i) => (
            <TooltipProvider key={a.project_id || i} delayDuration={200}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <span className={cn("inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold border cursor-default", projColors[i % projColors.length])}>
                    {a.project_code || '?'} <span className="ml-0.5 font-normal">{a.percentage}%</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent><span className="text-xs">{a.project_name || 'Unknown project'}</span></TooltipContent>
              </UITooltip>
            </TooltipProvider>
          ))}
        </div>
      );
    }

    const val = getCellValue(item, col);
    if (!val && val !== 0) return <span className="text-muted-foreground/30">—</span>;

    if (!TAGGED_COLUMNS.has(col.key)) {
      return renderCell(item, col) || <span className="text-muted-foreground/30">—</span>;
    }

    const colors = TAG_COLORS[col.key];
    const inRef = isRefValue(col.key, val);

    if (inRef && colors) {
      return (
        <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border truncate max-w-full", colors.bg, colors.text, colors.border)}>
          {String(val)}
        </span>
      );
    }
    // Free text — show with warning
    return (
      <span className="inline-flex items-center gap-0.5 truncate max-w-full" title="Free text — not from a reference list">
        <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
        <span className="text-muted-foreground truncate">{String(val)}</span>
      </span>
    );
  };

  const fetchAuditLog = async (entityId, entityType) => {
    try {
      const res = await fetch(`${API}/finance/audit-log/${workspaceId}?entity_type=${entityType}&entity_id=${entityId}`, { credentials: 'include' });
      if (res.ok) setAuditLog({ entityId, logs: await res.json() });
    } catch { setAuditLog({ entityId, logs: [] }); }
  };

  const startEdit = (rowId, colKey, currentVal, colType) => {
    if (colType === 'link') return;
    // Checkbox toggle directly
    if (colType === 'checkbox') {
      const newVal = currentVal === 'Y' ? 'N' : 'Y';
      onInlineUpdate(rowId, { [colKey]: newVal });
      return;
    }
    // Conditional: staff_contractor_name only editable for Payroll & Benefits or Professional Services
    if (colKey === 'staff_contractor_name') {
      const row = data.find(d => d[idField] === rowId);
      const et = (row?.expense_type || '').toLowerCase();
      if (!et.includes('payroll') && !et.includes('professional') && !et.includes('staff') && !et.includes('contractor')) return;
    }
    setEditingCell({ rowId, colKey });
    setEditValue(String(currentVal ?? ''));
    if (colType === 'person_combo' || colType === 'ref_select' || colType === 'status_select') { setPersonSearch(String(currentVal ?? '')); setPersonDropOpen(true); }
  };
  const saveEdit = () => {
    if (!editingCell) return;
    onInlineUpdate(editingCell.rowId, { [editingCell.colKey]: editValue });
    setEditingCell(null); setEditValue(''); setPersonSearch(''); setPersonDropOpen(false);
  };
  const cancelEdit = () => { setEditingCell(null); setEditValue(''); setPersonSearch(''); setPersonDropOpen(false); };

  // Project Attribution modal handlers
  const openProjAttrModal = (item) => {
    const current = item.project_attributions || [];
    setProjAttrModal({ expenseId: item[idField], expenseName: item.expense_item || item.description || 'Expense' });
    setProjAttrForm(current.length > 0 ? current.map(a => ({...a})) : []);
    setProjNewName('');
  };
  const addProjToAttr = (proj) => {
    if (projAttrForm.some(a => a.project_id === proj.project_id)) return;
    const remaining = 100 - projAttrForm.reduce((s, a) => s + (a.percentage || 0), 0);
    setProjAttrForm(prev => [...prev, { project_id: proj.project_id, project_code: proj.project_code || '', project_name: proj.name, percentage: Math.max(0, remaining) }]);
  };
  const removeProjFromAttr = (projectId) => { setProjAttrForm(prev => prev.filter(a => a.project_id !== projectId)); };
  const updateProjPct = (projectId, pct) => { setProjAttrForm(prev => prev.map(a => a.project_id === projectId ? {...a, percentage: Math.max(0, Math.min(100, Number(pct) || 0))} : a)); };
  const projAttrTotal = projAttrForm.reduce((s, a) => s + (a.percentage || 0), 0);
  const saveProjAttr = async () => {
    if (projAttrForm.length > 0 && Math.abs(projAttrTotal - 100) > 0.01) { toast.error(`Total must equal 100% (currently ${projAttrTotal}%)`); return; }
    setProjSaving(true);
    try {
      const res = await fetch(`/api/finance/expenses/${workspaceId}/${projAttrModal.expenseId}/project-attribution`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributions: projAttrForm }),
      });
      if (res.ok) {
        toast.success('Project attribution saved');
        // Update the local data
        onInlineUpdate(projAttrModal.expenseId, {
          project_attributions: projAttrForm,
          project_attribution: projAttrForm.map(a => `${a.project_code}(${a.percentage}%)`).join(', '),
        });
        setProjAttrModal(null);
      } else { const e = await res.json(); toast.error(e.detail || 'Failed to save'); }
    } catch { toast.error('Network error'); }
    setProjSaving(false);
  };
  const createNewProject = async () => {
    if (!projNewName.trim()) return;
    try {
      const res = await fetch(`/api/finance/ref/${workspaceId}/projects`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: projNewName.trim() }),
      });
      if (res.ok) {
        const newProj = await res.json();
        toast.success(`Project "${newProj.name}" created (${newProj.project_code})`);
        if (onCreateRef) onCreateRef('projects', newProj);
        addProjToAttr(newProj);
        setProjNewName('');
      } else { toast.error('Failed to create project'); }
    } catch { toast.error('Network error'); }
  };

  // Column resize handler
  const startResize = (colKey, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[colKey] || parseInt(columns.find(c => c.key === colKey)?.w || '100');
    const onMove = (ev) => { setColWidths(p => ({ ...p, [colKey]: Math.max(60, startW + ev.clientX - startX) })); };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  if (!data.length) return (
    <div className="text-center py-16 text-muted-foreground">
      <EmptyIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
      <p className="font-medium">{emptyLabel}</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="finance-ledger-table">
      {/* Enhanced filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterCol} onValueChange={setFilterCol}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-column-select"><SelectValue placeholder="All columns" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All columns</SelectItem>
            {columns.filter(c => c.type !== 'link').map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterOp} onValueChange={setFilterOp}>
          <SelectTrigger className="h-8 w-28 text-xs" data-testid="filter-op-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="contains">Contains</SelectItem>
            <SelectItem value="exact">Exact match</SelectItem>
            <SelectItem value="starts">Starts with</SelectItem>
            <SelectItem value="ends">Ends with</SelectItem>
            <SelectItem value="gt">Greater than</SelectItem>
            <SelectItem value="lt">Less than</SelectItem>
            <SelectItem value="empty">Is empty</SelectItem>
            <SelectItem value="notEmpty">Not empty</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={colFilter} onChange={e => setColFilter(e.target.value)} placeholder={filterOp === 'empty' || filterOp === 'notEmpty' ? 'Applied' : 'Filter value...'} className="pl-8 h-8 text-xs"
            disabled={filterOp === 'empty' || filterOp === 'notEmpty'} data-testid="table-col-filter" />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>From</span>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="table-date-from" />
          <span>to</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="table-date-to" />
        </div>
        {(dateFrom || dateTo || colFilter || filterCol !== '_all' || filterOp !== 'contains') && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setDateFrom(''); setDateTo(''); setColFilter(''); setFilterCol('_all'); setFilterOp('contains'); }} data-testid="clear-filters-btn">
            <X className="w-3 h-3" />
          </Button>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-muted-foreground">
            {selected.size > 0 && <><strong>{selectAllFiltered ? totalFiltered : selected.size}</strong> selected{selectAllFiltered ? ' (all filtered)' : ''} &middot; </>}
            Showing {paginatedStart + 1}–{Math.min(paginatedStart + pageSize, totalFiltered)} of {totalFiltered}{totalFiltered !== data.length ? ` (filtered from ${data.length})` : ''} records
          </span>
          <span className="ml-auto" />
          <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="h-6 w-20 text-[10px] border-0 bg-transparent"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[25, 50, 100, 250, 500].map(n => <SelectItem key={n} value={String(n)}>{n}/page</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)} data-testid="page-first"><ChevronsLeft className="w-3 h-3" /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} data-testid="page-prev"><ChevronLeft className="w-3 h-3" /></Button>
            <span className="text-[10px] text-muted-foreground px-1">Page {currentPage} of {totalPages || 1}</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} data-testid="page-next"><ChevronRight className="w-3 h-3" /></Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)} data-testid="page-last"><ChevronsRight className="w-3 h-3" /></Button>
          </div>
        </div>
        {/* Select all filtered banner */}
        {allPageSelected && !selectAllFiltered && totalFiltered > pageSize && (
          <div className="flex items-center justify-center gap-2 py-1.5 bg-blue-50 dark:bg-blue-950/30 border-t text-xs" data-testid="select-all-banner">
            <span>All <strong>{pageIds.length}</strong> records on this page are selected.</span>
            <button className="text-blue-600 font-medium hover:underline" onClick={selectAllFilteredRecords}>
              Select all {totalFiltered} records{totalFiltered !== data.length ? ' matching this filter' : ''}
            </button>
          </div>
        )}
        {selectAllFiltered && (
          <div className="flex items-center justify-center gap-2 py-1.5 bg-blue-50 dark:bg-blue-950/30 border-t text-xs" data-testid="all-filtered-selected-banner">
            <span>All <strong>{totalFiltered}</strong> records{totalFiltered !== data.length ? ' matching this filter' : ''} are selected.</span>
            <button className="text-blue-600 font-medium hover:underline" onClick={clearSelection}>Clear selection</button>
          </div>
        )}
      </div>

      {/* Table with both scroll directions */}
      <div className="border rounded-lg overflow-hidden bg-card" ref={tableRef}>
        <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
          <table className="text-sm border-collapse" style={{ minWidth: `${columns.reduce((s, c) => s + (colWidths[c.key] || parseInt(c.w)), 0) + 80}px` }}>
            <thead className="bg-card sticky top-0 z-20 border-b shadow-sm">
              <tr>
                <th className="p-1.5 w-[70px] sticky left-0 bg-card z-30 border-r border-border">
                  <Checkbox checked={allPageSelected} onCheckedChange={togglePageAll} data-testid="table-select-all" />
                </th>
                {columns.map(col => (
                  <th key={col.key} className="p-1.5 text-left font-medium text-xs whitespace-nowrap cursor-pointer hover:bg-muted/80 select-none relative group/th border-r border-border/30"
                    style={{ textAlign: col.align || 'left', width: `${colWidths[col.key] || parseInt(col.w)}px`, minWidth: '60px' }}
                    onClick={() => handleSort(col.key)} data-testid={`sort-${col.key}`}>
                    <div className={cn("flex items-center gap-1", col.align === 'right' && 'justify-end', col.align === 'center' && 'justify-center')}>
                      {col.label}
                      {sortCol === col.key && <ArrowUpDown className={cn("w-3 h-3", sortDir === 'desc' && "rotate-180")} />}
                    </div>
                    {/* Column resize handle */}
                    <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors"
                      onMouseDown={e => startResize(col.key, e)} onClick={e => e.stopPropagation()} data-testid={`resize-${col.key}`} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx) => {
                const id = item[idField];
                const entityType = idField === 'income_id' ? 'income' : 'expense';
                return (
                  <tr key={id || idx} className={cn("border-t border-border hover:bg-muted/20 group", selected.has(id) && "bg-primary/5")} data-testid={`row-${idx}`}>
                    <td className="p-1.5 sticky left-0 bg-card z-10 border-r border-border">
                      <div className="flex items-center gap-0.5">
                        <Checkbox checked={selected.has(id)} onCheckedChange={() => toggleOne(id)} className="shrink-0" />
                        <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100" onClick={() => onEdit(item)} title="Edit" data-testid={`edit-${idx}`}><Pencil className="w-2.5 h-2.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100 text-slate-500" onClick={() => fetchAuditLog(id, entityType)} title="Change log" data-testid={`history-${idx}`}><History className="w-2.5 h-2.5" /></Button>
                      </div>
                    </td>
                    {columns.map(col => {
                      const isEditing = editingCell?.rowId === id && editingCell?.colKey === col.key;
                      const val = getCellValue(item, col);
                      return (
                        <td key={col.key} className={cn("p-1.5 text-xs border-r border-border/10 transition-colors", col.align === 'right' && 'text-right', col.align === 'center' && 'text-center', col.type === 'money' && 'font-mono', col.key === columns[0].key && 'font-mono whitespace-nowrap', isEditing && 'bg-primary/5 ring-1 ring-primary/30 ring-inset')}
                          style={{ width: `${colWidths[col.key] || parseInt(col.w)}px` }}
                          onClick={() => {
                            if (isEditing) return;
                            if (col.type === 'project_attribution') { openProjAttrModal(item); return; }
                            startEdit(id, col.key, val, col.type);
                          }}>
                          {isEditing ? (
                            col.type === 'person_combo' || col.type === 'ref_select' || col.type === 'status_select' ? (
                              <div className="relative" data-testid={`${col.type}-edit`}>
                                <div className="flex items-center gap-0.5">
                                  <Input value={personSearch} onChange={e => { setPersonSearch(e.target.value); setEditValue(e.target.value); setPersonDropOpen(true); }} autoFocus
                                    className="h-6 text-xs px-1 flex-1" placeholder="Type or select..."
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') {
                                        saveEdit();
                                        // Trigger investor flag if classification changed to Investment
                                        if (col.key === 'income_classification' && onClassificationChange) {
                                          const row = data.find(d => d[idField] === editingCell?.rowId);
                                          onClassificationChange(row, editValue);
                                        }
                                      }
                                      if (e.key === 'Escape') cancelEdit();
                                    }}
                                    onFocus={() => setPersonDropOpen(true)} />
                                  <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={() => {
                                    if (col.key === 'income_classification' && onClassificationChange) {
                                      const row = data.find(d => d[idField] === editingCell?.rowId);
                                      onClassificationChange(row, editValue);
                                    }
                                    saveEdit();
                                  }}><Check className="w-3 h-3 text-green-600" /></Button>
                                  <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={cancelEdit}><X className="w-3 h-3 text-red-500" /></Button>
                                </div>
                                {personDropOpen && (() => {
                                  let options = [];
                                  if (col.type === 'status_select') {
                                    const STATUS_OPTIONS = ['Pending', 'Invoiced', 'Refunded', 'Paid'];
                                    const q = personSearch.toLowerCase();
                                    options = STATUS_OPTIONS.filter(s => !q || s.toLowerCase().includes(q)).map(s => ({ value: s, label: s, sub: '' }));
                                  } else if (col.type === 'person_combo') {
                                    const q = personSearch.toLowerCase();
                                    const activeMembers = orgMembers.filter(m => m._active !== false && (!q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)));
                                    const offboarded = orgMembers.filter(m => m._active === false && (!q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)));
                                    options = activeMembers.map(m => ({ value: m.name || m.email, label: m.name || m.email, sub: m.role !== 'Workspace' ? m.role : '', section: 'active' }));
                                    if (offboarded.length > 0) {
                                      options.push({ value: '__divider__', label: '', sub: '', section: 'divider' });
                                      for (const m of offboarded) {
                                        options.push({ value: m.name || m.email, label: m.name || m.email, sub: m.role || 'Offboarded', section: 'offboarded' });
                                      }
                                    }
                                  } else if (col.refKey === '_expense_types') {
                                    // Combine hardcoded EXPENSE_TYPES + custom types from categories (top-level, no parent_code)
                                    const customTypes = (refData.categories || []).filter(c => !c.parent_code).map(c => c.name);
                                    const allTypes = [...new Set([...EXPENSE_TYPES, ...customTypes])];
                                    options = allTypes.filter(t => {
                                      const q = personSearch.toLowerCase();
                                      return !q || t.toLowerCase().includes(q);
                                    }).map(t => ({ value: t, label: t, sub: '' }));
                                  } else if (col.refKey === '_income_types') {
                                    // Income source types from income categories (top-level)
                                    const customTypes = (refData.incomeCategories || []).filter(c => !c.parent_code).map(c => c.name);
                                    const q = personSearch.toLowerCase();
                                    options = customTypes.filter(t => !q || t.toLowerCase().includes(q)).map(t => ({ value: t, label: t, sub: '' }));
                                  } else if (col.refKey === '_income_classifications') {
                                    // Income classifications filtered by the current row's income source type
                                    const rowItem = data.find(d => d[idField] === editingCell?.rowId);
                                    const parentType = (rowItem?.income_source_type || rowItem?.source_type || '').toLowerCase();
                                    const topLevelNames = new Set((refData.incomeCategories || []).filter(c => !c.parent_code).map(c => c.name.toLowerCase()));
                                    // Level 2 only: items whose parent_code is a top-level type name
                                    const customClassifications = (refData.incomeCategories || []).filter(c => c.parent_code && topLevelNames.has(c.parent_code.toLowerCase()));
                                    // Filter by selected income type if set
                                    const filtered = parentType
                                      ? customClassifications.filter(c => (c.parent_code || '').toLowerCase() === parentType)
                                      : customClassifications;
                                    const q = personSearch.toLowerCase();
                                    options = filtered.filter(c => !q || (c.name || '').toLowerCase().includes(q)).map(c => ({ value: c.name, label: c.name, sub: c.code ? `${c.code}` : '' }));
                                  } else if (col.refKey === '_income_markets') {
                                    // Sub-classifications (markets) — filtered by the current row's classification
                                    const rowItem = data.find(d => d[idField] === editingCell?.rowId);
                                    const parentClass = (rowItem?.income_classification || '').toLowerCase();
                                    const marketItems = (refData.incomeCategories || []).filter(c => {
                                      if (!c.parent_code) return false;
                                      const isSubSub = (refData.incomeCategories || []).some(p => p.name === c.parent_code && p.parent_code);
                                      if (!isSubSub) return false;
                                      const q = personSearch.toLowerCase();
                                      const matchSearch = !q || (c.name || '').toLowerCase().includes(q);
                                      const matchParent = !parentClass || (c.parent_code || '').toLowerCase() === parentClass;
                                      return matchSearch && matchParent;
                                    });
                                    options = marketItems.map(c => ({ value: c.name, label: c.name, sub: c.parent_code || '' }));
                                  } else if (col.refKey === 'categories') {
                                    // Show sub-types filtered by the current row's expense_type as parent
                                    const rowItem = data.find(d => d[idField] === editingCell?.rowId);
                                    const parentType = (rowItem?.expense_type || '').toLowerCase();
                                    options = (refData[col.refKey] || []).filter(r => {
                                      const q = personSearch.toLowerCase();
                                      const matchSearch = !q || (r.name || '').toLowerCase().includes(q);
                                      // Show all if no parent type set, or filter by parent
                                      const matchParent = !parentType || !r.parent_code || (r.parent_code || '').toLowerCase() === parentType;
                                      return matchSearch && matchParent;
                                    }).map(r => ({ value: r.name, label: r.name, sub: r.parent_code || '' }));
                                  } else if (col.refKey && refData[col.refKey]) {
                                    options = refData[col.refKey].filter(r => {
                                      const q = personSearch.toLowerCase();
                                      return !q || (r.name || '').toLowerCase().includes(q);
                                    }).map(r => ({ value: r.name, label: r.name, sub: r.description || '' }));
                                  }
                                  const hasExact = options.some(o => o.value?.toLowerCase() === personSearch.toLowerCase());
                                  return (
                                    <div className="absolute z-50 top-7 left-0 w-64 max-h-40 overflow-y-auto border rounded-md bg-popover shadow-lg" data-testid={`${col.type}-dropdown`}>
                                      {options.map((o, oi) => (
                                        o.value === '__divider__' ? (
                                          <div key={oi} className="px-2 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30 border-t border-b">Offboarded</div>
                                        ) : (
                                          <button key={oi} className={cn("w-full text-left px-2 py-1.5 text-xs hover:bg-muted/50 flex items-center justify-between",
                                            o.section === 'offboarded' && "opacity-60"
                                          )}
                                            onMouseDown={e => {
                                              e.preventDefault(); setEditValue(o.value); setPersonSearch(o.value); setPersonDropOpen(false);
                                              // Trigger classification change for investor flag
                                              if (col.key === 'income_classification' && onClassificationChange) {
                                                const row = data.find(d => d[idField] === editingCell?.rowId);
                                                setTimeout(() => onClassificationChange(row, o.value), 0);
                                              }
                                            }}>
                                            <span className="font-medium truncate">{o.label}</span>
                                            {o.sub && <span className="text-[10px] text-muted-foreground ml-2 shrink-0">{o.sub}</span>}
                                          </button>
                                        )
                                      ))}
                                      {personSearch && !hasExact && (
                                        <button className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/50 border-t text-blue-600"
                                          onMouseDown={async e => {
                                            e.preventDefault();
                                            if (col.type === 'ref_select' && onCreateRef && col.refKey) {
                                              await onCreateRef(col.refKey, personSearch, data.find(d => d[idField] === editingCell?.rowId));
                                              toast.success(`Created "${personSearch}"`);
                                            } else if (col.type === 'person_combo' && onCreateStaff) {
                                              if (confirm(`Add "${personSearch}" as a new contractor?`)) {
                                                onCreateStaff(personSearch);
                                              }
                                            }
                                            setEditValue(personSearch); setPersonDropOpen(false);
                                          }}>
                                          {(col.type === 'ref_select' || (col.type === 'person_combo' && onCreateStaff)) ? `+ Create "${personSearch}"` : `Use "${personSearch}"`}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div className="flex items-center gap-0.5">
                                <Input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus className="h-6 text-xs px-1 flex-1"
                                  type={col.type === 'money' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                                  step={col.type === 'money' ? '0.01' : undefined}
                                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                                <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={saveEdit}><Check className="w-3 h-3 text-green-600" /></Button>
                                <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={cancelEdit}><X className="w-3 h-3 text-red-500" /></Button>
                              </div>
                            )
                          ) : (() => {
                            // Disable staff_contractor_name cell for non-staff expense types
                            const isStaffLocked = col.key === 'staff_contractor_name' && (() => {
                              const et = (item.expense_type || '').toLowerCase();
                              return !et.includes('payroll') && !et.includes('professional') && !et.includes('staff') && !et.includes('contractor');
                            })();
                            return isStaffLocked
                              ? <span className="truncate block text-muted-foreground/20 cursor-not-allowed" title="Only editable for Payroll or Professional Services expenses">{val || '—'}</span>
                              : <span className="truncate block cursor-text hover:bg-muted/30 rounded px-0.5 -mx-0.5" title={String(val)}>{renderTaggedValue(item, col)}</span>;
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Project Attribution Modal */}
      {projAttrModal && (
        <Dialog open={!!projAttrModal} onOpenChange={v => !v && setProjAttrModal(null)}>
          <DialogContent className="max-w-lg" data-testid="project-attribution-modal">
            <DialogHeader>
              <DialogTitle>Project Attribution</DialogTitle>
              <DialogDescription>Allocate this expense across projects. Total must equal 100%.</DialogDescription>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mb-2">Expense: <strong>{projAttrModal.expenseName}</strong></p>

            {/* Current attributions */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {projAttrForm.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">No projects attributed yet. Select from below.</p>}
              {projAttrForm.map((attr, i) => (
                <div key={attr.project_id} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/20">
                  <div className="flex-1">
                    <span className="text-xs font-semibold">{attr.project_code}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">{attr.project_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Input type="number" min={0} max={100} step={1} value={attr.percentage} onChange={e => updateProjPct(attr.project_id, e.target.value)}
                      className="h-7 w-16 text-xs text-right font-mono" data-testid={`proj-attr-pct-${i}`} />
                    <span className="text-xs text-muted-foreground">%</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeProjFromAttr(attr.project_id)} data-testid={`proj-attr-remove-${i}`}><X className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Total indicator */}
            <div className={cn("flex items-center justify-between px-2 py-1.5 rounded text-xs font-medium",
              projAttrForm.length === 0 ? "bg-muted/20 text-muted-foreground" :
              Math.abs(projAttrTotal - 100) < 0.01 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              "bg-red-50 text-red-700 border border-red-200")}>
              <span>Total</span>
              <span className="font-mono">{projAttrTotal}%</span>
            </div>

            {/* Select from existing projects */}
            <div className="border-t pt-3 space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Available Projects</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {(refData.projects || []).filter(p => !p.is_deleted && !projAttrForm.some(a => a.project_id === p.project_id)).map(p => (
                  <button key={p.project_id} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-muted/50 rounded border" onClick={() => addProjToAttr(p)} data-testid={`proj-select-${p.project_id}`}>
                    <span className="font-mono font-semibold text-indigo-600">{p.project_code || '—'}</span>
                    <span>{p.name}</span>
                    <Plus className="w-3 h-3 ml-auto text-muted-foreground" />
                  </button>
                ))}
                {(refData.projects || []).filter(p => !p.is_deleted && !projAttrForm.some(a => a.project_id === p.project_id)).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-1">All projects already attributed or none exist.</p>
                )}
              </div>

              {/* Create new project inline */}
              <div className="flex gap-1.5 mt-2">
                <Input value={projNewName} onChange={e => setProjNewName(e.target.value)} placeholder="New project name..." className="h-8 text-xs flex-1" data-testid="proj-new-name"
                  onKeyDown={e => e.key === 'Enter' && createNewProject()} />
                <Button size="sm" variant="outline" onClick={createNewProject} disabled={!projNewName.trim()} data-testid="proj-create-btn">
                  <Plus className="w-3 h-3 mr-1" />Create
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setProjAttrModal(null)}>Cancel</Button>
              <Button onClick={saveProjAttr} disabled={projSaving || (projAttrForm.length > 0 && Math.abs(projAttrTotal - 100) > 0.01)} data-testid="proj-attr-save-btn">
                {projSaving ? 'Saving...' : 'Save Attribution'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Audit Log Panel — Enhanced with friendly field labels */}
      {auditLog && (
        <div className="mt-3 border rounded-lg bg-card shadow-sm p-4 animate-in slide-in-from-bottom-2" data-testid="audit-log-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Change History</h4>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground">{auditLog.logs.length} change{auditLog.logs.length !== 1 ? 's' : ''}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setAuditLog(null)} data-testid="audit-close-btn"><X className="w-3 h-3" /></Button>
            </div>
          </div>
          {auditLog.logs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No changes recorded for this item.</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {auditLog.logs.map((log, i) => (
                <div key={i} className="text-xs border-l-2 border-primary/20 pl-3 pb-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">{(log.user_name || log.user_id || '?')[0].toUpperCase()}</span>
                    </div>
                    <span className="font-medium text-foreground">{log.user_name || log.user_id}</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">{log.action}</Badge>
                    <span className="text-[10px] text-muted-foreground ml-auto">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  {Object.entries(log.changes || {}).length > 0 && (
                    <div className="ml-7 space-y-1 bg-muted/30 rounded p-2">
                      {Object.entries(log.changes).map(([field, vals]) => {
                        const label = FIELD_LABEL_MAP[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        const oldStr = String(vals.old ?? '').trim();
                        const newStr = String(vals.new ?? '').trim();
                        return (
                          <div key={field} className="flex items-start gap-1.5 text-[11px]" data-testid={`change-${field}`}>
                            <span className="text-muted-foreground font-medium shrink-0 min-w-[100px]">{label}</span>
                            {oldStr ? (
                              <>
                                <span className="px-1 py-0.5 rounded bg-red-50 dark:bg-red-950/30 text-red-500 line-through max-w-[140px] truncate">{oldStr}</span>
                                <ArrowRight className="w-2.5 h-2.5 text-muted-foreground shrink-0 mt-0.5" />
                              </>
                            ) : (
                              <span className="text-muted-foreground/40 text-[10px]">(empty)</span>
                            )}
                            <span className="px-1 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 font-medium max-w-[140px] truncate">{newStr || '(cleared)'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== FORECAST VIEW ====================
const ForecastView = ({ forecasts, onSelect }) => {
  if (!forecasts.length) return (
    <div className="text-center py-16 text-muted-foreground">
      <LineChart className="w-16 h-16 mx-auto mb-4 opacity-20" />
      <p className="font-medium">No forecast models yet</p>
      <p className="text-sm">Create your first forecast to start modeling revenue and costs</p>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {forecasts.map(f => (
        <Card key={f.forecast_id} className="border-0 shadow-sm bg-card hover:shadow-md transition-shadow cursor-pointer" data-testid={`forecast-card-${f.forecast_id}`}
          onClick={() => onSelect(f)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center"><LineChart className="w-5 h-5 text-indigo-500" /></div>
              <Badge variant="outline" className="text-[10px]">{f.start_month} - {f.end_month}</Badge>
            </div>
            <h3 className="font-semibold text-sm">{f.name}</h3>
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span>{(f.revenue_models || []).length} revenue models</span>
              <span>{(f.cost_models || []).length} cost models</span>
              <span>{(f.hiring_models || []).length} hiring plans</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ==================== FORECAST DETAIL VIEW ====================
const ForecastDetailView = ({ forecast, onBack, onSave, onDelete }) => {
  const [revenueModels, setRevenueModels] = useState(forecast.revenue_models || []);
  const [costModels, setCostModels] = useState(forecast.cost_models || []);
  const [hiringModels, setHiringModels] = useState(forecast.hiring_models || []);
  const [activeSection, setActiveSection] = useState('revenue');
  const [dirty, setDirty] = useState(false);

  const addRevenueModel = () => {
    setRevenueModels(prev => [...prev, {
      id: `rev_${Date.now()}`, name: '', type: 'recurring', amount: 0,
      growth_rate: 0, start_month: forecast.start_month || '', notes: '',
    }]);
    setDirty(true);
  };

  const addCostModel = () => {
    setCostModels(prev => [...prev, {
      id: `cost_${Date.now()}`, name: '', type: 'fixed', amount: 0,
      growth_rate: 0, category: 'J', start_month: forecast.start_month || '', notes: '',
    }]);
    setDirty(true);
  };

  const addHiringModel = () => {
    setHiringModels(prev => [...prev, {
      id: `hire_${Date.now()}`, role: '', department: '', monthly_cost: 0,
      start_month: '', headcount: 1, notes: '',
    }]);
    setDirty(true);
  };

  const updateItem = (setter, idx, field, value) => {
    setter(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
    setDirty(true);
  };

  const removeItem = (setter, idx) => {
    setter(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = () => {
    onSave({ revenue_models: revenueModels, cost_models: costModels, hiring_models: hiringModels });
    setDirty(false);
  };

  // Generate monthly projections
  const generateProjections = () => {
    if (!forecast.start_month || !forecast.end_month) return [];
    const months = [];
    let [sy, sm] = forecast.start_month.split('-').map(Number);
    const [ey, em] = forecast.end_month.split('-').map(Number);
    while (sy < ey || (sy === ey && sm <= em)) {
      months.push(`${sy}-${String(sm).padStart(2, '0')}`);
      sm++;
      if (sm > 12) { sm = 1; sy++; }
    }
    return months.slice(0, 24); // max 24 months
  };

  const projMonths = generateProjections();

  const getMonthlyRevenue = (month, monthIdx) => {
    return revenueModels.reduce((sum, m) => {
      if (m.start_month && month < m.start_month) return sum;
      const base = Number(m.amount) || 0;
      const growth = Number(m.growth_rate) || 0;
      const monthsSinceStart = m.start_month ? projMonths.indexOf(month) - projMonths.indexOf(m.start_month) : monthIdx;
      return sum + base * Math.pow(1 + growth / 100, Math.max(monthsSinceStart, 0));
    }, 0);
  };

  const getMonthlyCost = (month, monthIdx) => {
    const modelCosts = costModels.reduce((sum, m) => {
      if (m.start_month && month < m.start_month) return sum;
      const base = Number(m.amount) || 0;
      const growth = Number(m.growth_rate) || 0;
      const monthsSinceStart = m.start_month ? projMonths.indexOf(month) - projMonths.indexOf(m.start_month) : monthIdx;
      return sum + base * Math.pow(1 + growth / 100, Math.max(monthsSinceStart, 0));
    }, 0);
    const hiringCosts = hiringModels.reduce((sum, m) => {
      if (m.start_month && month < m.start_month) return sum;
      return sum + (Number(m.monthly_cost) || 0) * (Number(m.headcount) || 1);
    }, 0);
    return modelCosts + hiringCosts;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="forecast-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h2 className="text-lg font-semibold">{forecast.name}</h2>
            <p className="text-xs text-muted-foreground">{forecast.start_month} to {forecast.end_month}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <Button onClick={handleSave} data-testid="save-forecast-models-btn">Save Changes</Button>}
          <Button variant="outline" size="sm" className="text-red-500" onClick={onDelete} data-testid="delete-forecast-btn">
            <Trash2 className="w-4 h-4 mr-1" />Delete
          </Button>
        </div>
      </div>

      {/* Section Toggle */}
      <div className="flex gap-2">
        {[
          { key: 'revenue', label: 'Revenue Models', icon: TrendingUp, count: revenueModels.length },
          { key: 'costs', label: 'Cost Models', icon: TrendingDown, count: costModels.length },
          { key: 'hiring', label: 'Hiring Plan', icon: Users, count: hiringModels.length },
          { key: 'projections', label: 'Projections', icon: BarChart3 },
        ].map(s => (
          <Button key={s.key} variant={activeSection === s.key ? 'default' : 'outline'} size="sm"
            onClick={() => setActiveSection(s.key)} data-testid={`forecast-section-${s.key}`}>
            <s.icon className="w-3.5 h-3.5 mr-1.5" />{s.label}
            {s.count !== undefined && <Badge variant="secondary" className="ml-1.5 text-[10px] h-4 px-1">{s.count}</Badge>}
          </Button>
        ))}
      </div>

      {/* Revenue Models */}
      {activeSection === 'revenue' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Define revenue streams with growth rates</p>
            <Button size="sm" onClick={addRevenueModel} data-testid="add-revenue-model-btn"><Plus className="w-4 h-4 mr-1" />Add Revenue Stream</Button>
          </div>
          {revenueModels.map((m, idx) => (
            <Card key={m.id} className="border shadow-sm bg-card" data-testid={`revenue-model-${idx}`}>
              <CardContent className="p-4 grid grid-cols-5 gap-3 items-end">
                <div><label className="text-xs text-muted-foreground block mb-1">Name</label>
                  <Input value={m.name} onChange={e => updateItem(setRevenueModels, idx, 'name', e.target.value)} placeholder="e.g. SaaS MRR" className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Type</label>
                  <Select value={m.type} onValueChange={v => updateItem(setRevenueModels, idx, 'type', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recurring">Recurring</SelectItem>
                      <SelectItem value="one_time">One-time</SelectItem>
                      <SelectItem value="usage">Usage-based</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Monthly Amount</label>
                  <Input type="number" value={m.amount} onChange={e => updateItem(setRevenueModels, idx, 'amount', e.target.value)} className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">MoM Growth %</label>
                  <Input type="number" step="0.1" value={m.growth_rate} onChange={e => updateItem(setRevenueModels, idx, 'growth_rate', e.target.value)} className="h-8 text-sm" /></div>
                <div className="flex gap-1 items-end">
                  <div className="flex-1"><label className="text-xs text-muted-foreground block mb-1">Start</label>
                    <Input type="month" value={m.start_month} onChange={e => updateItem(setRevenueModels, idx, 'start_month', e.target.value)} className="h-8 text-sm" /></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeItem(setRevenueModels, idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!revenueModels.length && <p className="text-center py-8 text-sm text-muted-foreground">No revenue models yet. Add one to start forecasting.</p>}
        </div>
      )}

      {/* Cost Models */}
      {activeSection === 'costs' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Define fixed and variable costs</p>
            <Button size="sm" onClick={addCostModel} data-testid="add-cost-model-btn"><Plus className="w-4 h-4 mr-1" />Add Cost Line</Button>
          </div>
          {costModels.map((m, idx) => (
            <Card key={m.id} className="border shadow-sm bg-card" data-testid={`cost-model-${idx}`}>
              <CardContent className="p-4 grid grid-cols-5 gap-3 items-end">
                <div><label className="text-xs text-muted-foreground block mb-1">Name</label>
                  <Input value={m.name} onChange={e => updateItem(setCostModels, idx, 'name', e.target.value)} placeholder="e.g. AWS Hosting" className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Type</label>
                  <Select value={m.type} onValueChange={v => updateItem(setCostModels, idx, 'type', v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="variable">Variable</SelectItem>
                      <SelectItem value="step">Step (per headcount)</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Monthly Amount</label>
                  <Input type="number" value={m.amount} onChange={e => updateItem(setCostModels, idx, 'amount', e.target.value)} className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">MoM Growth %</label>
                  <Input type="number" step="0.1" value={m.growth_rate} onChange={e => updateItem(setCostModels, idx, 'growth_rate', e.target.value)} className="h-8 text-sm" /></div>
                <div className="flex gap-1 items-end">
                  <div className="flex-1"><label className="text-xs text-muted-foreground block mb-1">Category</label>
                    <Select value={m.category} onValueChange={v => updateItem(setCostModels, idx, 'category', v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(CATEGORY_COLORS).map(([k, v]) => <SelectItem key={k} value={k}>{k}: {v.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeItem(setCostModels, idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!costModels.length && <p className="text-center py-8 text-sm text-muted-foreground">No cost models yet. Add one to start forecasting.</p>}
        </div>
      )}

      {/* Hiring Plan */}
      {activeSection === 'hiring' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Plan headcount growth and salary costs</p>
            <Button size="sm" onClick={addHiringModel} data-testid="add-hiring-model-btn"><Plus className="w-4 h-4 mr-1" />Add Role</Button>
          </div>
          {hiringModels.map((m, idx) => (
            <Card key={m.id} className="border shadow-sm bg-card" data-testid={`hiring-model-${idx}`}>
              <CardContent className="p-4 grid grid-cols-5 gap-3 items-end">
                <div><label className="text-xs text-muted-foreground block mb-1">Role</label>
                  <Input value={m.role} onChange={e => updateItem(setHiringModels, idx, 'role', e.target.value)} placeholder="e.g. Senior Engineer" className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Department</label>
                  <Input value={m.department} onChange={e => updateItem(setHiringModels, idx, 'department', e.target.value)} placeholder="e.g. Engineering" className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Monthly Cost</label>
                  <Input type="number" value={m.monthly_cost} onChange={e => updateItem(setHiringModels, idx, 'monthly_cost', e.target.value)} className="h-8 text-sm" /></div>
                <div><label className="text-xs text-muted-foreground block mb-1">Headcount</label>
                  <Input type="number" min="1" value={m.headcount} onChange={e => updateItem(setHiringModels, idx, 'headcount', e.target.value)} className="h-8 text-sm" /></div>
                <div className="flex gap-1 items-end">
                  <div className="flex-1"><label className="text-xs text-muted-foreground block mb-1">Start Month</label>
                    <Input type="month" value={m.start_month} onChange={e => updateItem(setHiringModels, idx, 'start_month', e.target.value)} className="h-8 text-sm" /></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeItem(setHiringModels, idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!hiringModels.length && <p className="text-center py-8 text-sm text-muted-foreground">No hiring plans yet. Add roles to model payroll costs.</p>}
        </div>
      )}

      {/* Monthly Projections Grid */}
      {activeSection === 'projections' && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Monthly revenue vs cost projections based on your models</p>
          {projMonths.length > 0 ? (
            <Card className="border shadow-sm bg-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-2 text-left font-medium sticky left-0 bg-muted/50 min-w-[100px]">Month</th>
                      <th className="p-2 text-right font-medium text-emerald-500 min-w-[100px]">Revenue</th>
                      <th className="p-2 text-right font-medium text-red-500 min-w-[100px]">Costs</th>
                      <th className="p-2 text-right font-medium min-w-[100px]">Net</th>
                      <th className="p-2 text-right font-medium min-w-[100px]">Cumulative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let cumulative = 0;
                      return projMonths.map((month, idx) => {
                        const rev = getMonthlyRevenue(month, idx);
                        const cost = getMonthlyCost(month, idx);
                        const net = rev - cost;
                        cumulative += net;
                        return (
                          <tr key={month} className="border-t border-border hover:bg-muted/30" data-testid={`projection-row-${month}`}>
                            <td className="p-2 font-mono font-medium sticky left-0 bg-card">{month}</td>
                            <td className="p-2 text-right font-mono text-emerald-500">{formatMoney(rev)}</td>
                            <td className="p-2 text-right font-mono text-red-500">{formatMoney(cost)}</td>
                            <td className={cn("p-2 text-right font-mono font-medium", net >= 0 ? "text-emerald-500" : "text-red-500")}>{formatMoney(net)}</td>
                            <td className={cn("p-2 text-right font-mono", cumulative >= 0 ? "text-emerald-500" : "text-red-500")}>{formatMoney(cumulative)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <p className="text-center py-8 text-sm text-muted-foreground">Set start and end months on the forecast to see projections.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== STAFFING VIEW ====================
const StaffingView = ({ workspaceId, workspaceType }) => {
  const API = '/api';
  const [activeEntries, setActiveEntries] = useState(null);
  const [archivedEntries, setArchivedEntries] = useState(null);
  const [tab, setTab] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [orgPeople, setOrgPeople] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showStaffImport, setShowStaffImport] = useState(false);
  const [staffImportPreview, setStaffImportPreview] = useState(null);
  const [staffImportLoading, setStaffImportLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', job_title: '', department: '', employment_type: 'contractor',
    pay_type: 'salary', annual_salary: '', hourly_rate: '', hours_per_week: '40',
    bonus_target: '', bonus_kpi: '', commission_rate: '', currency: 'USD',
    start_date: '', end_date: '', notes: '',
  });

  const fetchActive = useCallback(async () => {
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/entries?status=active`, { credentials: 'include' });
      if (r.ok) setActiveEntries(await r.json());
    } catch {}
  }, [workspaceId]);

  const fetchArchived = useCallback(async () => {
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/entries?status=archived`, { credentials: 'include' });
      if (r.ok) setArchivedEntries(await r.json());
    } catch {}
  }, [workspaceId]);

  const fetchOrgPeople = useCallback(async () => {
    try { const r = await fetch(`${API}/org/members`, { credentials: 'include' }); if (r.ok) setOrgPeople(await r.json()); } catch {}
  }, []);

  const fetchAll = useCallback(() => { fetchActive(); fetchArchived(); }, [fetchActive, fetchArchived]);

  useEffect(() => { fetchAll(); fetchOrgPeople(); }, [fetchAll, fetchOrgPeople]);

  const buildTeam = (entries) => {
    if (!entries) return [];
    const org = (entries.org_team || []).map(t => ({ ...t, _source: 'org' }));
    const contractors = (entries.contractors || []).map(t => ({ ...t, _source: 'direct' }));
    return [...org, ...contractors].sort((a, b) => (b.annual_salary || 0) - (a.annual_salary || 0));
  };

  const activeTeam = useMemo(() => buildTeam(activeEntries), [activeEntries]);
  const archivedTeam = useMemo(() => buildTeam(archivedEntries), [archivedEntries]);
  const currentTeam = tab === 'active' ? activeTeam : archivedTeam;

  const totalMonthly = activeTeam.reduce((sum, t) => {
    if (t.pay_type === 'hourly') return sum + (t.hourly_rate || 0) * (t.hours_per_week || 0) * 4.33;
    return sum + (t.annual_salary || 0) / 12;
  }, 0);

  // Clear selection when switching tabs
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const syncFromPeople = async () => {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/sync-from-people`, { method: 'POST', credentials: 'include' });
      if (r.ok) { const d = await r.json(); toast.success(d.message); fetchAll(); }
    } catch { toast.error('Sync failed'); }
    setSyncing(false);
  };

  const openForm = (entry) => {
    if (entry) {
      setEditEntry(entry);
      setForm({
        name: entry.name || '', email: entry.email || '', job_title: entry.job_title || '',
        department: entry.department || '', employment_type: entry.employment_type || 'contractor',
        pay_type: entry.pay_type || 'salary', annual_salary: String(entry.annual_salary || ''),
        hourly_rate: String(entry.hourly_rate || ''), hours_per_week: String(entry.hours_per_week || '40'),
        bonus_target: String(entry.bonus_target || ''), bonus_kpi: entry.bonus_kpi || '',
        commission_rate: String(entry.commission_rate || ''), currency: entry.currency || 'USD',
        start_date: entry.start_date || '', end_date: entry.end_date || '', notes: entry.notes || '',
      });
    } else {
      setEditEntry(null);
      setForm({
        name: '', email: '', job_title: '', department: '', employment_type: 'contractor',
        pay_type: 'salary', annual_salary: '', hourly_rate: '', hours_per_week: '40',
        bonus_target: '', bonus_kpi: '', commission_rate: '', currency: 'USD',
        start_date: '', end_date: '', notes: '',
      });
    }
    setShowForm(true);
  };

  const saveEntry = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const url = editEntry
      ? `${API}/finance/staffing/${workspaceId}/entries/${editEntry.entry_id}`
      : `${API}/finance/staffing/${workspaceId}/entries`;
    const r = await fetch(url, {
      method: editEntry ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success(editEntry ? 'Updated' : 'Added'); setShowForm(false); fetchAll(); }
    else { toast.error('Failed'); }
  };

  const deleteEntry = async (entryId) => {
    if (!confirm('Remove this entry?')) return;
    const r = await fetch(`${API}/finance/staffing/${workspaceId}/entries/${entryId}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) { toast.success('Removed'); fetchAll(); }
    else { try { const e = await r.json(); toast.error(e.detail || 'Cannot remove'); } catch { toast.error('Cannot remove'); } }
  };

  const promoteTopeople = async (entryId) => {
    const r = await fetch(`${API}/finance/staffing/${workspaceId}/entries/${entryId}/promote-to-people`, { method: 'POST', credentials: 'include' });
    if (r.ok) { const d = await r.json(); toast.success(d.message); fetchAll(); fetchOrgPeople(); }
    else { try { const e = await r.json(); toast.error(e.detail || 'Failed'); } catch { toast.error('Failed'); } }
  };

  const linkToPerson = async (entryId, personId) => {
    const r = await fetch(`${API}/finance/staffing/${workspaceId}/entries/${entryId}/link-to-person`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ person_id: personId }),
    });
    if (r.ok) { const d = await r.json(); toast.success(d.message); setLinkModal(null); fetchAll(); }
    else { try { const e = await r.json(); toast.error(e.detail || 'Failed'); } catch { toast.error('Failed'); } }
  };

  // Bulk actions
  const handleArchiveSelected = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/archive`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_ids: [...selected] }),
      });
      if (r.ok) { const d = await r.json(); toast.success(`Archived ${d.archived_count} entries`); setSelected(new Set()); fetchAll(); }
      else { toast.error('Archive failed'); }
    } catch { toast.error('Archive failed'); }
    setBulkLoading(false);
  };

  const handleReactivateSelected = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/reactivate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_ids: [...selected] }),
      });
      if (r.ok) { const d = await r.json(); toast.success(`Reactivated ${d.reactivated_count} entries`); setSelected(new Set()); fetchAll(); }
      else { toast.error('Reactivate failed'); }
    } catch { toast.error('Reactivate failed'); }
    setBulkLoading(false);
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    const hasOrgMembers = ids.some(id => id.startsWith('org_'));
    if (hasOrgMembers) {
      toast.error('Cannot delete org members from staffing. Remove them from Organisation > People first.');
      return;
    }
    if (!confirm(`Delete ${ids.length} selected entries permanently?`)) return;
    setBulkLoading(true);
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/bulk-action`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_ids: ids, action: 'delete' }),
      });
      if (r.ok) { const d = await r.json(); toast.success(`Deleted ${d.affected} entries`); setSelected(new Set()); fetchAll(); }
      else { toast.error('Delete failed'); }
    } catch { toast.error('Delete failed'); }
    setBulkLoading(false);
  };

  const toggleSelect = (entryId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId); else next.add(entryId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === currentTeam.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(currentTeam.map(p => p.entry_id)));
    }
  };

  const getMonthly = (t) => {
    if (t.pay_type === 'hourly') return (t.hourly_rate || 0) * (t.hours_per_week || 0) * 4.33;
    return (t.annual_salary || 0) / 12;
  };

  const getPayLabel = (t) => {
    switch (t.pay_type) {
      case 'hourly': return `${formatMoney(t.hourly_rate)}/hr × ${t.hours_per_week}h/wk`;
      case 'bonus': return `${formatMoney(t.annual_salary)}/yr + ${formatMoney(t.bonus_target)} bonus`;
      case 'commission': return `${formatMoney(t.annual_salary)}/yr + ${t.commission_rate}% comm`;
      case 'mixed': return `${formatMoney(t.annual_salary)}/yr + bonus + comm`;
      default: return `${formatMoney(t.annual_salary)}/yr`;
    }
  };

  // Check if selection has any org members (for disabling delete)
  const selectedHasOrgMembers = [...selected].some(id => id.startsWith('org_'));

  if (!activeEntries) return <div className="flex items-center justify-center h-[40vh]"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;


  const handleExportStaffing = async () => {
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/export`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const { fields, data } = await r.json();
      const header = fields.join(',');
      const rows = data.map(row => fields.map(f => {
        const v = String(row[f] || '');
        return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'staffing_export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} staffing entries`);
    } catch { toast.error('Export failed'); }
  };

  const handleDownloadStaffingTemplate = async () => {
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/import-template`, { credentials: 'include' });
      if (!r.ok) throw new Error();
      const { fields } = await r.json();
      const csv = fields.join(',') + '\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'staffing_import_template.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleStaffingFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (result) => { setStaffImportPreview(result.data); setShowStaffImport(true); },
      error: () => toast.error('Failed to parse CSV'),
    });
    e.target.value = '';
  };

  const handleConfirmStaffImport = async () => {
    if (!staffImportPreview?.length) return;
    setStaffImportLoading(true);
    try {
      const r = await fetch(`${API}/finance/staffing/${workspaceId}/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ records: staffImportPreview }),
      });
      if (r.ok) {
        const res = await r.json();
        toast.success(`Import: ${res.updated} updated, ${res.created} created, ${res.skipped} skipped`);
        setShowStaffImport(false); setStaffImportPreview(null);
        fetchActive(); fetchArchived();
      } else { toast.error('Import failed'); }
    } catch { toast.error('Import failed'); }
    setStaffImportLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" data-testid="staffing-title">Staffing & Contractors</h2>
          <p className="text-sm text-muted-foreground">Staff from Organisation &gt; People + contractors. Edits sync both ways.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="file" accept=".csv" id="staffing-import-file" className="hidden" onChange={handleStaffingFileUpload} />
          <Button variant="outline" size="sm" onClick={handleDownloadStaffingTemplate} data-testid="staffing-template-btn"><FileText className="w-3.5 h-3.5 mr-1" />Template</Button>
          <Button variant="outline" size="sm" onClick={() => document.getElementById('staffing-import-file').click()} data-testid="staffing-import-btn"><Upload className="w-3.5 h-3.5 mr-1" />Import</Button>
          <Button variant="outline" size="sm" onClick={handleExportStaffing} data-testid="staffing-export-btn"><FileText className="w-3.5 h-3.5 mr-1" />Export</Button>
          <Button onClick={() => openForm(null)} size="sm" data-testid="add-staffing-btn"><Plus className="w-4 h-4 mr-1" />Add Staff / Contractor</Button>
          {workspaceType !== 'personal' && (
          <Button onClick={syncFromPeople} variant="outline" size="sm" disabled={syncing} data-testid="sync-from-people-btn">
            <RefreshCw className={cn("w-4 h-4 mr-1", syncing && "animate-spin")} />Sync from People
          </Button>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-card"><CardContent className="p-4">
          <span className="text-xs text-muted-foreground">Active Headcount</span>
          <p className="text-2xl font-bold mt-1" data-testid="staffing-headcount">{activeTeam.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm bg-card"><CardContent className="p-4">
          <span className="text-xs text-muted-foreground">Monthly Payroll</span>
          <p className="text-2xl font-bold mt-1" data-testid="staffing-monthly">{formatMoney(totalMonthly)}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm bg-card"><CardContent className="p-4">
          <span className="text-xs text-muted-foreground">Annual Payroll</span>
          <p className="text-2xl font-bold mt-1" data-testid="staffing-annual">{formatMoney(totalMonthly * 12)}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm bg-card"><CardContent className="p-4">
          <span className="text-xs text-muted-foreground">Archived</span>
          <p className="text-2xl font-bold mt-1" data-testid="staffing-archived-count">{archivedTeam.length}</p>
        </CardContent></Card>
      </div>

      {/* Tabs: Active / Archived */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between">
          <TabsList data-testid="staffing-tabs">
            <TabsTrigger value="active" data-testid="staffing-tab-active">Active ({activeTeam.length})</TabsTrigger>
            <TabsTrigger value="archived" data-testid="staffing-tab-archived">Archived ({archivedTeam.length})</TabsTrigger>
          </TabsList>
          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="bulk-action-bar">
              <span className="text-xs text-muted-foreground">{selected.size} selected</span>
              {tab === 'active' && (
                <>
                  <Button size="sm" variant="outline" onClick={handleArchiveSelected} disabled={bulkLoading} data-testid="bulk-archive-btn">
                    <Archive className="w-3.5 h-3.5 mr-1" />Archive
                  </Button>
                  <div className="relative group">
                    <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkLoading || selectedHasOrgMembers} data-testid="bulk-delete-btn">
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                    </Button>
                    {selectedHasOrgMembers && (
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground border rounded-md px-3 py-2 text-xs shadow-lg whitespace-nowrap hidden group-hover:block z-50" data-testid="delete-disabled-tooltip">
                        Org members must be deleted from Organisation &gt; People
                      </div>
                    )}
                  </div>
                </>
              )}
              {tab === 'archived' && (
                <Button size="sm" variant="outline" onClick={handleReactivateSelected} disabled={bulkLoading} data-testid="bulk-reactivate-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />Reactivate
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Form (only on active tab) */}
        {showForm && tab === 'active' && (
          <Card className="border shadow-sm mt-4"><CardContent className="p-4 space-y-4">
            <h3 className="text-sm font-semibold">{editEntry ? `Edit: ${editEntry.name}` : 'Add Staff / Contractor'}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="h-8 text-sm" data-testid="staffing-name" /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Email</label>
                <Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="h-8 text-sm" /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Job Title</label>
                <Input value={form.job_title} onChange={e => setForm(f => ({...f, job_title: e.target.value}))} className="h-8 text-sm" /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Department</label>
                <Input value={form.department} onChange={e => setForm(f => ({...f, department: e.target.value}))} className="h-8 text-sm" /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Type</label>
                <Select value={form.employment_type} onValueChange={v => setForm(f => ({...f, employment_type: v}))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-Time</SelectItem>
                    <SelectItem value="part-time">Part-Time</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="intern">Intern</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Pay Structure</label>
                <Select value={form.pay_type} onValueChange={v => setForm(f => ({...f, pay_type: v}))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="pay-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salary">Salary (Annual)</SelectItem>
                    <SelectItem value="hourly">Hourly Rate</SelectItem>
                    <SelectItem value="bonus">Salary + Bonus (KPI)</SelectItem>
                    <SelectItem value="commission">Salary + Commission</SelectItem>
                    <SelectItem value="mixed">Mixed (Salary + Bonus + Commission)</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(form.pay_type !== 'hourly') && (
                <div><label className="text-xs text-muted-foreground mb-1 block">Annual Salary</label>
                  <Input type="number" value={form.annual_salary} onChange={e => setForm(f => ({...f, annual_salary: e.target.value}))} className="h-8 text-sm" placeholder="e.g. 85000" data-testid="annual-salary" /></div>
              )}
              {(form.pay_type === 'hourly') && (<>
                <div><label className="text-xs text-muted-foreground mb-1 block">Hourly Rate</label>
                  <Input type="number" value={form.hourly_rate} onChange={e => setForm(f => ({...f, hourly_rate: e.target.value}))} className="h-8 text-sm" placeholder="e.g. 75" /></div>
                <div><label className="text-xs text-muted-foreground mb-1 block">Hours / Week</label>
                  <Input type="number" value={form.hours_per_week} onChange={e => setForm(f => ({...f, hours_per_week: e.target.value}))} className="h-8 text-sm" placeholder="e.g. 40" /></div>
              </>)}
              {(form.pay_type === 'bonus' || form.pay_type === 'mixed') && (<>
                <div><label className="text-xs text-muted-foreground mb-1 block">Bonus Target</label>
                  <Input type="number" value={form.bonus_target} onChange={e => setForm(f => ({...f, bonus_target: e.target.value}))} className="h-8 text-sm" placeholder="e.g. 15000" /></div>
                <div><label className="text-xs text-muted-foreground mb-1 block">Bonus KPI / Criteria</label>
                  <Input value={form.bonus_kpi} onChange={e => setForm(f => ({...f, bonus_kpi: e.target.value}))} className="h-8 text-sm" placeholder="e.g. Sales target" /></div>
              </>)}
              {(form.pay_type === 'commission' || form.pay_type === 'mixed') && (
                <div><label className="text-xs text-muted-foreground mb-1 block">Commission Rate (%)</label>
                  <Input type="number" value={form.commission_rate} onChange={e => setForm(f => ({...f, commission_rate: e.target.value}))} className="h-8 text-sm" placeholder="e.g. 5" /></div>
              )}
              <div><label className="text-xs text-muted-foreground mb-1 block">Currency</label>
                <Input value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} className="h-8 text-sm" /></div>
              <div><label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="h-8 text-sm" /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveEntry} data-testid="save-staffing-btn">{editEntry ? 'Update' : 'Add'}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent></Card>
        )}

        <TabsContent value="active" className="mt-4">
          {activeTeam.length > 0 ? (
            <Card className="border-0 shadow-sm bg-card overflow-hidden">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Team & Contractors ({activeTeam.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 w-10">
                        <Checkbox checked={selected.size === activeTeam.length && activeTeam.length > 0} onCheckedChange={toggleSelectAll} data-testid="staffing-select-all" />
                      </th>
                      <th className="p-3 text-left font-medium text-xs">Name</th>
                      <th className="p-3 text-left font-medium text-xs">Role</th>
                      <th className="p-3 text-left font-medium text-xs">Dept</th>
                      <th className="p-3 text-left font-medium text-xs">Type</th>
                      <th className="p-3 text-left font-medium text-xs">Pay Structure</th>
                      <th className="p-3 text-right font-medium text-xs">Monthly</th>
                      <th className="p-3 text-right font-medium text-xs w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTeam.map((p, i) => (
                      <tr key={p.entry_id || p.user_id || i} className={cn("border-t border-border hover:bg-muted/30 group", selected.has(p.entry_id) && "bg-blue-50/50 dark:bg-blue-950/20")} data-testid={`staffing-row-${i}`}>
                        <td className="p-3">
                          <Checkbox checked={selected.has(p.entry_id)} onCheckedChange={() => toggleSelect(p.entry_id)} data-testid={`staffing-select-${i}`} />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.name || 'Unknown'}</span>
                            <Badge variant="outline" className={cn("text-[9px]", p._source === 'org' ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600")}>
                              {p._source === 'org' ? 'Org' : 'Contractor'}
                            </Badge>
                            {p.is_workspace_linked && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-600">WS</Badge>}
                          </div>
                          {p.email && <span className="text-[10px] text-muted-foreground">{p.email}</span>}
                        </td>
                        <td className="p-3 text-xs">
                          <div>{p.job_title || <span className="text-muted-foreground">-</span>}</div>
                          {p.role_history?.length > 0 && (
                            <details className="mt-0.5">
                              <summary className="text-[9px] text-muted-foreground cursor-pointer hover:text-foreground">{p.role_history.length} prev</summary>
                              <div className="mt-0.5 space-y-0.5 pl-2 border-l border-muted">
                                {[...p.role_history].reverse().map((rh, ri) => (
                                  <div key={ri} className="text-[9px] text-muted-foreground/60">{rh.role}{rh.end_date ? ` (until ${rh.end_date})` : ''}</div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{p.department || '-'}</Badge></td>
                        <td className="p-3 text-xs capitalize">{p.employment_type}</td>
                        <td className="p-3 text-xs text-muted-foreground">{getPayLabel(p)}</td>
                        <td className="p-3 text-right font-mono text-xs">{formatMoney(getMonthly(p))}</td>
                        <td className="p-3 text-right">
                          <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Edit" onClick={() => openForm(p)} data-testid={`edit-staffing-${i}`}><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Archive" onClick={async () => {
                              const r = await fetch(`${API}/finance/staffing/${workspaceId}/archive`, {
                                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ entry_ids: [p.entry_id] }),
                              });
                              if (r.ok) { toast.success('Archived'); fetchAll(); } else { toast.error('Archive failed'); }
                            }} data-testid={`archive-staffing-${i}`}><Archive className="w-3 h-3" /></Button>
                            {p._source === 'direct' && <>
                              <Button variant="ghost" size="icon" className="h-6 w-6" title="Add to People" onClick={() => promoteTopeople(p.entry_id)} data-testid={`promote-${i}`}><UserPlus className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" title="Link to existing person" onClick={() => setLinkModal(p)} data-testid={`link-${i}`}><Link2 className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" title="Remove" onClick={() => deleteEntry(p.entry_id)} data-testid={`delete-staffing-${i}`}><Trash2 className="w-3 h-3" /></Button>
                            </>}
                            {p._source === 'org' && (
                              <div className="relative group/tip">
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground/40 cursor-not-allowed" disabled data-testid={`delete-disabled-${i}`}><Trash2 className="w-3 h-3" /></Button>
                                <div className="absolute bottom-full mb-1 right-0 bg-popover text-popover-foreground border rounded px-2 py-1 text-[10px] shadow-md whitespace-nowrap hidden group-hover/tip:block z-50">
                                  Delete from Organisation &gt; People
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">No active team members or contractors</p>
              <p className="text-sm mt-1">Click &quot;Sync from People&quot; to pull from Organisation, or add contractors directly.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          {archivedTeam.length > 0 ? (
            <Card className="border-0 shadow-sm bg-card overflow-hidden">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Archived Staff & Contractors ({archivedTeam.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 w-10">
                        <Checkbox checked={selected.size === archivedTeam.length && archivedTeam.length > 0} onCheckedChange={toggleSelectAll} data-testid="archived-select-all" />
                      </th>
                      <th className="p-3 text-left font-medium text-xs">Name</th>
                      <th className="p-3 text-left font-medium text-xs">Role</th>
                      <th className="p-3 text-left font-medium text-xs">Dept</th>
                      <th className="p-3 text-left font-medium text-xs">Type</th>
                      <th className="p-3 text-left font-medium text-xs">Pay Structure</th>
                      <th className="p-3 text-right font-medium text-xs">Monthly</th>
                      <th className="p-3 text-right font-medium text-xs w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedTeam.map((p, i) => (
                      <tr key={p.entry_id || p.user_id || i} className={cn("border-t border-border hover:bg-muted/30 group opacity-70", selected.has(p.entry_id) && "bg-blue-50/50 dark:bg-blue-950/20 opacity-100")} data-testid={`archived-row-${i}`}>
                        <td className="p-3">
                          <Checkbox checked={selected.has(p.entry_id)} onCheckedChange={() => toggleSelect(p.entry_id)} data-testid={`archived-select-${i}`} />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.name || 'Unknown'}</span>
                            <Badge variant="outline" className={cn("text-[9px]", p._source === 'org' ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600")}>
                              {p._source === 'org' ? 'Org' : 'Contractor'}
                            </Badge>
                            <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500">Archived</Badge>
                          </div>
                          {p.email && <span className="text-[10px] text-muted-foreground">{p.email}</span>}
                        </td>
                        <td className="p-3 text-xs">
                          <div>{p.job_title || <span className="text-muted-foreground">-</span>}</div>
                          {p.role_history?.length > 0 && (
                            <details className="mt-0.5">
                              <summary className="text-[9px] text-muted-foreground cursor-pointer hover:text-foreground">{p.role_history.length} prev</summary>
                              <div className="mt-0.5 space-y-0.5 pl-2 border-l border-muted">
                                {[...p.role_history].reverse().map((rh, ri) => (
                                  <div key={ri} className="text-[9px] text-muted-foreground/60">{rh.role}{rh.end_date ? ` (until ${rh.end_date})` : ''}</div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{p.department || '-'}</Badge></td>
                        <td className="p-3 text-xs capitalize">{p.employment_type}</td>
                        <td className="p-3 text-xs text-muted-foreground">{getPayLabel(p)}</td>
                        <td className="p-3 text-right font-mono text-xs">{formatMoney(getMonthly(p))}</td>
                        <td className="p-3 text-right">
                          <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Reactivate" onClick={async () => {
                              const r = await fetch(`${API}/finance/staffing/${workspaceId}/reactivate`, {
                                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ entry_ids: [p.entry_id] }),
                              });
                              if (r.ok) { toast.success('Reactivated'); fetchAll(); } else { toast.error('Reactivate failed'); }
                            }} data-testid={`reactivate-${i}`}>
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <Archive className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="font-medium">No archived staff</p>
              <p className="text-sm mt-1">Archived team members and contractors will appear here.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Link to Person Modal */}
      <Dialog open={!!linkModal} onOpenChange={v => !v && setLinkModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link &quot;{linkModal?.name}&quot; to an existing person</DialogTitle>
            <DialogDescription>This will merge the contractor&apos;s pay data into the selected person&apos;s profile and remove the duplicate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {orgPeople.filter(p => p.source !== 'orphaned').map(p => (
              <button key={p.person_id || p.user_id} className="w-full flex items-center gap-2 p-2 rounded hover:bg-muted text-left text-sm" onClick={() => linkToPerson(linkModal?.entry_id, p.person_id || p.user_id)} data-testid={`link-option-${p.person_id || p.user_id}`}>
                <div className="flex-1">
                  <span className="font-medium">{p.name || p.email}</span>
                  {p.email && <span className="text-xs text-muted-foreground ml-2">{p.email}</span>}
                  {!p.has_user_account && <Badge className="ml-1 bg-slate-100 text-slate-500 text-[8px]">Person only</Badge>}
                </div>
                <Badge variant="outline" className="text-[9px]">{p.source}</Badge>
              </button>
            ))}
            {orgPeople.length === 0 && <p className="text-xs text-muted-foreground p-4 text-center">No org members found. Sync from workspace first.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Staffing Import Preview Dialog */}
      <Dialog open={showStaffImport} onOpenChange={open => { if (!open) { setShowStaffImport(false); setStaffImportPreview(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-sm">Import Staffing — Preview</DialogTitle>
            <DialogDescription>{staffImportPreview?.length || 0} records. Matches by email — existing updated, new created as contractors.</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr>
                  {staffImportPreview?.[0] && Object.keys(staffImportPreview[0]).slice(0, 6).map(h => (
                    <th key={h} className="p-2 text-left font-medium text-muted-foreground">{h}</th>
                  ))}
                  {staffImportPreview?.[0] && Object.keys(staffImportPreview[0]).length > 6 && <th className="p-2 text-muted-foreground">+{Object.keys(staffImportPreview[0]).length - 6} more</th>}
                </tr>
              </thead>
              <tbody>
                {(staffImportPreview || []).slice(0, 20).map((row, i) => (
                  <tr key={i} className="border-t hover:bg-muted/10">
                    {Object.values(row).slice(0, 6).map((v, j) => (
                      <td key={j} className="p-2 max-w-[140px] truncate">{String(v || '')}</td>
                    ))}
                    {Object.values(row).length > 6 && <td className="p-2 text-muted-foreground">...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {(staffImportPreview?.length || 0) > 20 && <div className="p-2 text-xs text-muted-foreground text-center border-t">Showing 20 of {staffImportPreview.length}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowStaffImport(false); setStaffImportPreview(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleConfirmStaffImport} disabled={staffImportLoading} data-testid="confirm-staffing-import-btn">
              {staffImportLoading ? 'Importing...' : `Import ${staffImportPreview?.length || 0} Records`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ScenarioView = ({ scenarios }) => {
  const typeColors = { base: 'bg-slate-100 text-slate-700', conservative: 'bg-amber-100 text-amber-700', aggressive: 'bg-emerald-100 text-emerald-700', raise_now: 'bg-blue-100 text-blue-700', raise_later: 'bg-violet-100 text-violet-700', no_raise: 'bg-red-100 text-red-700', custom: 'bg-stone-100 text-stone-700' };

  if (!scenarios.length) return (
    <div className="text-center py-16 text-muted-foreground">
      <Layers className="w-16 h-16 mx-auto mb-4 opacity-20" />
      <p className="font-medium">No scenarios yet</p>
      <p className="text-sm">Create scenarios to compare different financial outcomes</p>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {scenarios.map(s => (
        <Card key={s.scenario_id} className="border-0 shadow-sm bg-card hover:shadow-md transition-shadow cursor-pointer" data-testid={`scenario-card-${s.scenario_id}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Badge className={cn("text-xs", typeColors[s.scenario_type] || typeColors.custom)}>{s.scenario_type?.replace('_', ' ')}</Badge>
            </div>
            <h3 className="font-semibold text-sm">{s.name}</h3>
            {s.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const ReportsView = ({ expenses, income, dashboard }) => {
  const [activeReport, setActiveReport] = useState(null);
  const [dateRange, setDateRange] = useState('year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Date filtering
  const getDateBounds = () => {
    const now = new Date();
    let from, to = now.toISOString().slice(0, 10);
    if (dateRange === 'month') { from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10); }
    else if (dateRange === '3months') { from = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10); }
    else if (dateRange === '6months') { from = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().slice(0, 10); }
    else if (dateRange === 'year') { from = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10); }
    else if (dateRange === 'all') { from = '2000-01-01'; }
    else if (dateRange === 'custom') { from = customFrom || '2000-01-01'; to = customTo || to; }
    else { from = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10); }
    return { from, to };
  };

  const { from: dFrom, to: dTo } = getDateBounds();
  const filteredExpenses = useMemo(() => (expenses || []).filter(e => {
    const d = e.date_of_expense || e.created_at || '';
    return d >= dFrom && d <= dTo + 'Z';
  }), [expenses, dFrom, dTo]);
  const filteredIncome = useMemo(() => (income || []).filter(i => {
    const d = i.date_received || i.created_at || '';
    return d >= dFrom && d <= dTo + 'Z';
  }), [income, dFrom, dTo]);

  const totalExpenses = filteredExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalIncome = filteredIncome.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const netIncome = totalIncome - totalExpenses;

  // Group expenses by category
  const expByCategory = useMemo(() => {
    const m = {};
    filteredExpenses.forEach(e => {
      const cat = e.expense_type || e.category || 'Uncategorised';
      m[cat] = (m[cat] || 0) + (parseFloat(e.amount) || 0);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [filteredExpenses]);

  // Group income by type
  const incByType = useMemo(() => {
    const m = {};
    filteredIncome.forEach(i => {
      const t = i.income_source_type || i.type || 'Uncategorised';
      m[t] = (m[t] || 0) + (parseFloat(i.amount) || 0);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [filteredIncome]);

  // Monthly P/L data
  const monthlyPL = useMemo(() => {
    const m = {};
    filteredExpenses.forEach(e => {
      const d = (e.date_of_expense || e.created_at || '').slice(0, 7);
      if (!d) return;
      if (!m[d]) m[d] = { month: d, income: 0, expenses: 0 };
      m[d].expenses += parseFloat(e.amount) || 0;
    });
    filteredIncome.forEach(i => {
      const d = (i.date_received || i.created_at || '').slice(0, 7);
      if (!d) return;
      if (!m[d]) m[d] = { month: d, income: 0, expenses: 0 };
      m[d].income += parseFloat(i.amount) || 0;
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredExpenses, filteredIncome]);

  const fmtMoney = (v) => {
    if (v == null) return '$0';
    const abs = Math.abs(v);
    return `${v < 0 ? '-' : ''}$${abs >= 1000 ? abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : abs.toFixed(2)}`;
  };

  const exportCSV = (title, rows) => {
    const header = Object.keys(rows[0] || {}).join(',');
    const body = rows.map(r => Object.values(r).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${title}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => { window.print(); };

  const reports = [
    { id: 'income-statement', title: 'Income Statement', desc: 'Revenue, expenses, and net income', icon: BarChart3 },
    { id: 'cash-flow', title: 'Cash Flow Statement', desc: 'Operating cash flows by period', icon: Activity },
    { id: 'balance-sheet', title: 'Balance Sheet', desc: 'Assets, liabilities, and equity snapshot', icon: Scale },
  ];

  // Date range selector bar
  const DateBar = () => (
    <div className="flex items-center gap-2 flex-wrap" data-testid="report-date-filter">
      {[{v:'month',l:'This Month'},{v:'3months',l:'3 Months'},{v:'6months',l:'6 Months'},{v:'year',l:'This Year'},{v:'all',l:'All Time'},{v:'custom',l:'Custom'}].map(r => (
        <button key={r.v} onClick={() => setDateRange(r.v)}
          className={cn("text-[11px] px-2.5 py-1 rounded-md border transition-colors",
            dateRange === r.v ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-accent"
          )} data-testid={`date-range-${r.v}`}>{r.l}</button>
      ))}
      {dateRange === 'custom' && (<>
        <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-7 text-xs w-32" />
        <span className="text-xs text-muted-foreground">to</span>
        <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-7 text-xs w-32" />
      </>)}
    </div>
  );

  if (!activeReport) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Financial Reports</h2>
            <p className="text-xs text-muted-foreground">Generate, view, and export financial statements</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reports.map(r => (
            <Card key={r.id} className="border shadow-sm bg-card hover:shadow-md transition-shadow cursor-pointer group" onClick={() => setActiveReport(r.id)} data-testid={`report-card-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors"><r.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" /></div>
                  <div>
                    <h3 className="font-semibold text-sm">{r.title}</h3>
                    <p className="text-[10px] text-muted-foreground">{r.desc}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full text-xs">
                  <FileText className="w-3 h-3 mr-1.5" />View Report
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ─── Income Statement ───
  if (activeReport === 'income-statement') {
    return (
      <div className="space-y-4 print:space-y-2" data-testid="income-statement-report">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveReport(null)} className="text-xs"><ChevronLeft className="w-3.5 h-3.5 mr-1" />Back</Button>
            <h2 className="text-lg font-semibold">Income Statement</h2>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCSV('income-statement', [
              ...incByType.map(([t, a]) => ({ Category: 'Revenue', Item: t, Amount: a.toFixed(2) })),
              { Category: 'Revenue', Item: 'TOTAL REVENUE', Amount: totalIncome.toFixed(2) },
              ...expByCategory.map(([c, a]) => ({ Category: 'Expenses', Item: c, Amount: a.toFixed(2) })),
              { Category: 'Expenses', Item: 'TOTAL EXPENSES', Amount: totalExpenses.toFixed(2) },
              { Category: 'Net', Item: 'NET INCOME', Amount: netIncome.toFixed(2) },
            ])} data-testid="export-csv-btn"><Download className="w-3 h-3 mr-1" />CSV</Button>
            <Button size="sm" variant="outline" onClick={printReport} data-testid="export-pdf-btn"><Printer className="w-3 h-3 mr-1" />Print / PDF</Button>
          </div>
        </div>
        <DateBar />
        <Card className="border shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="income-statement-table">
              <thead className="bg-muted/30">
                <tr><th colSpan={2} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">Income Statement — {dFrom} to {dTo}</th></tr>
              </thead>
              <tbody className="divide-y">
                <tr className="bg-emerald-50/50 dark:bg-emerald-950/20"><td className="px-4 py-2 font-semibold text-xs text-emerald-700 dark:text-emerald-400" colSpan={2}>Revenue</td></tr>
                {incByType.map(([t, a]) => (
                  <tr key={t} className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">{t}</td><td className="px-4 py-2 text-xs text-right font-mono">{fmtMoney(a)}</td></tr>
                ))}
                {incByType.length === 0 && <tr><td className="px-4 py-2 text-xs pl-8 text-muted-foreground" colSpan={2}>No revenue recorded</td></tr>}
                <tr className="bg-emerald-100/50 dark:bg-emerald-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Total Revenue</td><td className="px-4 py-2.5 text-xs text-right font-mono text-emerald-700 dark:text-emerald-400">{fmtMoney(totalIncome)}</td></tr>

                <tr className="bg-red-50/50 dark:bg-red-950/20"><td className="px-4 py-2 font-semibold text-xs text-red-700 dark:text-red-400" colSpan={2}>Expenses</td></tr>
                {expByCategory.map(([c, a]) => (
                  <tr key={c} className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">{c}</td><td className="px-4 py-2 text-xs text-right font-mono">{fmtMoney(a)}</td></tr>
                ))}
                {expByCategory.length === 0 && <tr><td className="px-4 py-2 text-xs pl-8 text-muted-foreground" colSpan={2}>No expenses recorded</td></tr>}
                <tr className="bg-red-100/50 dark:bg-red-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Total Expenses</td><td className="px-4 py-2.5 text-xs text-right font-mono text-red-700 dark:text-red-400">{fmtMoney(totalExpenses)}</td></tr>

                <tr className={cn("font-bold text-sm", netIncome >= 0 ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-red-100 dark:bg-red-950/40")}>
                  <td className="px-4 py-3">Net Income / (Loss)</td>
                  <td className={cn("px-4 py-3 text-right font-mono", netIncome >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400")}>{fmtMoney(netIncome)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
        {/* Monthly Trend */}
        {monthlyPL.length > 0 && (
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-xs font-semibold mb-3">Monthly P/L Trend</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/20"><tr>
                    <th className="text-left p-2 font-medium">Month</th>
                    <th className="text-right p-2 font-medium text-emerald-600">Income</th>
                    <th className="text-right p-2 font-medium text-red-600">Expenses</th>
                    <th className="text-right p-2 font-medium">Net</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {monthlyPL.map(m => (
                      <tr key={m.month} className="hover:bg-accent/30">
                        <td className="p-2 font-medium">{m.month}</td>
                        <td className="p-2 text-right font-mono text-emerald-600">{fmtMoney(m.income)}</td>
                        <td className="p-2 text-right font-mono text-red-600">{fmtMoney(m.expenses)}</td>
                        <td className={cn("p-2 text-right font-mono font-semibold", m.income - m.expenses >= 0 ? "text-emerald-600" : "text-red-600")}>{fmtMoney(m.income - m.expenses)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ─── Cash Flow Statement ───
  if (activeReport === 'cash-flow') {
    const operatingIn = filteredIncome.filter(i => {
      const t = (i.income_source_type || '').toLowerCase();
      return !t.includes('invest') && !t.includes('fundrais') && !t.includes('loan') && !t.includes('grant');
    }).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const operatingOut = filteredExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const netOperating = operatingIn - operatingOut;

    const financingIn = filteredIncome.filter(i => {
      const t = (i.income_source_type || '').toLowerCase();
      return t.includes('invest') || t.includes('fundrais') || t.includes('loan') || t.includes('grant');
    }).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

    const netCashChange = netOperating + financingIn;
    const allTimeIncome = (income || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const allTimeExpenses = (expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const cashOnHand = allTimeIncome - allTimeExpenses;

    return (
      <div className="space-y-4 print:space-y-2" data-testid="cash-flow-report">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveReport(null)} className="text-xs"><ChevronLeft className="w-3.5 h-3.5 mr-1" />Back</Button>
            <h2 className="text-lg font-semibold">Cash Flow Statement</h2>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCSV('cash-flow', [
              { Section: 'Operating', Item: 'Revenue Received', Amount: operatingIn.toFixed(2) },
              { Section: 'Operating', Item: 'Operating Expenses', Amount: (-operatingOut).toFixed(2) },
              { Section: 'Operating', Item: 'Net Operating Cash Flow', Amount: netOperating.toFixed(2) },
              { Section: 'Financing', Item: 'Investment / Fundraising', Amount: financingIn.toFixed(2) },
              { Section: 'Total', Item: 'Net Cash Change', Amount: netCashChange.toFixed(2) },
              { Section: 'Position', Item: 'Cash on Hand', Amount: cashOnHand.toFixed(2) },
            ])} data-testid="export-csv-btn"><Download className="w-3 h-3 mr-1" />CSV</Button>
            <Button size="sm" variant="outline" onClick={printReport} data-testid="export-pdf-btn"><Printer className="w-3 h-3 mr-1" />Print / PDF</Button>
          </div>
        </div>
        <DateBar />
        <Card className="border shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="cash-flow-table">
              <thead className="bg-muted/30">
                <tr><th colSpan={2} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">Cash Flow Statement — {dFrom} to {dTo}</th></tr>
              </thead>
              <tbody className="divide-y">
                <tr className="bg-blue-50/50 dark:bg-blue-950/20"><td className="px-4 py-2 font-semibold text-xs text-blue-700 dark:text-blue-400" colSpan={2}>Operating Activities</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Revenue received</td><td className="px-4 py-2 text-xs text-right font-mono text-emerald-600">{fmtMoney(operatingIn)}</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Operating expenses paid</td><td className="px-4 py-2 text-xs text-right font-mono text-red-600">({fmtMoney(operatingOut)})</td></tr>
                <tr className="bg-blue-100/50 dark:bg-blue-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Net Operating Cash Flow</td><td className={cn("px-4 py-2.5 text-xs text-right font-mono", netOperating >= 0 ? "text-emerald-600" : "text-red-600")}>{fmtMoney(netOperating)}</td></tr>

                <tr className="bg-violet-50/50 dark:bg-violet-950/20"><td className="px-4 py-2 font-semibold text-xs text-violet-700 dark:text-violet-400" colSpan={2}>Financing Activities</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Investment / fundraising proceeds</td><td className="px-4 py-2 text-xs text-right font-mono text-emerald-600">{fmtMoney(financingIn)}</td></tr>
                <tr className="bg-violet-100/50 dark:bg-violet-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Net Financing Cash Flow</td><td className="px-4 py-2.5 text-xs text-right font-mono text-emerald-600">{fmtMoney(financingIn)}</td></tr>

                <tr className={cn("font-bold text-sm", netCashChange >= 0 ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-red-100 dark:bg-red-950/40")}>
                  <td className="px-4 py-3">Net Change in Cash</td>
                  <td className={cn("px-4 py-3 text-right font-mono", netCashChange >= 0 ? "text-emerald-700" : "text-red-700")}>{fmtMoney(netCashChange)}</td>
                </tr>
                <tr className="bg-slate-100 dark:bg-slate-900 font-bold text-sm">
                  <td className="px-4 py-3">Cash on Hand (all time)</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(cashOnHand)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Balance Sheet ───
  if (activeReport === 'balance-sheet') {
    const allTimeIncome = (income || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const allTimeExpenses = (expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const cashOnHand = allTimeIncome - allTimeExpenses;
    const pendingIncome = (income || []).filter(i => i.payment_status === 'Pending' || i.payment_status === 'Invoiced').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const retainedEarnings = allTimeIncome - allTimeExpenses;

    return (
      <div className="space-y-4 print:space-y-2" data-testid="balance-sheet-report">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveReport(null)} className="text-xs"><ChevronLeft className="w-3.5 h-3.5 mr-1" />Back</Button>
            <h2 className="text-lg font-semibold">Balance Sheet</h2>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCSV('balance-sheet', [
              { Section: 'Assets', Item: 'Cash on Hand', Amount: cashOnHand.toFixed(2) },
              { Section: 'Assets', Item: 'Accounts Receivable', Amount: pendingIncome.toFixed(2) },
              { Section: 'Assets', Item: 'TOTAL ASSETS', Amount: (cashOnHand + pendingIncome).toFixed(2) },
              { Section: 'Equity', Item: 'Retained Earnings', Amount: retainedEarnings.toFixed(2) },
              { Section: 'Equity', Item: 'TOTAL EQUITY', Amount: retainedEarnings.toFixed(2) },
            ])} data-testid="export-csv-btn"><Download className="w-3 h-3 mr-1" />CSV</Button>
            <Button size="sm" variant="outline" onClick={printReport} data-testid="export-pdf-btn"><Printer className="w-3 h-3 mr-1" />Print / PDF</Button>
          </div>
        </div>
        <Card className="border shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="balance-sheet-table">
              <thead className="bg-muted/30">
                <tr><th colSpan={2} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">Balance Sheet — as at {dTo}</th></tr>
              </thead>
              <tbody className="divide-y">
                <tr className="bg-emerald-50/50 dark:bg-emerald-950/20"><td className="px-4 py-2 font-semibold text-xs text-emerald-700 dark:text-emerald-400" colSpan={2}>Assets</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Cash on Hand</td><td className="px-4 py-2 text-xs text-right font-mono">{fmtMoney(cashOnHand)}</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Accounts Receivable (Pending Income)</td><td className="px-4 py-2 text-xs text-right font-mono">{fmtMoney(pendingIncome)}</td></tr>
                <tr className="bg-emerald-100/50 dark:bg-emerald-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Total Assets</td><td className="px-4 py-2.5 text-xs text-right font-mono">{fmtMoney(cashOnHand + pendingIncome)}</td></tr>

                <tr className="bg-blue-50/50 dark:bg-blue-950/20"><td className="px-4 py-2 font-semibold text-xs text-blue-700 dark:text-blue-400" colSpan={2}>Equity</td></tr>
                <tr className="hover:bg-accent/30"><td className="px-4 py-2 text-xs pl-8">Retained Earnings</td><td className="px-4 py-2 text-xs text-right font-mono">{fmtMoney(retainedEarnings)}</td></tr>
                <tr className="bg-blue-100/50 dark:bg-blue-950/30 font-semibold"><td className="px-4 py-2.5 text-xs">Total Equity</td><td className="px-4 py-2.5 text-xs text-right font-mono">{fmtMoney(retainedEarnings)}</td></tr>

                <tr className="bg-slate-100 dark:bg-slate-900 font-bold text-sm">
                  <td className="px-4 py-3">Assets = Equity</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(cashOnHand + pendingIncome)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

// ==================== INVOICES TAB ====================
function InvoicesTab({ income, workspaceId, API, fetchIncome }) {
  const invoiceItems = useMemo(() => {
    return (income || []).filter(i => i.invoice_number).sort((a, b) => {
      const da = a.invoice_issue_date || a.date_received || '';
      const db = b.invoice_issue_date || b.date_received || '';
      return db.localeCompare(da);
    });
  }, [income]);

  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);

  const handleFileUpload = async (incomeId, file) => {
    if (!file || !workspaceId) return;
    setUploadingId(incomeId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${API}/finance/income/${workspaceId}/${incomeId}/upload-invoice`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      if (r.ok) {
        const res = await r.json();
        toast.success('Invoice file uploaded');
        fetchIncome();
      } else { toast.error('Upload failed'); }
    } catch { toast.error('Upload failed'); }
    setUploadingId(null);
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Invoices</h3>
          <p className="text-[10px] text-muted-foreground">All income records with invoice numbers. Upload files or add links.</p>
        </div>
      </div>
      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="bg-card border-b sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Invoice #</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Date Issued</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Status</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Source</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Item</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs">Amount</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Invoice Link</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs w-32">File</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoiceItems.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-xs">No invoices found. Add an invoice number to income records to see them here.</td></tr>
            ) : invoiceItems.map(item => (
              <tr key={item.income_id} className="hover:bg-accent/50 group" data-testid={`invoice-row-${item.income_id}`}>
                <td className="px-3 py-2.5 text-xs font-mono font-medium">{item.invoice_number}</td>
                <td className="px-3 py-2.5 text-xs">{item.invoice_issue_date || item.date_received || '—'}</td>
                <td className="px-3 py-2.5 text-xs">
                  {(() => {
                    const s = item.invoice_status || item.payment_status || '';
                    const colors = { Pending: 'bg-yellow-100 text-yellow-800', Invoiced: 'bg-blue-100 text-blue-800', Refunded: 'bg-red-100 text-red-800', Paid: 'bg-green-100 text-green-800' };
                    return s ? <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", colors[s] || 'bg-muted')}>{s}</span> : '—';
                  })()}
                </td>
                <td className="px-3 py-2.5 text-xs">{item.source_of_funds || '—'}</td>
                <td className="px-3 py-2.5 text-xs truncate max-w-[200px]">{item.income_item_summary || '—'}</td>
                <td className="px-3 py-2.5 text-xs text-right font-mono">{formatMoney(item.amount_usd)}</td>
                <td className="px-3 py-2.5 text-xs">
                  {item.receipt_link ? <a href={item.receipt_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline"><Link2 className="w-3 h-3 inline mr-1" />View</a> : '—'}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {item.invoice_file_url ? (
                    <a href={item.invoice_file_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-[10px]"><FileText className="w-3 h-3 inline mr-1" />File</a>
                  ) : (
                    <>
                      <input type="file" className="hidden" ref={el => { if (uploadingId === item.income_id) fileRef.current = el; }}
                        onChange={e => handleFileUpload(item.income_id, e.target.files?.[0])} />
                      <Button variant="ghost" size="sm" className="h-6 text-[10px]"
                        disabled={uploadingId === item.income_id}
                        onClick={() => {
                          setUploadingId(item.income_id);
                          setTimeout(() => fileRef.current?.click(), 50);
                        }}
                        data-testid={`upload-invoice-${item.income_id}`}>
                        {uploadingId === item.income_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" />Upload</>}
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== SOURCE OF FUNDS TAB (CRM-style) ====================
function SourceOfFundsTab({ items, activeWorkspace, API, onSave, onDelete, form, setForm, editing, setEditing, showForm, setShowForm }) {
  const fields = [
    { key: 'name', label: 'Name', required: true },
    { key: 'type', label: 'Type', options: ['Company', 'Person', 'Government', 'Fund', 'Other'] },
    { key: 'contact_name', label: 'Contact Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'website', label: 'Website' },
    { key: 'address', label: 'Address' },
    { key: 'notes', label: 'Notes' },
  ];

  return (
    <div className="space-y-4" data-testid="source-of-funds-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Source of Funds</h3>
          <p className="text-[10px] text-muted-foreground">Manage companies, people, and entities that provide income. Used in the "Source of Funds" column.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ type: 'Company' }); setShowForm(true); }} data-testid="add-source-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add Source
        </Button>
      </div>

      {showForm && (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {fields.map(f => (
              <div key={f.key}>
                <label className="text-xs text-muted-foreground mb-1 block">{f.label}{f.required ? ' *' : ''}</label>
                {f.options ? (
                  <Select value={form[f.key] || f.options[0]} onValueChange={v => setForm(p => ({ ...p, [f.key]: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="h-8 text-xs" data-testid={`sof-field-${f.key}`} />
                )}
              </div>
            ))}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Investor?</label>
              <div className="flex items-center gap-2 h-8">
                <Switch checked={form.is_investor || false} onCheckedChange={v => setForm(p => ({ ...p, is_investor: v }))} data-testid="sof-field-investor" />
                <span className="text-xs text-muted-foreground">{form.is_investor ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave(form, editing?.source_id)} data-testid="save-source-btn">{editing ? 'Update' : 'Create'}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="source-of-funds-table">
          <thead className="bg-card border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Name</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Type</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Contact</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Email</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Phone</th>
              <th className="text-center px-3 py-2 font-medium text-muted-foreground text-xs">Investor</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(items || []).length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground text-xs">No sources yet.</td></tr>
            ) : (items || []).map(item => (
              <tr key={item.source_id} className="hover:bg-accent/50 group" data-testid={`sof-row-${item.source_id}`}>
                <td className="px-3 py-2.5 text-xs font-medium">{item.name}</td>
                <td className="px-3 py-2.5 text-xs"><Badge variant="outline" className="text-[10px]">{item.type || 'Company'}</Badge></td>
                <td className="px-3 py-2.5 text-xs">{item.contact_name || '—'}</td>
                <td className="px-3 py-2.5 text-xs">{item.email || '—'}</td>
                <td className="px-3 py-2.5 text-xs">{item.phone || '—'}</td>
                <td className="px-3 py-2.5 text-xs text-center">
                  {item.is_investor ? <Badge className="text-[9px] bg-green-600">Investor</Badge> : <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(item); setForm(item); setShowForm(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(item.source_id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== INCOME CATEGORIES TAB (Types & Classifications with codes) ====================
function IncomeCategoriesTab({ refIncomeCategories, onSave, onDelete, activeWorkspace, fetchRefData, form, setForm, editing, setEditing, showForm, setShowForm }) {
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [formMode, setFormMode] = useState('type');
  const [importing, setImporting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const fileInputRef = useRef(null);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineForm, setInlineForm] = useState({});

  const types = useMemo(() => {
    const allCats = refIncomeCategories || [];
    const parents = allCats.filter(c => !c.parent_code)
      .sort((a, b) => {
        // Sort by code alphabetically (A, B, C...)
        return (a.code || a.name || '').localeCompare(b.code || b.name || '', undefined, { sensitivity: 'base' });
      });
    const childMap = {};
    for (const c of allCats) {
      if (c.parent_code) {
        if (!childMap[c.parent_code]) childMap[c.parent_code] = [];
        childMap[c.parent_code].push(c);
      }
    }
    for (const key in childMap) {
      childMap[key].sort((a, b) => {
        return (a.code || a.name || '').localeCompare(b.code || b.name || '', undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return parents.map(p => ({
      ...p,
      children: (childMap[p.name] || []).map(cls => ({
        ...cls,
        markets: childMap[cls.name] || [],
      })),
    }));
  }, [refIncomeCategories]);

  const toggleExpand = (name) => {
    setExpandedTypes(prev => { const next = new Set(prev); if (next.has(name)) next.delete(name); else next.add(name); return next; });
  };

  const downloadTemplate = () => {
    const header = 'Income Type,Type Code,Income Classification,Sub-Classification (Market)';
    const example = [
      'Client,A,TopSchool Pilot Income,',
      'Client,A,TopSchool Enterprise Income,',
      'Client,A,TopSchool License & API Income,',
      'Client,A,TopSchool Partner Income,',
      'Client,A,B2C TopTeacher Income,',
      'Client,A,B2C TopParent Income,',
      'Client,A,Training Income,',
      'Grant,B,Grants/CSR Income,',
      'Other,C,Expense Reimbursement,',
      'Client,A,Consulting,South Africa',
      'Client,A,Consulting,UK',
      'Other,C,Directors Loans,',
      'Investment,D,Investment,',
      'Refund,E,Refund,',
      'Interest,F,Interest,',
      'Royalties,G,Royalties,',
      'Other,C,Other,',
    ];
    const csv = [header, ...example].join('\r\n') + '\r\n';
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'income_types_classifications_template.csv';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const exportCSV = () => {
    const header = 'Income Type,Type Code,Income Classification,Classification Code,Sub-Classification (Market),Market Code';
    const rows = [];
    for (const t of types) {
      if (t.children.length === 0) {
        rows.push(`${t.name},${t.code || ''},,,,,`);
      } else {
        for (const cls of t.children) {
          if (cls.markets.length === 0) {
            rows.push(`${t.name},${t.code || ''},${cls.name},${cls.code || ''},,`);
          } else {
            for (const mkt of cls.markets) {
              rows.push(`${t.name},${t.code || ''},${cls.name},${cls.code || ''},${mkt.name},${mkt.code || ''}`);
            }
          }
        }
      }
    }
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    const csv = [header, ...rows].join('\r\n') + '\r\n';
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'income_types_classifications.csv';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean).slice(1);
      const rows = lines.map(line => {
        const parts = line.split(',').map(s => s.trim());
        return { type: parts[0] || '', type_code: parts[1] || '', sub_type: parts[2] || '', sub_type_code: parts[3] || '' };
      }).filter(r => r.type || r.sub_type);
      if (rows.length === 0) { toast.error('No valid rows'); setImporting(false); return; }
      const wsId = activeWorkspace?.workspace_id || '';
      const r = await fetch(`/api/finance/ref/${wsId}/income-categories/bulk-import`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      if (r.ok) { const d = await r.json(); toast.success(d.message); if (fetchRefData) fetchRefData(); }
      else toast.error('Import failed');
    } catch { toast.error('Error reading file'); }
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4" data-testid="income-categories-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Income Types & Classifications</h3>
          <p className="text-[10px] text-muted-foreground">Manage income types, their classifications with codes, and market sub-classifications.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setShowGuide(true)} data-testid="import-guide-btn">
            <HelpCircle className="w-3.5 h-3.5 mr-1" />Guide
          </Button>
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" />Template
          </Button>
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={types.length === 0} data-testid="export-categories-btn">
            <Upload className="w-3.5 h-3.5 mr-1" />Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-csv-btn">
            <Upload className="w-3.5 h-3.5 mr-1" />{importing ? 'Importing...' : 'Import CSV'}
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
          <Button size="sm" onClick={() => {
            // Auto-generate code: next letter for types
            const usedCodes = types.map(t => t.code || '').filter(Boolean);
            const nextLetter = (() => {
              for (let i = 0; i < 26; i++) {
                const l = String.fromCharCode(65 + i);
                if (!usedCodes.includes(l)) return l;
              }
              return '';
            })();
            setEditing(null); setForm({ name: '', code: nextLetter, parent_code: '', description: '' }); setFormMode('type'); setShowForm(true);
          }}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Type
          </Button>
        </div>
      </div>

      {/* Import/Export Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGuide(false)}>
          <div className="bg-card border rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="import-guide-modal">
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold">Import / Export Guide</h2>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowGuide(false)}><X className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">How the hierarchy works</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">Income categories have three levels. Each level maps to a column in the CSV:</p>
                  <div className="bg-muted/40 rounded-lg p-4 space-y-2 border">
                    <div className="flex items-center gap-2">
                      <span className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded text-[10px] font-bold">Level 1</span>
                      <span className="text-xs font-semibold">Income Type</span>
                      <span className="text-[10px] text-muted-foreground">— Broad category (Client, Investment, Grant, etc.)</span>
                    </div>
                    <div className="flex items-center gap-2 pl-6">
                      <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-[10px] font-bold">Level 2</span>
                      <span className="text-xs font-semibold">Income Classification</span>
                      <span className="text-[10px] text-muted-foreground">— Specific income stream with a code (e.g. 01 — Pilot Income)</span>
                    </div>
                    <div className="flex items-center gap-2 pl-12">
                      <span className="bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded text-[10px] font-bold">Level 3</span>
                      <span className="text-xs font-semibold">Market</span>
                      <span className="text-[10px] text-muted-foreground">— Optional geographic or segment breakdown</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">CSV format</h3>
                  <p className="text-xs text-muted-foreground mb-2">4 columns, comma-separated:</p>
                  <div className="bg-zinc-900 dark:bg-zinc-950 text-zinc-100 rounded-lg p-3 font-mono text-[11px] leading-relaxed overflow-x-auto">
                    <div className="text-zinc-500">Income Type,Classification Code,Income Classification,Sub-Classification (Market)</div>
                    <div>Client,<span className="text-blue-400">01</span>,TopSchool Pilot Income,</div>
                    <div>Client,<span className="text-blue-400">10</span>,Consulting,<span className="text-rose-400">South Africa</span></div>
                    <div>Client,<span className="text-blue-400">10</span>,Consulting,<span className="text-rose-400">UK</span></div>
                    <div>Investment,<span className="text-blue-400">12</span>,Investment,</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">Rules</h3>
                  <ul className="text-xs text-muted-foreground space-y-1.5">
                    <li className="flex gap-2"><span className="text-green-500 font-bold shrink-0">1.</span><span><strong>Income Type</strong> (col 1) is required. Repeated types create one entry.</span></li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold shrink-0">2.</span><span><strong>Classification Code</strong> (col 2) is the numeric code shown in ledger and P&L forecast.</span></li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold shrink-0">3.</span><span><strong>Income Classification</strong> (col 3) is linked to the type in col 1.</span></li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold shrink-0">4.</span><span><strong>Market</strong> (col 4) is optional. For multiple markets per classification, use one row per market.</span></li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold shrink-0">5.</span><span><strong>Safe to re-import:</strong> duplicates are skipped. You can add new rows and re-import without data loss.</span></li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">Steps</h3>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal pl-4">
                    <li>Click <strong>Download &gt; Blank Template</strong> to get a CSV pre-filled with example rows.</li>
                    <li>Edit the CSV in Excel or Google Sheets. Add, remove, or change rows.</li>
                    <li>Save as CSV (UTF-8, comma-separated).</li>
                    <li>Click <strong>Import CSV</strong> and select your file.</li>
                    <li>New entries are created; existing ones are skipped.</li>
                    <li>Use <strong>Download &gt; Export Current Data</strong> to get a CSV of what you have now.</li>
                  </ol>
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button size="sm" onClick={() => { downloadTemplate(); setShowGuide(false); }}><Download className="w-3.5 h-3.5 mr-1" />Download Template</Button>
                <Button size="sm" variant="outline" onClick={() => setShowGuide(false)}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline editing replaces the top form for edits */}
      {showForm && !editing && (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <h4 className="text-xs font-semibold text-muted-foreground">Create {formMode === 'type' ? 'Income Type' : 'Income Classification'}</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
              <Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Code</label>
              <Input value={form.code || ''} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="Auto-generated" />
            </div>
            {formMode === 'sub' && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Parent</label>
                <Input value={form.parent_code || ''} readOnly className="bg-muted" />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSave(form)} data-testid="save-income-cat-btn">Create</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="income-categories-table">
          <thead className="bg-card border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs w-12">Code</th>
              <th className="text-left px-3 py-2 font-medium text-muted-foreground text-xs">Income Type / Income Classification</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground text-xs w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {types.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-muted-foreground text-xs">No income types yet. Add types and their classifications.</td></tr>
            ) : types.map(t => {
              const isTypeEditing = inlineEditId === t.category_id;
              return (
              <React.Fragment key={t.category_id || t.name}>
                <tr className={cn("bg-muted/30 hover:bg-muted/50 cursor-pointer group", isTypeEditing && "bg-blue-50/50 dark:bg-blue-950/20")} onClick={() => !isTypeEditing && toggleExpand(t.name)}>
                  <td className="px-3 py-2.5 text-xs font-mono font-bold">
                    {isTypeEditing ? <Input value={inlineForm.code || ''} onChange={e => setInlineForm(p => ({...p, code: e.target.value}))} className="h-7 text-xs w-16 font-mono" onClick={e => e.stopPropagation()} /> : (t.code || '—')}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-semibold flex items-center gap-2">
                    {!isTypeEditing && <ChevronRight className={cn("w-3.5 h-3.5 transition-transform", expandedTypes.has(t.name) && "rotate-90")} />}
                    {isTypeEditing ? <Input value={inlineForm.name || ''} onChange={e => setInlineForm(p => ({...p, name: e.target.value}))} className="h-7 text-xs flex-1" autoFocus onClick={e => e.stopPropagation()} data-testid="inline-edit-name" /> : t.name}
                    {!isTypeEditing && <Badge variant="outline" className="text-[9px] ml-1">{t.children.length} classifications</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                    {isTypeEditing ? (
                      <div className="flex gap-0.5 justify-end">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => { onSave(inlineForm, t.category_id); setInlineEditId(null); }} data-testid={`save-inline-type`}><Check className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setInlineEditId(null)} data-testid={`cancel-inline-type`}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    ) : (
                    <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => {
                        e.stopPropagation();
                        const typeCode = t.code || '';
                        const existingCodes = t.children.map(c => c.code || '').filter(Boolean);
                        let nextNum = 1;
                        while (existingCodes.includes(`${typeCode}${nextNum}`)) nextNum++;
                        const autoCode = typeCode ? `${typeCode}${nextNum}` : '';
                        setEditing(null); setForm({ name: '', code: autoCode, parent_code: t.name }); setFormMode('sub'); setShowForm(true); setExpandedTypes(prev => new Set([...prev, t.name]));
                      }} title="Add classification"><Plus className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setInlineEditId(t.category_id); setInlineForm({ name: t.name, code: t.code || '', parent_code: '' }); }}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={e => { e.stopPropagation(); onDelete(t.category_id); }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    )}
                  </td>
                </tr>
                {expandedTypes.has(t.name) && t.children.map(sub => {
                  const isSubEditing = inlineEditId === sub.category_id;
                  return (
                  <React.Fragment key={sub.category_id || sub.name}>
                    <tr className={cn("hover:bg-accent/50 group cursor-pointer", isSubEditing && "bg-blue-50/50 dark:bg-blue-950/20")} onClick={() => !isSubEditing && toggleExpand(`${t.name}::${sub.name}`)}>
                      <td className="px-3 py-2 text-xs font-mono text-muted-foreground pl-8">
                        {isSubEditing ? <Input value={inlineForm.code || ''} onChange={e => setInlineForm(p => ({...p, code: e.target.value}))} className="h-7 text-xs w-16 font-mono" onClick={e => e.stopPropagation()} /> : (sub.code || '—')}
                      </td>
                      <td className="px-3 py-2 text-xs pl-10 flex items-center gap-2">
                        {!isSubEditing && sub.markets.length > 0 && <ChevronRight className={cn("w-3 h-3 transition-transform", expandedTypes.has(`${t.name}::${sub.name}`) && "rotate-90")} />}
                        {isSubEditing ? <Input value={inlineForm.name || ''} onChange={e => setInlineForm(p => ({...p, name: e.target.value}))} className="h-7 text-xs flex-1" autoFocus onClick={e => e.stopPropagation()} data-testid="inline-edit-cls-name" /> : sub.name}
                        {!isSubEditing && sub.markets.length > 0 && <Badge variant="outline" className="text-[8px]">{sub.markets.length} markets</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                        {isSubEditing ? (
                          <div className="flex gap-0.5 justify-end">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => { onSave(inlineForm, sub.category_id); setInlineEditId(null); }} data-testid={`save-inline-cls`}><Check className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setInlineEditId(null)}><X className="w-3.5 h-3.5" /></Button>
                          </div>
                        ) : (
                        <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => {
                            e.stopPropagation();
                            const clsCode = sub.code || '';
                            const existingMktCodes = sub.markets.map(m => m.code || '').filter(Boolean);
                            const baseForMkt = clsCode.match(/^([A-Z])(\d+)$/i)
                              ? `${clsCode[0]}.${clsCode.slice(1)}`
                              : clsCode;
                            let nextMktNum = 1;
                            while (existingMktCodes.includes(`${baseForMkt}.${nextMktNum}`)) nextMktNum++;
                            const autoMktCode = baseForMkt ? `${baseForMkt}.${nextMktNum}` : '';
                            setEditing(null); setForm({ name: '', code: autoMktCode, parent_code: sub.name }); setFormMode('sub'); setShowForm(true); setExpandedTypes(prev => new Set([...prev, `${t.name}::${sub.name}`]));
                          }} title="Add market/sub-classification"><Plus className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setInlineEditId(sub.category_id); setInlineForm({ name: sub.name, code: sub.code || '', parent_code: sub.parent_code || '' }); }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={e => { e.stopPropagation(); onDelete(sub.category_id); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        )}
                      </td>
                    </tr>
                    {expandedTypes.has(`${t.name}::${sub.name}`) && sub.markets.map(mkt => {
                      const isMktEditing = inlineEditId === mkt.category_id;
                      return (
                      <tr key={mkt.category_id || mkt.name} className={cn("hover:bg-accent/50 group", isMktEditing && "bg-blue-50/50 dark:bg-blue-950/20")}>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-muted-foreground/60 pl-14">
                          {isMktEditing ? <Input value={inlineForm.code || ''} onChange={e => setInlineForm(p => ({...p, code: e.target.value}))} className="h-6 text-[10px] w-16 font-mono" /> : (mkt.code || '—')}
                        </td>
                        <td className="px-3 py-1.5 text-[10px] text-muted-foreground pl-16">
                          {isMktEditing ? <Input value={inlineForm.name || ''} onChange={e => setInlineForm(p => ({...p, name: e.target.value}))} className="h-6 text-[10px] flex-1" autoFocus data-testid="inline-edit-mkt-name" /> : mkt.name}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {isMktEditing ? (
                            <div className="flex gap-0.5 justify-end">
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-green-600" onClick={() => { onSave(inlineForm, mkt.category_id); setInlineEditId(null); }}><Check className="w-2.5 h-2.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setInlineEditId(null)}><X className="w-2.5 h-2.5" /></Button>
                            </div>
                          ) : (
                          <div className="flex gap-0.5 justify-end opacity-0 group-hover:opacity-100">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setInlineEditId(mkt.category_id); setInlineForm({ name: mkt.name, code: mkt.code || '', parent_code: mkt.parent_code || '' }); }}>
                              <Pencil className="w-2.5 h-2.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => onDelete(mkt.category_id)}>
                              <Trash2 className="w-2.5 h-2.5" />
                            </Button>
                          </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FinancePage;
